// cherish3: 배틀 한글화 3차 적용됨
declare var CherishKo: any; // cherish fork: 한글 이름 헬퍼 (battle-dex.ts 정의)
/**
 * Pokemon Showdown Battle Animations
 *
 * There are the specific resource files and scripts for misc animations
 *
 * Licensing note: PS's client has complicated licensing:
 * - The client as a whole is AGPLv3
 * - The battle replay/animation engine (battle-*.ts) by itself is MIT
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license MIT
 */

import type { Battle, HPColor, Pokemon, Side, WeatherState } from './battle';
import type { BattleSceneStub } from './battle-scene-stub';
import { BattleMoveAnims } from './battle-animations-moves';
import { BattleLog } from './battle-log';
import { type BattleBGM, BattleSound } from './battle-sound';
import { extractionAudioAllows } from './extraction-audio'; // cherish-extraction-audio
import { Dex, toID, type ID, type SpriteData } from './battle-dex';
import { BattleNatures } from './battle-dex-data';
import { BattleTooltips } from './battle-tooltips';
import { BattleTextParser, type Args, type KWArgs } from './battle-text-parser';

// cherish-bgm:table-begin (patch-cherish-bgm.py)
/**
 * cherish fork: 체리쉬 자체 배틀 BGM. loopstart/loopend 는 ms.
 *
 * `id` 는 api 의 곡 표(apps/api/data/battle-music.json)와 잇는 열쇠다. 음악 CD 시스템(2026-09-09)이
 * 「이 방은 이 곡」을 id 로 내려주고, 여기 없는 id 를 받으면 조용히 기본곡으로 떨어진다.
 * `dflt` 는 CD 없이 나오는 기본곡. api 가 답을 못 줄 때(재시작·옛 방)는 기본곡끼리만 해시로 고른다 —
 * 안 그러면 CD 로만 열리는 곡이 아무에게나 새어 나간다.
 */
const CHERISH_BGM: { id: string, url: string, loopstart: number, loopend: number, dflt: boolean }[] = [
	{ id: 'still-going', url: 'audio/cherish/still-going-v2.mp3', loopstart: 74645, loopend: 149539, dflt: true },
	{ id: 'no-turning-back', url: 'audio/cherish/no-turning-back.mp3', loopstart: 3738, loopend: 190236, dflt: false },
	{ id: 'never-show-my-back', url: 'audio/cherish/never-show-my-back.mp3', loopstart: 2368, loopend: 155887, dflt: false },
	{ id: 'catch-my-breath', url: 'audio/cherish/catch-my-breath-v2.mp3', loopstart: 6532, loopend: 162352, dflt: false },
	{ id: 'unbroken-resolve', url: 'audio/cherish/unbroken-resolve.mp3', loopstart: 1602, loopend: 246106, dflt: true },
	{ id: 'tournament-quarterfinal', url: 'audio/cherish/tournament-quarterfinal-v1.mp3', loopstart: 0, loopend: 135000, dflt: false },
	{ id: 'tournament-semifinal', url: 'audio/cherish/tournament-semifinal-v1.mp3', loopstart: 0, loopend: 135000, dflt: false },
	{ id: 'tournament-final', url: 'audio/cherish/tournament-final-v1.mp3', loopstart: 0, loopend: 135000, dflt: false },
	// 2026-10-01 신곡 11곡 — 전부 음악 CD 전용. 마스터링 C:\ym\포켓몬gm-work-20260930inal (picked.json)
	{ id: 'mirror-queen', url: 'audio/cherish/mirror-queen.mp3', loopstart: 1904, loopend: 173968, dflt: false },
	{ id: 'black-sun', url: 'audio/cherish/black-sun.mp3', loopstart: 3413, loopend: 175637, dflt: false },
	{ id: 'dreaming-ribbon', url: 'audio/cherish/dreaming-ribbon.mp3', loopstart: 2345, loopend: 177302, dflt: false },
	{ id: 'unending', url: 'audio/cherish/unending.mp3', loopstart: 234600, loopend: 297686, dflt: false },
	{ id: 'hearts-regathered', url: 'audio/cherish/hearts-regathered.mp3', loopstart: 134790, loopend: 292792, dflt: false },
	{ id: 'onslaught', url: 'audio/cherish/onslaught.mp3', loopstart: 3785, loopend: 230431, dflt: false },
	{ id: 'wrapped-in-sparkle', url: 'audio/cherish/wrapped-in-sparkle.mp3', loopstart: 47175, loopend: 173872, dflt: false },
	{ id: 'power-of-love', url: 'audio/cherish/power-of-love.mp3', loopstart: 96722, loopend: 173013, dflt: false },
	{ id: 'world-end', url: 'audio/cherish/world-end.mp3', loopstart: 105482, loopend: 172521, dflt: false },
	{ id: 'what-we-kept', url: 'audio/cherish/what-we-kept.mp3', loopstart: 199114, loopend: 295114, dflt: false },
	{ id: 'silent-sanctuary', url: 'audio/cherish/silent-sanctuary.mp3', loopstart: 4621, loopend: 185795, dflt: false },
];
// cherish-bgm:table-end
// cherish-bgm-title:begin (patch-bgm-title.py)
/**
 * 곡 파일 -> 화면에 띄울 제목. 체리쉬 자체 제작곡이다.
 * 표에 없는 파일이면 제목을 안 띄운다(조용히 넘어간다 — 곡 추가 시 여기에 한 줄 추가).
 */
const CHERISH_BGM_TITLES: { [url: string]: string } = {
	'audio/cherish/still-going-v2.mp3': '그래도 간다',
	'audio/cherish/no-turning-back.mp3': '돌아서지 않아',
	'audio/cherish/never-show-my-back.mp3': '등을 보이지 않고',
	'audio/cherish/catch-my-breath-v2.mp3': '숨을 고르고',
	'audio/cherish/unbroken-resolve.mp3': '흐트러지지 않는 결의',
	'audio/cherish/tournament-quarterfinal-v1.mp3': '체리쉬 컵 · 8강',
	'audio/cherish/tournament-semifinal-v1.mp3': '체리쉬 컵 · 4강',
	'audio/cherish/tournament-final-v1.mp3': '체리쉬 컵 · 결승',
	'audio/cherish/mirror-queen.mp3': '거울 속의 여왕',
	'audio/cherish/black-sun.mp3': '검은 태양',
	'audio/cherish/dreaming-ribbon.mp3': '꿈꾸는 리본',
	'audio/cherish/unending.mp3': '끝나지 않은',
	'audio/cherish/hearts-regathered.mp3': '다시 모인 마음',
	'audio/cherish/onslaught.mp3': '맹공',
	'audio/cherish/wrapped-in-sparkle.mp3': '반짝임을 두르고',
	'audio/cherish/power-of-love.mp3': '사랑의 힘으로',
	'audio/cherish/world-end.mp3': '세상의 끝에서',
	'audio/cherish/what-we-kept.mp3': '지켜낸 것',
	'audio/cherish/silent-sanctuary.mp3': '침묵하는 성소',
};
// cherish-bgm-title:end

/*

Most of this file is: CC0 (public domain)
  <http://creativecommons.org/publicdomain/zero/1.0/>

This license DOES extend to all images in the fx/ folder, with the exception of icicle.png, lightning.png, and bone.png.

icicle.png and lightning.png by Clint Bellanger are triple-licensed GPLv2/GPLv3/CC-BY-SA-3.0.
  <http://opengameart.org/content/icicle-spell>
  <http://opengameart.org/content/lightning-shock-spell>

rocks.png, rock1.png, rock2.png by PO user "Gilad" is licensed GPLv3.

This license DOES NOT extend to any images in the sprites/ folder.

This license DOES NOT extend to any other files in this repository.

*/

// cherish-hit-fx:begin (patch-hit-fx.py)
/** 속성색 — battle-dex.ts 의 CHERISH_TYPE_COLOR 와 같은 값 (그쪽은 모듈 지역이라 못 쓴다) */
const CW_TYPE_COLOR: { [type: string]: string } = {
	Normal: '#A8A878', Fire: '#F08030', Water: '#6890F0', Electric: '#D8B430', Grass: '#78C850',
	Ice: '#98D8D8', Fighting: '#C03028', Poison: '#A040A0', Ground: '#E0C068', Flying: '#A890F0',
	Psychic: '#F85888', Bug: '#A8B820', Rock: '#B8A038', Ghost: '#705898', Dragon: '#7038F8',
	Dark: '#705848', Steel: '#B8B8D0', Fairy: '#EE99AC',
};
/** 잔상을 붙일 이펙트 — 구슬·투사체만. 주먹·발톱 같은 기호는 겹치면 지저분해진다 */
const CW_TRAIL_FX: { [name: string]: number } = {
	wisp: 1, purplewisp: 1, waterwisp: 1, mudwisp: 1, blackwisp: 1,
	fireball: 1, bluefireball: 1, shadowball: 1, energyball: 1, electroball: 1,
	mistball: 1, iceball: 1, flareball: 1, icicle: 1, pinkicicle: 1,
	rock1: 1, rock2: 1, rock3: 1, rocks: 1, leaf1: 1, leaf2: 1, petal: 1, feather: 1, shine: 1,
};
// cherish-hit-fx:end

// cherish-fx-sound (patch-fx-sound.py): 타격음. 전부 CC0 실사 녹음이다.
/** 속성 → 소리 계열. 결이 겹치는 속성은 한 소리를 나눠 쓴다 */
const CW_SFX_TYPE: { [type: string]: string } = {
	Fire: 'fire', Water: 'water', Grass: 'grass', Electric: 'electric', Ice: 'ice',
	Ground: 'ground', Rock: 'ground', Steel: 'steel', Fairy: 'fairy',
	Psychic: 'psychic', Ghost: 'psychic', Flying: 'wind', Poison: 'poison',
	Bug: 'slice', Dark: 'slice', Dragon: 'burst',
	Normal: 'strike', Fighting: 'strike',
};
/** 속성보다 앞서는 것 — 터지는 기술은 속성이 뭐든 폭발음이 맞다 */
const CW_SFX_BURST: { [id: string]: number } = {
	explosion: 1, selfdestruct: 1, mistyexplosion: 1,
};
/** 파일 이름은 고정이고 내용만 갈린다 — 갈아 끼우면 이 값을 올릴 것 */
const CW_SFX_STAMP = '?v=20260909a';

// cherish-fx2:begin (patch-fx-assemble.py)
/** fx 이미지 캐시 스탬프 — patch-fx-stamp.py 가 쓰는 것과 같은 값 */
const CW_FX_STAMP = '?v=20260909a';
/**
 * 구슬 이펙트만 쓰는 기술 — 「예쁜 동그라미가 날아가는」 그 기술들이다.
 * 여기 있는 것만 속성 조립 연출로 대체하고, 나머지(지진·10만볼트처럼 이미
 * 개성이 있는 연출)는 원래대로 둔다. 목록은 patch-fx-assemble.py 가
 * battle-animations-moves.ts 를 분석해 만든다 — 손으로 고치지 말 것.
 */
const CW_AUTO_MOVES: { [id: string]: number } = {
	acid: 1, acidarmor: 1, aciddownpour: 1, acidspray: 1, aeroblast: 1, aircutter: 1, airslash: 1, amnesia: 1,
	anchorshot: 1, appleacid: 1, aquajet: 1, aquaring: 1, aquatail: 1, armorcannon: 1, astralbarrage: 1, attackorder: 1,
	aurasphere: 1, aurawheel: 1, autotomize: 1, avalanche: 1, banefulbunker: 1, barbbarrage: 1, barrage: 1, batonpass: 1,
	beakblast: 1, belch: 1, bind: 1, blackholeeclipse: 1, blastburn: 1, blazekick: 1, bleakwindstorm: 1, blueflare: 1,
	bodypress: 1, bodyslam: 1, boltbeak: 1, boltstrike: 1, boomburst: 1, bounce: 1, brutalswing: 1, bubble: 1,
	bubblebeam: 1, bugbuzz: 1, bulkup: 1, bulletseed: 1, burningbulwark: 1, burningjealousy: 1, burnup: 1, calmmind: 1,
	camouflage: 1, chargebeam: 1, chatter: 1, chillingwater: 1, chloroblast: 1, clangingscales: 1, clearsmog: 1, coaching: 1,
	coil: 1, collisioncourse: 1, comeuppance: 1, confuseray: 1, constrict: 1, coreenforcer: 1, corrosivegas: 1, cosmicpower: 1,
	cottonspore: 1, courtchange: 1, crabhammer: 1, darkestlariat: 1, darkpulse: 1, darkvoid: 1, dazzlinggleam: 1, decorate: 1,
	destinybond: 1, devastatingdrake: 1, dig: 1, discharge: 1, dive: 1, dragonascent: 1, dragonbreath: 1, dragoncheer: 1,
	dragondance: 1, dragondarts: 1, dragonenergy: 1, dragonhammer: 1, dragonpulse: 1, dragonrage: 1, drainingkiss: 1, drainpunch: 1,
	dreameater: 1, dynamaxcannon: 1, dynamicpunch: 1, echoedvoice: 1, eeriespell: 1, eggbomb: 1, electrify: 1, electroball: 1,
	electroshot: 1, embargo: 1, ember: 1, endeavor: 1, energyball: 1, entrainment: 1, eternabeam: 1, expandingforce: 1,
	explosion: 1, fairywind: 1, fellstinger: 1, ficklebeam: 1, ficklebeamallout: 1, fierydance: 1, fierywrath: 1, filletaway: 1,
	finalgambit: 1, fireblast: 1, firefang: 1, firelash: 1, firepledge: 1, firespin: 1, fishiousrend: 1, flameburst: 1,
	flamecharge: 1, flamethrower: 1, flamewheel: 1, flareblitz: 1, flash: 1, flashcannon: 1, fling: 1, flipturn: 1,
	floralhealing: 1, flowertrick: 1, focusblast: 1, forestscurse: 1, foulplay: 1, freezeshock: 1, fusionbolt: 1, fusionflare: 1,
	gastroacid: 1, genesissupernova: 1, gigatonhammer: 1, gmaxcannonade: 1, gmaxcentiferno: 1, gmaxdepletion: 1, gmaxfireball: 1, gmaxhydrosnipe: 1,
	gmaxmalodor: 1, gmaxsnooze: 1, gravapple: 1, guardsplit: 1, guardswap: 1, gunkshot: 1, gust: 1, hardpress: 1,
	haze: 1, healblock: 1, healingwish: 1, healpulse: 1, heartswap: 1, heatcrash: 1, heatwave: 1, heavyslam: 1,
	helpinghand: 1, hex: 1, hiddenpower: 1, holdhands: 1, hornattack: 1, hornleech: 1, hurricane: 1, hydrosteam: 1,
	hydrovortex: 1, hyperbeam: 1, hyperspacehole: 1, hypervoice: 1, hypnosis: 1, iceball: 1, iceburn: 1, icespinner: 1,
	imprison: 1, incinerate: 1, infernalparade: 1, inferno: 1, infernooverdrive: 1, infestation: 1, ironhead: 1, ivycudgelfire: 1,
	ivycudgelwater: 1, judgment: 1, lavaplume: 1, leechlife: 1, leechseed: 1, lifedew: 1, lightthatburnsthesky: 1, liquidation: 1,
	lunge: 1, magicpowder: 1, magmastorm: 1, magnetbomb: 1, magnetrise: 1, maliciousmoonsault: 1, malignantchain: 1, matchagotcha: 1,
	maxairstream: 1, maxdarkness: 1, maxflare: 1, maxgeyser: 1, maxguard: 1, maxooze: 1, maxphantasm: 1, maxwyrmwind: 1,
	mefirst: 1, megahorn: 1, menacingmoonrazemaelstrom: 1, metalburst: 1, meteorassault: 1, meteormash: 1, mimic: 1, mindblown: 1,
	mindreader: 1, miracleeye: 1, mirrorcoat: 1, mirrorshot: 1, mist: 1, mistball: 1, mistyexplosion: 1, moongeistbeam: 1,
	morningsun: 1, mortalspin: 1, mudbomb: 1, muddywater: 1, mudshot: 1, mudslap: 1, mudsport: 1, mysticalfire: 1,
	mysticalpower: 1, nastyplot: 1, naturalgift: 1, naturesmadness: 1, neverendingnightmare: 1, nightdaze: 1, nuzzle: 1, oblivionwing: 1,
	oceanicoperetta: 1, octazooka: 1, octolock: 1, ominouswind: 1, originpulse: 1, overdrive: 1, overheat: 1, painsplit: 1,
	paleowave: 1, paraboliccharge: 1, payday: 1, phantomforce: 1, photongeyser: 1, pinmissile: 1, poisonfang: 1, poisongas: 1,
	poisonjab: 1, poisonpowder: 1, poisonsting: 1, poisontail: 1, polarflare: 1, pollenpuff: 1, poltergeist: 1, pounce: 1,
	powder: 1, powershift: 1, powersplit: 1, powerswap: 1, powertrip: 1, present: 1, prismaticlaser: 1, psybeam: 1,
	psyblade: 1, psychicnoise: 1, psychoboost: 1, psychocut: 1, psychoshift: 1, psychup: 1, psyshock: 1, psystrike: 1,
	psywave: 1, pulverizingpancake: 1, purify: 1, pursuit: 1, pyroball: 1, quiverdance: 1, reflecttype: 1, relicsong: 1,
	rest: 1, revelationdance: 1, risingvoltage: 1, roaroftime: 1, roleplay: 1, ruination: 1, sacredfire: 1, saltcure: 1,
	sandattack: 1, sandsearstorm: 1, sandtomb: 1, scald: 1, scaleshot: 1, searingshot: 1, searingsunrazesmash: 1, secretpower: 1,
	seedbomb: 1, seedflare: 1, seismictoss: 1, selfdestruct: 1, shadowball: 1, shadowforce: 1, shadowstrike: 1, shellsidearmphysical: 1,
	shellsidearmspecial: 1, shelltrap: 1, shockwave: 1, signalbeam: 1, silverwind: 1, simplebeam: 1, sinisterarrowraid: 1, skillswap: 1,
	skittersmack: 1, slackoff: 1, sleeppowder: 1, sludge: 1, sludgebomb: 1, sludgewave: 1, smog: 1, smokescreen: 1,
	snarl: 1, snipeshot: 1, softboiled: 1, solarbeam: 1, spark: 1, sparklingaria: 1, speedswap: 1, spicyextract: 1,
	spikecannon: 1, spiritshackle: 1, spite: 1, spitup: 1, splash: 1, spore: 1, steameruption: 1, steelbeam: 1,
	storedpower: 1, strangesteam: 1, strengthsap: 1, strugglebug: 1, stunspore: 1, sunsteelstrike: 1, supercellslam: 1, supersonic: 1,
	supersonicskystrike: 1, surf: 1, surgingstrikes: 1, swift: 1, syrupbomb: 1, tailglow: 1, tailwind: 1, tarshot: 1,
	technoblast: 1, temperflare: 1, terablast: 1, terablastbug: 1, terablastdark: 1, terablastdragon: 1, terablastfighting: 1, terablastfire: 1,
	terablastflying: 1, terablastghost: 1, terablastgrass: 1, terablastnormal: 1, terablastpoison: 1, terablaststeel: 1, terastarstorm: 1, terrainpulse: 1,
	thousandarrows: 1, thousandwaves: 1, thundershock: 1, thunderwave: 1, tidyup: 1, topsyturvy: 1, torchsong: 1, toxic: 1,
	trickortreat: 1, triplearrows: 1, tripledive: 1, twinbeam: 1, twineedle: 1, twister: 1, uproar: 1, vcreate: 1,
	venomdrench: 1, venoshock: 1, victorydance: 1, voltswitch: 1, waterfall: 1, watergun: 1, waterpulse: 1, watersport: 1,
	waterspout: 1, wavecrash: 1, weatherball: 1, whirlpool: 1, whirlwind: 1, willowisp: 1, wish: 1, workup: 1,
	worryseed: 1, wrap: 1, zenheadbutt: 1,
};
/**
 * cherish-fx-beam: 속성마다 빛이 나가는 방식이 다르다.
 * bolt=꺾인다 · thick=굵게 흐른다 · shards=덩어리가 줄지어 간다 · ray=곧은 줄기
 */
const CW_BEAM_STYLE: { [type: string]: string } = {
	Electric: 'bolt', Steel: 'bolt',
	Water: 'thick', Fire: 'thick', Poison: 'thick', Ghost: 'thick', Dragon: 'thick',
	Ice: 'shards', Rock: 'shards', Ground: 'shards', Bug: 'shards',
};
/**
 * cherish-fx-reflect: 받은 걸 그대로 되돌리는 기술. 위력이 0이라 일반 타격 연출로는
 * 아무 말도 못 한다 — 리플렉트처럼 막을 세우고 그 막에서 되돌려 보낸다.
 */
const CW_REFLECT_MOVES: { [id: string]: number } = {
	counter: 1, mirrorcoat: 1, metalburst: 1, comeuppance: 1,
};
/**
 * cherish-fx-shapes: 플래그에 없는 결은 이름이 유일한 단서다.
 * 「폭발」 「파도」 「흡수」 「춤」 은 기술 데이터 어디에도 안 적혀 있다.
 */
const CW_NAME_BRANCH: { re: RegExp, br: string }[] = [
	{ re: /심포니|아리아|Symphony|Aria/, br: 'sound' },
	{ re: /먹기|흡수|드레인|Drain|Absorb|Leech/, br: 'drainish' },
	{ re: /파도|탁류|해일|급류|소용돌이|스톰|웨이브|Surf|Torrent|Whirl|Storm|Wave/, br: 'vortexish' },
	{ re: /폭발|버스트|노바|블래스트|플레어|Burst|Nova|Blast|Explosion/, br: 'burstish' },
	{ re: /춤|댄스|Dance/, br: 'danceish' },
];
/** wind 플래그는 8개뿐이라 이름이 바람인 특수기가 그냥 흐름으로 나갔다 */
// ⚠️ Storm 은 넣지 않는다 — 마그마스톰처럼 감싸는 결은 바람이 아니라 휘몰아침이다
const CW_WIND_NAMES = /바람|회오리|폭풍|Wind|Gust|Twister/;
/** 마스크로 쓸 모양. 알파만 쓰므로 원본 색은 상관없다. */
const CW_SHAPE: { [k: string]: { url: string, w: number, h: number } } = {
	// cherish-fx-shapes: 마스크 전용 모양 (fx/cwmask-*.png, 알파만 쓴다)
	burst: { url: 'cwmask-burst.png', w: 128, h: 128 },
	shock: { url: 'cwmask-shock.png', w: 128, h: 128 },
	impactstar: { url: 'cwmask-impactstar.png', w: 128, h: 128 },
	wave: { url: 'cwmask-wave.png', w: 128, h: 128 },
	gust: { url: 'cwmask-gust.png', w: 128, h: 128 },
	beamcore: { url: 'cwmask-beamcore.png', w: 128, h: 128 },
	swirl: { url: 'cwmask-swirl.png', w: 128, h: 128 },
	drain: { url: 'cwmask-drain.png', w: 128, h: 128 },
	crystal: { url: 'cwmask-crystal.png', w: 128, h: 128 },
	arc: { url: 'cwmask-arc.png', w: 128, h: 128 },
	ember: { url: 'cwmask-ember.png', w: 128, h: 128 },
	smoke: { url: 'cwmask-smoke.png', w: 128, h: 128 },
	// 기존 이름은 전용 모양으로 넘긴다 (부르는 곳을 안 고쳐도 되게)
	orb: { url: 'wisp.png', w: 100, h: 100 },
	spark: { url: 'cwmask-ember.png', w: 128, h: 128 },
	impact: { url: 'cwmask-burst.png', w: 128, h: 128 },
	shard: { url: 'cwmask-crystal.png', w: 128, h: 128 },
	slashL: { url: 'cwmask-arc.png', w: 128, h: 128 },
	slashR: { url: 'cwmask-arc.png', w: 128, h: 128 },
	fist: { url: 'fist.png', w: 55, h: 49 },
	biteTop: { url: 'topbite.png', w: 108, h: 64 },
	biteBottom: { url: 'bottombite.png', w: 108, h: 64 },
	leaf: { url: 'leaf1.png', w: 32, h: 26 },
};
/** 한글 기술명 — 이름으로 결을 읽을 때 쓴다 (ko 텍스트가 없으면 빈 문자열) */
function cwKoMoveName(id: string) {
	const t: any = (window as any).BattleText;
	const e = t && t.ko && t.ko.Moves && t.ko.Moves[id];
	return (e && e.name) || '';
}
/** #rrggbb 를 밝게/어둡게. 그라데이션 코어와 테두리를 만드는 데 쓴다. */
function cwShade(hex: string, amount: number) {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex);
	if (!m) return hex;
	const n = parseInt(m[1], 16);
	const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
		const t = amount > 0 ? 255 : 0;
		const k = amount > 0 ? amount : -amount;
		return Math.round(v + (t - v) * k);
	});
	return 'rgb(' + ch[0] + ',' + ch[1] + ',' + ch[2] + ')';
}
/** 속성색 덩어리 — 코어는 흰빛, 가장자리는 짙게. 평평한 단색을 피한다. */
function cwGrad(color: string) {
	return 'radial-gradient(circle at 50% 42%, #ffffff 0%, ' + cwShade(color, 0.55) +
		' 26%, ' + color + ' 58%, ' + cwShade(color, -0.45) + ' 100%)';
}
// cherish-fx2:end

// cherish-fx3:begin (patch-fx-particles.py)
/**
 * 속성마다 입자의 물리를 다르게 준다. 색만 바꾸면 전부 같은 결이 된다.
 * grav 는 아래로 +, 위로 -. drag 는 매 프레임 곱하는 감쇠.
 */
const CW_P_TYPE: { [type: string]: {
	grav: number, drag: number, size: number, life: number, speed: number, flicker?: number,
} } = {
	Fire: { grav: -0.055, drag: 0.955, size: 3.4, life: 540, speed: 1.05 },
	Water: { grav: 0.115, drag: 0.945, size: 3.1, life: 460, speed: 1.15 },
	Electric: { grav: 0, drag: 0.86, size: 2.6, life: 300, speed: 1.5, flicker: 0.55 },
	Grass: { grav: 0.012, drag: 0.935, size: 3.5, life: 620, speed: 0.85 },
	Ice: { grav: 0.035, drag: 0.975, size: 2.9, life: 640, speed: 1.0 },
	Ground: { grav: 0.16, drag: 0.93, size: 3.6, life: 420, speed: 0.95 },
	Rock: { grav: 0.19, drag: 0.94, size: 3.8, life: 440, speed: 0.9 },
	Steel: { grav: 0.055, drag: 0.9, size: 2.3, life: 300, speed: 1.45, flicker: 0.3 },
	Poison: { grav: -0.022, drag: 0.965, size: 3.6, life: 660, speed: 0.7 },
	Ghost: { grav: -0.03, drag: 0.97, size: 4.0, life: 700, speed: 0.6 },
	Dark: { grav: 0.02, drag: 0.965, size: 3.6, life: 560, speed: 0.75 },
	Psychic: { grav: -0.018, drag: 0.975, size: 2.8, life: 720, speed: 0.8, flicker: 0.25 },
	Fairy: { grav: -0.026, drag: 0.978, size: 2.6, life: 760, speed: 0.8, flicker: 0.35 },
	Dragon: { grav: -0.01, drag: 0.96, size: 3.3, life: 600, speed: 1.05 },
	Flying: { grav: -0.04, drag: 0.955, size: 2.9, life: 520, speed: 1.2 },
	Bug: { grav: 0.03, drag: 0.94, size: 2.5, life: 440, speed: 1.0 },
	Fighting: { grav: 0.045, drag: 0.925, size: 3.2, life: 380, speed: 1.3 },
	Normal: { grav: 0.03, drag: 0.945, size: 3.0, life: 440, speed: 1.0 },
};

interface CwPart {
	x: number; y: number; vx: number; vy: number;
	life: number; max: number; size: number; color: string; flicker: number;
	grav: number; drag: number;
}

/**
 * 필드 위에 깔리는 입자 레이어. 기존 애니 엔진과 완전히 분리돼 있어
 * pos()·showEffect 를 건드리지 않는다.
 */
class CherishParticles {
	canvas: HTMLCanvasElement;
	ctx: CanvasRenderingContext2D | null;
	parts: CwPart[] = [];
	raf = 0;
	last = 0;
	dpr = 1;
	/** 색마다 발광 원을 한 번만 그려 두고 재사용한다 (매 프레임 그라데이션은 느리다) */
	static glowCache: { [color: string]: HTMLCanvasElement } = {};
	/** 저사양 안전핀 — 이보다 많이 쌓이면 오래된 것부터 버린다 */
	static MAX = 420;

	constructor(width: number, height: number) {
		this.canvas = document.createElement('canvas');
		this.canvas.className = 'cw-parts';
		this.dpr = Math.min(2, (window.devicePixelRatio || 1));
		this.canvas.width = Math.round(width * this.dpr);
		this.canvas.height = Math.round(height * this.dpr);
		this.canvas.style.width = width + 'px';
		this.canvas.style.height = height + 'px';
		this.ctx = this.canvas.getContext('2d');
		if (this.ctx) this.ctx.scale(this.dpr, this.dpr);
	}

	static glow(color: string) {
		let c = CherishParticles.glowCache[color];
		if (c) return c;
		c = document.createElement('canvas');
		c.width = c.height = 64;
		const g = c.getContext('2d');
		if (g) {
			const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
			grad.addColorStop(0, '#ffffff');
			grad.addColorStop(0.28, color);
			grad.addColorStop(0.62, cwShade(color, -0.25));
			grad.addColorStop(1, 'rgba(0,0,0,0)');
			g.fillStyle = grad;
			g.fillRect(0, 0, 64, 64);
		}
		CherishParticles.glowCache[color] = c;
		return c;
	}

	add(p: CwPart) {
		if (this.parts.length >= CherishParticles.MAX) this.parts.shift();
		this.parts.push(p);
		this.start();
	}

	/** 한 점에서 사방으로 */
	burst(x: number, y: number, color: string, type: string, opts: {
		count?: number, speed?: number, size?: number, life?: number,
		dir?: number, spread?: number,
	} = {}) {
		const t = CW_P_TYPE[type] || CW_P_TYPE.Normal;
		const n = opts.count || 22;
		const sp = (opts.speed || 1) * t.speed;
		const spread = opts.spread === undefined ? Math.PI * 2 : opts.spread;
		const dir = opts.dir === undefined ? 0 : opts.dir;
		for (let i = 0; i < n; i++) {
			const a = dir + (spread >= Math.PI * 2 ?
				(i / n) * Math.PI * 2 : (Math.random() - 0.5) * spread);
			const v = (0.9 + Math.random() * 2.4) * sp;
			this.add({
				x, y,
				vx: Math.cos(a) * v,
				vy: Math.sin(a) * v * 0.8,
				life: 1, max: (opts.life || t.life) * (0.7 + Math.random() * 0.6),
				size: (opts.size || t.size) * (0.6 + Math.random() * 0.9),
				color, flicker: t.flicker || 0, grav: t.grav, drag: t.drag,
			});
		}
	}

	/** 두 점을 잇는 경로를 따라 흩뿌린다 (빔·투사체 궤적) */
	trail(x0: number, y0: number, x1: number, y1: number, color: string, type: string,
		opts: { count?: number, spread?: number, size?: number, life?: number } = {}) {
		const t = CW_P_TYPE[type] || CW_P_TYPE.Normal;
		const n = opts.count || 26;
		const spread = opts.spread === undefined ? 16 : opts.spread;
		for (let i = 0; i < n; i++) {
			const k = Math.random();
			this.add({
				x: x0 + (x1 - x0) * k + (Math.random() - 0.5) * spread,
				y: y0 + (y1 - y0) * k + (Math.random() - 0.5) * spread,
				vx: (Math.random() - 0.5) * 1.3 * t.speed,
				vy: (Math.random() - 0.5) * 1.3 * t.speed,
				life: 1, max: (opts.life || t.life) * (0.5 + Math.random() * 0.7),
				size: (opts.size || t.size) * (0.5 + Math.random() * 0.8),
				color, flicker: t.flicker || 0, grav: t.grav * 0.6, drag: t.drag,
			});
		}
	}

	/** 발밑에서 솟는 기운 */
	rise(x: number, y: number, color: string, type: string,
		opts: { count?: number, width?: number, size?: number, life?: number } = {}) {
		const t = CW_P_TYPE[type] || CW_P_TYPE.Normal;
		const n = opts.count || 24;
		const w = opts.width || 64;
		for (let i = 0; i < n; i++) {
			this.add({
				x: x + (Math.random() - 0.5) * w,
				y: y + Math.random() * 16,
				vx: (Math.random() - 0.5) * 0.5,
				vy: -(0.7 + Math.random() * 1.5),
				life: 1, max: (opts.life || t.life) * (0.8 + Math.random() * 0.7),
				size: (opts.size || t.size) * (0.5 + Math.random() * 0.8),
				color, flicker: t.flicker || 0, grav: -Math.abs(t.grav) - 0.01, drag: 0.985,
			});
		}
	}

	start() {
		if (this.raf) return;
		this.last = 0;
		const tick = (now: number) => {
			this.raf = 0;
			const dt = this.last ? Math.min(48, now - this.last) : 16;
			this.last = now;
			this.step(dt);
			this.draw();
			if (this.parts.length) this.raf = requestAnimationFrame(tick);
			else this.last = 0;
		};
		this.raf = requestAnimationFrame(tick);
	}

	step(dt: number) {
		const f = dt / 16.67;
		const out: CwPart[] = [];
		for (let i = 0; i < this.parts.length; i++) {
			const p = this.parts[i];
			p.life -= dt / p.max;
			if (p.life <= 0) continue;
			p.vy += p.grav * f;
			const d = Math.pow(p.drag, f);
			p.vx *= d;
			p.vy *= d;
			p.x += p.vx * f;
			p.y += p.vy * f;
			out.push(p);
		}
		this.parts = out;
	}

	draw() {
		const ctx = this.ctx;
		if (!ctx) return;
		ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
		ctx.globalCompositeOperation = 'lighter';
		for (let i = 0; i < this.parts.length; i++) {
			const p = this.parts[i];
			let a = p.life;
			if (a > 0.75) a = (1 - a) / 0.25; // 태어날 때 짧게 밝아진다
			if (p.flicker) a *= 1 - p.flicker * Math.random();
			const r = p.size * (0.45 + p.life * 0.9);
			ctx.globalAlpha = Math.max(0, Math.min(1, a));
			ctx.drawImage(CherishParticles.glow(p.color), p.x - r * 2, p.y - r * 2, r * 4, r * 4);
		}
		ctx.globalAlpha = 1;
		ctx.globalCompositeOperation = 'source-over';
	}

	clear() {
		this.parts = [];
		if (this.raf) cancelAnimationFrame(this.raf);
		this.raf = 0;
		if (this.ctx) this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
	}
}
// cherish-fx3:end

export class BattleScene implements BattleSceneStub {
	battle: Battle;
	animating = true;
	acceleration = 1;

	/** Note: Not the actual generation of the battle, but the gen of the sprites/background */
	gen = 7;
	mod = '';
	/** 1 = singles, 2 = doubles, 3 = triples */
	activeCount = 1;

	numericId = 0;
	$frame: JQuery;
	$battle: JQuery = null!;
	$options: JQuery = null!;
	log: BattleLog;
	$terrain: JQuery = null!;
	$weather: JQuery = null!;
	$bgEffect: JQuery = null!;
	$bg: JQuery = null!;
	$sprite: JQuery = null!;
	$sprites: [JQuery, JQuery] = [null!, null!];
	$spritesFront: [JQuery, JQuery] = [null!, null!];
	$stat: JQuery = null!;
	$fx: JQuery = null!;
	$leftbar: JQuery = null!;
	$rightbar: JQuery = null!;
	$turn: JQuery = null!;
	$messagebar: JQuery = null!;
	$delay: JQuery = null!;
	$hiddenMessage: JQuery = null!;
	$tooltips: JQuery = null!;
	tooltips: BattleTooltips;

	sideConditions: [{ [id: string]: Sprite[] }, { [id: string]: Sprite[] }] = [{}, {}];

	preloadDone = 0;
	preloadNeeded = 0;
	bgm: BattleBGM | null = null;
	backdropImage = '';
	bgmNum = 0;
	/** cherish-bgm-title: 곡명 토스트를 이미 띄웠는지 (배틀당 1회). */
	cherishTitleShown = false;
	preloadCache: { [url: string]: HTMLImageElement } = {};

	messagebarOpen = false;
	customControls = false;
	interruptionCount = 1;
	curWeather = '';
	curTerrain = '';

	// Animation state
	////////////////////////////////////

	// cherish-hit-fx:begin (patch-hit-fx.py) — 타격 반응 연출 상태
	/** 이번 기술 연출에서 이펙트가 방어자에 처음 닿는 시각(ms). runMoveAnim 이 채운다. */
	cwHitTime = 0;
	/** runMoveAnim 이 도는 동안만 살아 있는 도달 시각 수집기 */
	cwHitProbe: { sprite: PokemonSprite | null, times: number[], marks: number[], reactions: number[], elapsed: number } | null = null;
	/** -crit / -supereffective 는 -damage 보다 먼저 오므로 개체별로 모아 둔다 */
	cwHitFlags: { [ident: string]: { [k: string]: boolean | number } } = {};
	/** 피해 숫자에 팝을 줄지 — damageAnim 이 세우고 resultAnim 이 소비한다 */
	cwResultPop: { crit: boolean, superEff: boolean } | null = null;
	/** 잔상 예산. 이펙트를 20장 넘게 겹치는 기술이 있어 배치마다 상한을 둔다 */
	cwTrailBudget = 0;
	cwInTrail = false;
	/** 같은 순간에 흔들림이 여러 번 겹치지 않게 */
	cwShakeAt = 0;
	/** cherish-fx3: 필드 위 입자 레이어 (reset 마다 새로 만든다) */
	cwParts: CherishParticles | null = null;
	// cherish-hit-fx:end
	timeOffset = 0;
	pokemonTimeOffset = 0;
	minDelay = 0;
	/** jQuery objects that need to finish animating */
	activeAnimations = $();

	constructor(battle: Battle, $frame: JQuery, $logFrame: JQuery) {
		this.battle = battle;

		$frame.addClass('battle');
		this.$frame = $frame;
		this.log = new BattleLog($logFrame[0] as HTMLDivElement, this);
		this.log.battleParser!.pokemonName = (pokemonId: string) => {
			if (!pokemonId) return '';
			if (battle.ignoreNicks || battle.ignoreOpponent) {
				const pokemon = battle.getPokemon(pokemonId);
				if (pokemon) return pokemon.speciesForme;
			}
			if (!pokemonId.startsWith('p')) return '???pokemon:' + pokemonId + '???';
			if (pokemonId.charAt(3) === ':') return BattleTextParser.escapeReplace(pokemonId.slice(4).trim());
			else if (pokemonId.charAt(2) === ':') return BattleTextParser.escapeReplace(pokemonId.slice(3).trim());
			return '???pokemon:' + pokemonId + '???';
		};

		let numericId = 0;
		if (battle.id) {
			numericId = parseInt(battle.id.slice(battle.id.lastIndexOf('-') + 1), 10);
			if (this.battle.id.includes('digimon')) this.mod = 'digimon';
		}
		if (!numericId) {
			numericId = Math.floor(Math.random() * 1000000);
		}
		this.numericId = numericId;
		this.tooltips = new BattleTooltips(battle);
		this.tooltips.listen($frame[0]);

		this.preloadEffects();
		// reset() is called during battle initialization, so it doesn't need to be called here
	}

	reset() {
		this.updateGen();

		// Log frame
		/////////////

		if (this.$options) {
			this.log.reset();
		} else {
			this.$options = $('<div class="battle-options"></div>');
			$(this.log.elem).prepend(this.$options);
		}

		// Battle frame
		///////////////

		this.$frame.empty();
		// cherish-bgm-title: 토스트 DOM 이 위 empty() 로 지워졌다. 표식도 되돌려 다음 animationOn 이 다시 띄우게 한다.
		this.cherishTitleShown = false;
		this.$battle = $('<div class="innerbattle"></div>');
		this.$frame.append(this.$battle);

		this.$bg = $('<div class="backdrop" style="background-image:url(' + Dex.resourcePrefix + this.backdropImage + ');display:block;opacity:0.8"></div>');
		this.$terrain = $('<div class="weather"></div>');
		this.$weather = $('<div class="weather"></div>');
		this.$bgEffect = $('<div></div>');
		this.$sprite = $('<div></div>');

		this.$sprites = [$('<div></div>'), $('<div></div>')];
		this.$spritesFront = [$('<div></div>'), $('<div></div>')];
		this.sideConditions = [{}, {}];

		this.$sprite.append(this.$sprites[1]);
		this.$sprite.append(this.$spritesFront[1]);
		this.$sprite.append(this.$spritesFront[0]);
		this.$sprite.append(this.$sprites[0]);

		this.$stat = $('<div role="complementary" aria-label="Active Pokemon"></div>');
		this.$fx = $('<div></div>');
		this.$leftbar = $('<div class="leftbar" role="complementary" aria-label="Your Team"></div>');
		this.$rightbar = $('<div class="rightbar" role="complementary" aria-label="Opponent\'s Team"></div>');
		this.$turn = $('<div></div>');
		this.$messagebar = $('<div class="messagebar message"></div>');
		this.$delay = $('<div></div>');
		this.$hiddenMessage = $('<div class="message" style="position:absolute;display:block;visibility:hidden"></div>');
		this.$tooltips = $('<div class="tooltips"></div>');

		this.$battle.append(this.$bg);
		this.$battle.append(this.$terrain);
		this.$battle.append(this.$weather);
		this.$battle.append(this.$bgEffect);
		this.$battle.append(this.$sprite);
		this.$battle.append(this.$stat);
		this.$battle.append(this.$fx);
		// cherish-fx3: 입자 레이어. $fx 다음이라 이펙트보다 앞에 온다
		this.cwParts = new CherishParticles(640, 360);
		this.$battle.append(this.cwParts.canvas);
		this.$battle.append(this.$leftbar);
		this.$battle.append(this.$rightbar);
		this.$battle.append(this.$turn);
		this.$battle.append(this.$messagebar);
		this.$battle.append(this.$delay);
		this.$battle.append(this.$hiddenMessage);
		this.$battle.append(this.$tooltips);

		if (!this.animating) {
			this.$battle.append('<div class="seeking"><strong>seeking...</strong></div>');
		}

		this.messagebarOpen = false;
		this.timeOffset = 0;
		this.pokemonTimeOffset = 0;
		this.curTerrain = '';
		this.curWeather = '';

		this.log.battleParser!.perspective = this.battle.mySide.sideid;

		this.resetSides(true);
	}

	animationOff() {
		this.$battle.append('<div class="seeking"><strong>seeking...</strong></div>');
		this.$frame.find('div.playbutton').remove();
		this.stopAnimation();

		this.animating = false;
		this.$messagebar.empty().css({
			opacity: 0,
			height: 0,
		});
	}
	stopAnimation() {
		this.interruptionCount++;
		this.$battle.find(':animated').finish();
		this.$fx.empty();
	}
	animationOn() {
		if (this.animating) return;
		$.fx.off = false;
		this.animating = true;
		this.$battle.find('.seeking').remove();
		this.updateSidebars();
		for (const side of this.battle.sides) {
			for (const pokemon of side.pokemon) {
				pokemon.sprite.reset(pokemon);
			}
		}
		this.updateWeather(true);
		this.resetTurn();
		this.resetSideConditions();
		this.cherishShowBgmTitle(); // cherish-bgm-title: 따라잡기 중 건너뛴 토스트를 여기서 되찾는다
	}
	pause() {
		this.stopAnimation();
		this.updateBgm();
		if (!this.battle.started) {
			this.$frame.append('<div class="playbutton"><button name="play" class="button"><i class="fa fa-play" aria-hidden="true"></i> Play</button><br /><br /><button name="play-muted" class="startsoundchooser button" style="font-size:10pt">Play (sound off)</button></div>');
			this.$frame.find('div.playbutton button[name=play-muted]').click(() => {
				this.setMute(true);
				this.battle.play();
			});
		}
		this.$frame.find('div.playbutton button[name=play]').click(() => this.battle.play());
	}
	resume() {
		this.$frame.find('div.playbutton').remove();
		this.updateBgm();
	}
	setMute(muted: boolean) {
		BattleSound.setMute(muted);
	}
	wait(time: number) {
		if (!this.animating) return;
		this.timeOffset += time;
	}

	// Sprite handling
	/////////////////////////////////////////////////////////////////////

	addSprite(sprite: PokemonSprite) {
		if (sprite.$el) this.$sprites[+sprite.isFrontSprite].append(sprite.$el);
	}
	showEffect(
		effect: string | SpriteData, start: ScenePos, end: ScenePos,
		transition: string, after?: string, additionalCss?: JQuery.PlainObject
	) {
		// cherish-hit-fx:begin — 이름을 알아야 결(회전·맥동)과 잔상을 붙일 수 있다
		const cwName = typeof effect === 'string' ? effect : '';
		if (typeof effect === 'string') effect = BattleEffects[effect];
		if (cwName && !this.cwInTrail && this.cwTrailBudget > 0 &&
			CW_TRAIL_FX[cwName] && this.cwTrailOn()) {
			const sx = start.x || 0, sy = start.y || 0, sz = start.z || 0;
			const ex = end.x === undefined ? sx : end.x;
			const ey = end.y === undefined ? sy : end.y;
			const ez = end.z === undefined ? sz : end.z;
			const cwDist = Math.sqrt((ex - sx) * (ex - sx) + (ey - sy) * (ey - sy) + (ez - sz) * (ez - sz));
			const cwStartT = start.time || 0;
			const cwEndT = end.time === undefined ? cwStartT + 500 : end.time;
			const cwSpan = cwEndT - cwStartT;
			// 멀리·빠르게 날아가는 것만. 제자리에서 커지는 이펙트에 잔상을 주면 그냥 겹쳐 보인다
			if (cwDist > 140 && cwSpan > 0 && cwSpan <= 700) {
				this.cwInTrail = true;
				this.cwTrailBudget -= 2;
				const cwBase = start.opacity === undefined ? 1 : start.opacity;
				const cwEndBase = end.opacity === undefined ? cwBase : end.opacity;
				const cwLags = [[70, 0.34], [140, 0.15]];
				for (let i = 0; i < cwLags.length; i++) {
					const lag = cwLags[i][0], mul = cwLags[i][1];
					const s2: ScenePos = { ...start, time: cwStartT + lag, opacity: cwBase * mul };
					const e2: ScenePos = { ...end, time: cwEndT + lag, opacity: cwEndBase * mul };
					// 잔상까지 explode 시키면 화면이 터진다 — 조용히 사라지게만
					this.showEffect(effect, s2, e2, transition, after === 'explode' ? 'fade' : after, additionalCss);
				}
				this.cwInTrail = false;
			}
		}
		// cherish-hit-fx:end

		let $effect = $(`<img src="${effect.url!}" style="display:block;position:absolute" />`);
		this.$fx.append($effect);
		if (additionalCss) $effect.css(additionalCss);
		$effect = this.$fx.children().last();
		// cherish-hit-fx: 이름표를 달아 두면 CSS 가 결을 준다 (transform·filter 만 — opacity 는 절대 금지)
		if (cwName && this.cwLifeOn()) $effect.addClass('cwfx-' + cwName + (this.cwInTrail ? ' cwfx-trail' : ''));

		return this.animateEffect($effect, effect, start, end, transition, after);
	}
	animateEffect(
		$effect: JQuery, effect: string | SpriteData, start: ScenePos, end: ScenePos,
		transition: string, after?: string, additionalCss?: JQuery.PlainObject
	) {
		if (typeof effect === 'string') effect = BattleEffects[effect];

		if (!start.time) start.time = 0;
		if (!end.time) end.time = start.time + 500;
		start.time += this.timeOffset;
		end.time += this.timeOffset;
		if (!end.scale && end.scale !== 0 && start.scale) end.scale = start.scale;
		if (!end.xscale && end.xscale !== 0 && start.xscale) end.xscale = start.xscale;
		if (!end.yscale && end.yscale !== 0 && start.yscale) end.yscale = start.yscale;
		end = { ...start, ...end };

		// cherish-fx-sound-timing: v1 — 정지 효과는 등장, 투사체는 도달 시각.
		if (this.cwHitProbe && this.cwHitProbe.sprite && !this.cwInTrail) {
			const t = this.cwHitProbe.sprite;
			const dx = (end.x || 0) - t.x, dy = (end.y || 0) - t.y, dz = (end.z || 0) - t.z;
			const sx = (start.x || 0) - (end.x || 0);
			const sy = (start.y || 0) - (end.y || 0);
			const sz = (start.z || 0) - (end.z || 0);
			// 넓은 130 반경은 빔 중간 부품도 타격으로 오인한다.
			if (dx * dx + dy * dy + dz * dz <= 60 * 60) {
				const stationary = sx * sx + sy * sy + sz * sz <= 4;
				this.cwHitProbe.times.push(stationary ? start.time : end.time);
			}
		}

		let startpos = this.pos(start, effect);
		let endpos = this.posT(end, effect, transition, start);

		if (start.time) {
			$effect.css({ ...startpos, opacity: 0 });
			$effect.delay(start.time).animate({
				opacity: startpos.opacity,
			}, 1);
		} else if ($effect.queue().length) {
			$effect.animate(startpos, 0);
		} else {
			$effect.css(startpos);
		}
		$effect.animate(endpos, end.time! - start.time);
		if (after === 'fade') {
			$effect.animate({
				opacity: 0,
			}, 100);
		}
		if (after === 'explode') {
			if (end.scale) end.scale *= 3;
			if (end.xscale) end.xscale *= 3;
			if (end.yscale) end.yscale *= 3;
			end.opacity = 0;
			let endendpos = this.pos(end, effect);
			$effect.animate(endendpos, 200);
		}
		this.waitFor($effect);

		return $effect;
	}
	// cherish-hit-fx:begin (patch-hit-fx.py)
	/**
	 * 연출을 켤지. 검수 기간이라 연출 실험실(/cwfxlab.html)에서만 참이다 —
	 * 일반 유저에게는 노출 0. 오픈할 때 CHERISH_HIT_FX 줄을 지우고
	 * 배틀 옵션 체크박스를 되살리면 된다 (절차는 patch-hit-fx.py 4번 섹션).
	 */
	cwGate(pref: string) {
		if (!(window as any).CHERISH_HIT_FX) return false;
		if (Dex.prefs(pref)) return false;
		if (typeof window !== 'undefined' && window.matchMedia &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
		return true;
	}
	/** 타격 반응 — 흔들림·번쩍임·플래시·배경 워시·쓰러짐 탈색 */
	cwFxOn() { return this.cwGate('cherishnohitfx'); }
	/** 이펙트의 결 — 회전·나풀거림·맥동·깜빡임 */
	cwLifeOn() { return this.cwGate('cherishnofxlife'); }
	/** 투사체 잔상 */
	cwTrailOn() { return this.cwGate('cherishnofxtrail'); }
	/** battle.ts 가 -crit / -supereffective / -resisted / 직접 타격을 여기 적어 둔다 */
	cwFlag(pokemon: Pokemon, key: string, value: boolean | number = true) {
		const id = pokemon.ident || '?';
		if (!this.cwHitFlags[id]) this.cwHitFlags[id] = {};
		this.cwHitFlags[id][key] = value;
	}
	/** 화면 흔들림. 세기 1~3, delay 는 타격 순간까지의 ms */
	cwShake(level: number, delay: number) {
		const scene = this;
		setTimeout(() => {
			const $b = scene.$battle;
			if (!$b || !$b.length) return;
			const now = Date.now();
			// 광역기는 -damage 가 개체 수만큼 오는데 화면은 하나다
			if (now - scene.cwShakeAt < 90) return;
			scene.cwShakeAt = now;
			const cls = 'cw-shake-' + level;
			$b.removeClass('cw-shake-1 cw-shake-2 cw-shake-3');
			if ($b[0]) void ($b[0] as HTMLElement).offsetWidth; // 같은 클래스를 다시 붙여도 애니가 돌게
			$b.addClass(cls);
			setTimeout(() => { $b.removeClass(cls); }, 520);
		}, delay);
	}
	/** 화면 전체 플래시. 포켓몬·상태바 앞에 깔린다 */
	cwFlash(color: string, peak: number, ms: number, delay: number) {
		const scene = this;
		setTimeout(() => {
			if (!scene.$fx || !scene.$fx.length) return;
			const $el = $('<div class="cw-flash"></div>').css({ background: color, opacity: 0 });
			scene.$fx.append($el);
			$el.animate({ opacity: peak }, Math.round(ms * 0.3))
				.animate({ opacity: 0 }, Math.round(ms * 0.7), () => { $el.remove(); });
		}, delay);
	}
	/** 속성색을 배경에 은은하게 깐다 (특수기 전용 — 전 기술에 넣으면 피로하다) */
	cwWash(color: string, peak: number, ms: number, delay: number) {
		const scene = this;
		setTimeout(() => {
			if (!scene.$bgEffect || !scene.$bgEffect.length) return;
			const $el = $('<div class="background"></div>').css({
				background: color, display: 'block', opacity: 0,
			});
			scene.$bgEffect.append($el);
			$el.animate({ opacity: peak }, Math.round(ms * 0.25))
				.animate({ opacity: 0 }, Math.round(ms * 0.75), () => { $el.remove(); });
		}, delay);
	}
	/** 맞은 아이가 흰 실루엣으로 번쩍인다 */
	cwHitFlash(sprite: PokemonSprite, level: number, crit: boolean, delay: number) {
		setTimeout(() => {
			const $el = sprite.$el;
			if (!$el || !$el.length) return;
			const cls = 'cw-hit-' + level + (crit ? ' cw-hit-crit' : '');
			$el.removeClass('cw-hit-1 cw-hit-2 cw-hit-3 cw-hit-crit');
			if ($el[0]) void ($el[0] as HTMLElement).offsetWidth;
			$el.addClass(cls);
			setTimeout(() => { $el.removeClass(cls); }, 460);
		}, delay);
	}
	/**
	 * 타격 순간 화면이 반응하게 한다. 전 기술 공통 경로 (`-damage` → `damageAnim`).
	 * 세기는 피해 비율 + 급소·상성으로 정한다.
	 */
	cwImpact(pokemon: Pokemon) {
		const id = pokemon.ident || '?';
		const flags = this.cwHitFlags[id] || {};
		delete this.cwHitFlags[id];
		const crit = !!flags.crit, superEff = !!flags.supereff, resisted = !!flags.resisted;
		this.cwResultPop = { crit, superEff };
		if (!flags.direct || !this.cwFxOn()) return;

		// -damage 의 delta 는 음수로 온다 (getDamageRange 가 그대로 넘긴다). 크기만 쓴다
		let power = typeof flags.ratio === 'number' ? Math.abs(flags.ratio) : 0.25;
		if (superEff) power += 0.18;
		if (crit) power += 0.16;
		if (resisted) power -= 0.12;
		const level = power < 0.13 ? 1 : power < 0.34 ? 2 : 3;
		const delay = this.cwHitTime;

		this.cwShake(level, delay);
		this.cwHitFlash(pokemon.sprite, level, crit, delay);
		// cherish-fx3: 맞은 자리에서 속성 입자가 터진다 (전 기술)
		{
			let hitMove: any = null;
			try { hitMove = this.battle.dex.moves.get(this.battle.lastMove); } catch {}
			const hitType = (hitMove && hitMove.type) || 'Normal';
			const hitColor = CW_TYPE_COLOR[hitType] || '#FFFFFF';
			const sp = pokemon.sprite;
			this.cwEmit('burst', hitColor, hitType, { x: sp.x, y: sp.y, z: sp.z }, null, {
				count: level === 3 ? 34 : level === 2 ? 24 : 14,
				speed: level === 3 ? 1.35 : 1,
			}, delay);
		}

		let move = null;
		try { move = this.battle.dex.moves.get(this.battle.lastMove); } catch {}
		const color = (move && CW_TYPE_COLOR[move.type]) || '#FFFFFF';

		if (crit) {
			this.cwFlash('#FFFFFF', 0.44, 200, delay);
		} else if (superEff) {
			this.cwFlash(color, 0.26, 240, delay);
		}
		// 특수기는 속성 기운이 화면에 남는다는 결. 물리기는 흔들림만으로 충분하다
		if (move && move.category === 'Special' && !resisted) {
			this.cwWash(color, 0.16, 420, delay > 120 ? delay - 120 : 0);
		}
	}
	/** 실험실이 「2차로 바뀌는 기술」을 정확히 추릴 수 있게 대상 목록을 내준다 */
	static cherishAutoMoves() { return CW_AUTO_MOVES; }
	/** 연출 실험실(/cwfxlab.html)이 갈래별로 켜고 끌 수 있게 노출한다 */
	static cherishFxOptions() {
		const ps: any = (window as any).PS;
		const item = (label: string, pref: string) => ({
			label,
			get: () => !Dex.prefs(pref),
			set: (v: boolean) => {
				if (ps && ps.prefs && ps.prefs.set) ps.prefs.set(pref, v ? null : true);
			},
		});
		return [
			item('1차 · 타격 반응 (흔들림 · 번쩍임 · 플래시)', 'cherishnohitfx'),
			item('1차 · 이펙트 결 (회전 · 맥동 · 깜빡임)', 'cherishnofxlife'),
			item('1차 · 잔상', 'cherishnofxtrail'),
			item('2차 · 속성 조립 (빔 · 고리 · 입자 · 파편)', 'cherishnofx2'),
			item('3차 · 입자 레이어 (캔버스 · 속성별 물리)', 'cherishnofx3'),
			item('타격음 (계열별 실사 녹음)', 'cherishnofxsound'), // cherish-fx-sound
		];
	}
	// cherish-hit-fx:end
	// cherish-fx-sound: 타격음. 실험실 게이트 뒤라 유저 화면에서는 안 난다
	cwSfxOn() { return this.cwGate('cherishnofxsound'); }
	/** 이 기술이 어느 소리를 쓰는지. 속성이 기본이고 결이 뚜렷한 것만 덮어쓴다 */
	cwSfxKind(move: any) {
		if (!move) return 'strike';
		if (CW_SFX_BURST[move.id]) return 'burst';
		if (move.flags && move.flags.slicing) return 'slice';
		return CW_SFX_TYPE[move.type] || 'strike';
	}
	/** 이번 기술이 빗나갔는지. battle.ts 의 animateMove 가 미리 알려 준다 */
	cwMoveMissed = false;
	/**
	 * 이펙트가 방어자에 닿는 순간에 맞춰 낸다 (runMoveAnim 끝에서 부른다).
	 * 이 시점에는 급소·상성·피해량을 아직 모른다 — `-damage` 는 다음 애니 블록에
	 * 오기 때문이다. 그래서 음량은 **위력**으로 정한다.
	 */
	cwPlayMoveSound(moveid: string, measured: boolean) {
		const missed = this.cwMoveMissed;
		this.cwMoveMissed = false;
		if (missed || !this.cwSfxOn()) return;
		let move: any = null;
		try { move = this.battle.dex.moves.get(moveid); } catch {}
		if (!move || move.category === 'Status') return;
		const bp = move.basePower || 0;
		const scale = bp >= 100 ? 0.92 : bp >= 60 ? 0.76 : 0.58;
		let delay = this.cwHitTime;
		// 이펙트도 조립 표식도 없는 연출(스프라이트만 부딪는 것)은 도달 시각을 못 구한다.
		// 피해기 664 개를 재 보니 그런 게 49 개였고, 실측 중앙값이 접촉기 500ms · 비접촉 300ms 였다
		if (!measured && move.flags && move.flags.contact) delay = 500;
		this.cwSfx(this.cwSfxKind(move), scale, delay);
	}
	/** 계열 이름으로 소리 하나를 낸다. 음소거·효과음 음량은 상류 설정을 그대로 따른다 */
	cwSfx(kind: string, scale: number, delay: number) {
		if (!this.cwSfxOn() || !this.animating) return;
		const url = 'audio/cherish/hit/' + kind + '.mp3' + CW_SFX_STAMP;
		// 타격 순간에 네트워크 요청을 시작하지 않도록 미리 준비한다.
		const audio = BattleSound.getSound(url);
		if (!audio) return;
		const interruption = this.interruptionCount;
		const battle = this.battle;
		// 실험실이 감싸는 동일한 delay 큐. 느리게 보기에서도 화면과 함께 늘어난다.
		const timer = $('<div>');
		timer.delay(Math.max(0, delay)).queue(() => {
			timer.remove();
			if (this.battle !== battle || !this.battle || !this.animating ||
				this.interruptionCount !== interruption || !this.cwSfxOn()) return;
			if (!extractionAudioAllows(this.battle.id)) return; // cherish-extraction-audio: delayed hit
			const vol = (BattleSound.muted ? 0 : BattleSound.effectVolume) * scale;
			if (!vol) return;
			try {
				audio.volume = Math.max(0, Math.min(1, vol / 100));
				audio.currentTime = 0;
				const p: any = audio.play();
				if (p && p.catch) p.catch(function () {});
			} catch {}
		});
	}

	// cherish-fx2:begin (patch-fx-assemble.py)
	/** 2차 연출을 켤지 (실험실 전용 게이트 뒤) */
	cwAutoOn() { return this.cwGate('cherishnofx2'); }
	/**
	 * PS 좌표를 화면 픽셀 중심점으로. `pos()` 에 크기 0 짜리 더미를 넘기면
	 * 좌상단 보정이 0 이 되어 left/top 이 곧 중심이 된다.
	 */
	cwPoint(x: number, y: number, z: number) {
		const p = this.pos({ x, y, z, scale: 1 }, { w: 0, h: 0, gen: this.gen } as any);
		let s = this.gen === 5 ? 2.0 - (z / 200) : 1.5 - 0.5 * (z / 200);
		if (s < 0.1) s = 0.1;
		return { left: p.left as number, top: p.top as number, scale: s };
	}
	/** 조립 연출이 방어자에 닿는 시각을 1차의 타격 추정기에 알린다 */
	cwMarkHit(time: number) {
		if (this.cwHitProbe) this.cwHitProbe.marks.push(time + this.timeOffset);
	}

	/** 마스크 착색 스프라이트 — 모양은 알파로, 색은 속성으로 */
	cwShape(
		shape: string, color: string, start: ScenePos, end: ScenePos,
		transition = 'linear', after?: string, extraCss?: JQuery.PlainObject
	) {
		const sp = CW_SHAPE[shape] || CW_SHAPE.orb;
		const url = Dex.resourcePrefix + 'fx/' + sp.url + CW_FX_STAMP;
		const $el = $('<div class="cw-shape"></div>').css({
			'-webkit-mask-image': 'url(' + url + ')',
			'mask-image': 'url(' + url + ')',
			background: cwGrad(color),
		});
		if (extraCss) $el.css(extraCss);
		this.$fx.append($el);
		return this.animateEffect(
			$el, { w: sp.w, h: sp.h, gen: this.gen } as any, start, end, transition, after
		);
	}

	/** 두 점을 잇는 빔. 각도는 인라인 transform 으로 고정하고 폭만 늘린다 */
	cwBeam(
		from: PokemonSprite, to: PokemonSprite, color: string,
		opts: { delay?: number, grow?: number, hold?: number, thickness?: number } = {}
	) {
		const a = this.cwPoint(from.x, from.y, from.z);
		const b = this.cwPoint(to.x, to.y, to.z);
		const dx = b.left - a.left, dy = b.top - a.top;
		const len = Math.sqrt(dx * dx + dy * dy) + 20;
		const ang = Math.atan2(dy, dx) * 180 / Math.PI;
		const thick = (opts.thickness || 24) * ((a.scale + b.scale) / 2);
		const grow = opts.grow || 160;
		const hold = opts.hold || 260;
		const light = cwShade(color, 0.5);
		const $el = $('<div class="cw-beam"></div>').css({
			left: a.left,
			top: a.top - thick / 2,
			width: 0,
			height: thick,
			transform: 'rotate(' + ang + 'deg)',
			'transform-origin': '0 50%',
			background: 'linear-gradient(90deg, ' + light + ' 0%, #ffffff 18%, ' + color +
				' 45%, ' + color + ' 88%, rgba(255,255,255,0) 100%)',
			'box-shadow': '0 0 ' + Math.round(thick * 0.7) + 'px ' + color,
			opacity: 0,
		});
		this.$fx.append($el);
		$el.delay((opts.delay || 0) + this.timeOffset)
			.animate({ opacity: 0.95, width: len }, grow, 'linear')
			.delay(hold)
			.animate({ opacity: 0 }, 180, () => { $el.remove(); });
		this.waitFor($el);
		return $el;
	}

	/**
	 * cherish-fx-beam: 꺾인 줄기. 경로를 네 조각으로 나누고 조각마다 각도를 흔든다.
	 * 뒤 조각일수록 늦게 뻗어 「타고 흐른다」는 결이 된다.
	 */
	cwBolt(
		from: PokemonSprite, to: PokemonSprite, color: string,
		opts: { delay?: number, thickness?: number, segments?: number } = {}
	) {
		const a = this.cwPoint(from.x, from.y, from.z);
		const b = this.cwPoint(to.x, to.y, to.z);
		const segs = opts.segments || 4;
		const thick = (opts.thickness || 13) * ((a.scale + b.scale) / 2);
		const delay = opts.delay || 0;
		const light = cwShade(color, 0.6);
		let px = a.left, py = a.top;
		for (let i = 1; i <= segs; i++) {
			const k = i / segs;
			const jitter = i === segs ? 0 : (Math.random() - 0.5) * 46;
			const nx = a.left + (b.left - a.left) * k + jitter;
			const ny = a.top + (b.top - a.top) * k + jitter * 0.6;
			const dx = nx - px, dy = ny - py;
			const len = Math.sqrt(dx * dx + dy * dy);
			const ang = Math.atan2(dy, dx) * 180 / Math.PI;
			const $el = $('<div class="cw-beam"></div>').css({
				left: px,
				top: py - thick / 2,
				width: 0,
				height: thick,
				transform: 'rotate(' + ang + 'deg)',
				'transform-origin': '0 50%',
				background: 'linear-gradient(90deg, #ffffff 0%, ' + light + ' 30%, ' + color + ' 100%)',
				'box-shadow': '0 0 ' + Math.round(thick * 1.1) + 'px ' + color,
				opacity: 0,
			});
			this.$fx.append($el);
			$el.delay(delay + (i - 1) * 45 + this.timeOffset)
				.animate({ opacity: 1, width: len }, 55, 'linear')
				.delay(200)
				.animate({ opacity: 0 }, 150, function () { $(this).remove(); });
			this.waitFor($el);
			px = nx;
			py = ny;
		}
	}
	/** 퍼지는 고리 — 음파·파동·착탄 */
	cwRing(
		at: PokemonSprite, color: string,
		opts: { delay?: number, size?: number, time?: number, thickness?: number, peak?: number } = {}
	) {
		const p = this.cwPoint(at.x, at.y, at.z);
		const s0 = 14 * p.scale;
		const s1 = (opts.size || 150) * p.scale;
		const ms = opts.time || 420;
		const $el = $('<div class="cw-ring"></div>').css({
			left: p.left - s0 / 2,
			top: p.top - s0 / 2,
			width: s0,
			height: s0,
			'border-color': color,
			'border-width': Math.max(2, (opts.thickness || 5) * p.scale),
			'box-shadow': '0 0 10px ' + color + ', inset 0 0 10px ' + color,
			opacity: 0,
		});
		this.$fx.append($el);
		$el.delay((opts.delay || 0) + this.timeOffset)
			.animate({ opacity: opts.peak || 0.9 }, 50)
			.animate({
				left: p.left - s1 / 2, top: p.top - s1 / 2, width: s1, height: s1, opacity: 0,
			}, ms, 'linear', () => { $el.remove(); });
		this.waitFor($el);
		return $el;
	}

	/** 사방으로 튀는 파편 */
	cwBurst(
		at: PokemonSprite, color: string,
		opts: { delay?: number, count?: number, dist?: number, size?: number, shape?: string } = {}
	) {
		const count = opts.count || 8;
		const dist = opts.dist || 70;
		const size = opts.size || 0.28;
		const shape = opts.shape || 'spark';
		const delay = opts.delay || 0;
		for (let i = 0; i < count; i++) {
			const ang = (i / count) * Math.PI * 2 + Math.random() * 0.5;
			const d = dist * (0.55 + Math.random() * 0.75);
			this.cwShape(shape, color, {
				x: at.x, y: at.y, z: at.z, scale: size * 0.5, opacity: 1, time: delay,
			}, {
				x: at.x + Math.cos(ang) * d,
				y: at.y + Math.sin(ang) * d * 0.7,
				z: at.z,
				scale: size,
				opacity: 0,
				time: delay + 260 + Math.random() * 180,
			}, 'decel');
		}
	}

	/** 입자 흐름 — 구슬 하나 대신 여러 개를 시간·위치 지터로 흘려보낸다 */
	cwStream(
		from: PokemonSprite, to: PokemonSprite, color: string,
		opts: { delay?: number, count?: number, span?: number, size?: number,
			spread?: number, shape?: string, after?: string } = {}
	) {
		const count = opts.count || 14;
		const span = opts.span || 460;
		const size = opts.size || 0.42;
		const spread = opts.spread === undefined ? 34 : opts.spread;
		const shape = opts.shape || 'orb';
		const delay = opts.delay || 0;
		for (let i = 0; i < count; i++) {
			const t = delay + (i / count) * span;
			const r = () => (Math.random() - 0.5) * 2;
			const sc = size * (0.6 + Math.random() * 0.8);
			this.cwShape(shape, color, {
				x: from.x + r() * 12,
				y: from.y + r() * 12,
				z: from.z,
				scale: sc * 0.5,
				opacity: 0.9,
				time: t,
			}, {
				x: to.x + r() * spread,
				y: to.y + r() * spread * 0.8,
				z: to.behind(-10),
				scale: sc,
				opacity: 0.85,
				time: t + 300,
			}, 'decel', opts.after || 'fade');
		}
		return delay + 300;
	}

	/** 변화기의 기운 — 발밑에서 솟아오른다 */
	cwAura(at: PokemonSprite, color: string, opts: { delay?: number, rise?: boolean } = {}) {
		const delay = opts.delay || 0;
		const up = opts.rise === false ? -1 : 1;
		this.cwRing(at, color, { delay, size: 130, time: 520, peak: 0.75 });
		for (let i = 0; i < 9; i++) {
			const t = delay + i * 42;
			const dx = (Math.random() - 0.5) * 66;
			this.cwShape('spark', color, {
				x: at.x + dx, y: at.y - up * 22, z: at.z,
				scale: 0.16, opacity: 0.2, time: t,
			}, {
				x: at.x + dx * 0.6, y: at.y + up * 62, z: at.z,
				scale: 0.3, opacity: 0.9, time: t + 420,
			}, 'decel', 'fade');
		}
	}

	/**
	 * 전달 방식 판정. 조립기와 실험실이 **같은 규칙**을 보게 하려고 떼어 뒀다.
	 * 순서가 곧 우선순위다 (앞이 이긴다).
	 */
	cwAutoBranch(move: any, selfish: boolean) {
		const flags = move.flags || {};
		const bp = move.basePower || 0;
		if (selfish || move.category === 'Status') {
			// cherish-fx-status: 변화기 100개가 전부 같은 오라였다. 클라 movedex 에 있는 값만으로 가른다
			// (boosts·weather·sideCondition 은 축약본에 없다 — target·status·heal·플래그로 판정)
			if (move.target === 'all' || move.target === 'allySide' && move.type === 'Normal' &&
				move.pseudoWeather) return 'field';
			if (move.target === 'all') return 'field';
			if (move.target === 'allySide') return 'screen';
			if (move.target === 'foeSide') return 'hazard';
			if (move.heal || flags.heal) return 'heal';
			if (move.status) return 'status';
			if (move.target === 'self' || move.target === 'adjacentAllyOrSelf' ||
				move.target === 'adjacentAlly' || selfish) return 'buff';
			return 'debuff';
		}
		if (CW_REFLECT_MOVES[move.id]) return 'reflect';
		if (flags.punch) return 'punch';
		if (flags.bite) return 'bite';
		if (flags.slicing) return 'slicing';
		if (flags.sound || flags.pulse) return 'sound';
		if (flags.wind) return 'wind';
		// 플래그가 없어도 이름이 바람이면 바람 결로 (특수만 — 「폭풍」 붙은 접촉기를 끌어오면 안 된다)
		if (move.category === 'Special' &&
			(CW_WIND_NAMES.test(move.name) || CW_WIND_NAMES.test(cwKoMoveName(move.id)))) return 'wind';
		if (flags.bullet) return 'bullet';
		// cherish-fx-shapes: 여기까지 왔다면 플래그로는 결을 못 정한 것 — 이름을 본다
		const nm = move.name + ' ' + cwKoMoveName(move.id);
		for (let i = 0; i < CW_NAME_BRANCH.length; i++) {
			if (CW_NAME_BRANCH[i].re.test(nm)) return CW_NAME_BRANCH[i].br;
		}
		if (move.category === 'Special' && bp >= 65) return 'beam';
		if (move.category === 'Special') return 'stream';
		return flags.contact ? 'lunge' : 'shot';
	}
	/**
	 * cherish-fx-mods: 「어떻게」를 뽑는다. 전달 방식(cwAutoBranch)과 직교하는 축이라
	 * 주먹 연타 · 돌진 연타처럼 같은 변조가 다른 전달에 얹힌다.
	 */
	cwAutoMods(move: any) {
		const mh = move.multihit;
		let multi = 0;
		if (typeof mh === 'number') multi = mh;
		else if (mh && mh.length) multi = Math.round((mh[0] + mh[1]) / 2);
		if (multi > 5) multi = 5;
		const tgt = move.target;
		return {
			multi,
			recoil: !!(move.recoil || move.hasCrashDamage),
			quick: (move.priority || 0) > 0,
			spread: tgt === 'allAdjacent' || tgt === 'allAdjacentFoes' || tgt === 'foeSide',
			heavy: (move.basePower || 0) >= 100,
		};
	}
	/** 전달 방식과 무관하게 얹히는 뒷맛 */
	cwAutoAfter(sprites: PokemonSprite[], move: any, mods: any) {
		const attacker = sprites[0];
		const defender = sprites[1] || sprites[0];
		if (attacker === defender) return;
		const color = CW_TYPE_COLOR[move.type] || '#AAAAAA';
		const hit = this.cwHitTime || 300;

		// 물리는 조립기 본문에서 타격 자체를 반복한다. 한 번 나가는 전달은 착탄만 되풀이한다
		if (mods.multi > 1) {
			const br2 = this.cwAutoBranch(move, false);
			if (br2 !== 'lunge' && br2 !== 'shot' && br2 !== 'punch') {
				for (let h = 1; h < mods.multi; h++) {
					const t2 = hit + h * 145;
					const f2 = 1 - h * 0.12;
					this.cwRing(defender, color, {
						delay: t2, size: 125 * f2, time: 320, thickness: 5,
					});
					this.cwBurst(defender, color, { delay: t2 + 5, count: 6, dist: 62 * f2 });
				}
			}
		}
		if (mods.spread) {
			// 둘 이상을 한꺼번에 — 넓은 고리가 한 겹 더
			this.cwRing(defender, color, {
				delay: hit + 40, size: 300, time: 560, thickness: 4, peak: 0.6,
			});
		}
		if (mods.quick) {
			// 빠르다는 결 — 지나간 자리에 짧은 잔상
			for (let i = 0; i < 2; i++) {
				this.cwShape('spark', color, {
					x: attacker.x, y: attacker.y + 6, z: attacker.z,
					scale: 0.5, xscale: 1.5, yscale: 0.2, opacity: 0.55, time: i * 60,
				}, {
					x: defender.x, y: defender.y + 6, z: defender.behind(-8),
					scale: 0.7, xscale: 2.2, yscale: 0.14, opacity: 0, time: i * 60 + 200,
				}, 'linear');
			}
		}
		if (mods.recoil) {
			// 부딪힌 값을 자기도 치른다 — 뒤로 튕기고 파편이 이쪽으로
			attacker.anim({ x: attacker.leftof(14), y: attacker.y, z: attacker.behind(16), time: 140 }, 'accel');
			attacker.anim({ time: 260 }, 'decel');
			this.cwBurst(attacker, color, { delay: hit + 60, count: 7, dist: 58, size: 0.22 });
			this.cwEmit('burst', color, move.type,
				{ x: attacker.x, y: attacker.y, z: attacker.z }, null,
				{ count: 16, speed: 0.8 }, hit + 60);
		}
	}
	/**
	 * 기술 데이터(속성·분류·위력·플래그)로 연출을 조립한다.
	 * 구슬만 쓰던 기술을 이걸로 대체한다 — CW_AUTO_MOVES 참고.
	 */
	cwAutoMove(sprites: PokemonSprite[], move: any) {
		const mods = this.cwAutoMods(move);
		this.cwAutoMoveCore(sprites, move, mods);
		this.cwAutoAfter(sprites, move, mods);
	}
	cwAutoMoveCore(sprites: PokemonSprite[], move: any, mods: any) {
		const attacker = sprites[0];
		const defender = sprites[1] || sprites[0];
		const color = CW_TYPE_COLOR[move.type] || '#AAAAAA';
		const flags = move.flags || {};
		const bp = move.basePower || 0;
		const selfish = !sprites[1] || attacker === defender;
		const br = this.cwAutoBranch(move, selfish);

		// cherish-fx-status — 변화기는 갈래마다 다른 그림을 낸다
		if (br === 'buff') {
			const who = selfish ? attacker : defender;
			this.cwAura(who, color, {});
			this.cwEmit('rise', color, move.type, { x: who.x, y: who.y, z: who.z },
				null, { count: 26, width: 70 }, 60);
			return;
		}
		if (br === 'heal') {
			const who = selfish ? attacker : defender;
			// 흰빛을 섞어 「낫는다」는 결을 준다
			this.cwRing(who, '#B9F5C8', { delay: 40, size: 120, time: 560, thickness: 4, peak: 0.8 });
			this.cwRing(who, color, { delay: 200, size: 150, time: 520, thickness: 3, peak: 0.6 });
			for (let i = 0; i < 10; i++) {
				const dx = (Math.random() - 0.5) * 70;
				this.cwShape('spark', '#DFFFE6', {
					x: who.x + dx, y: who.y - 26, z: who.z, scale: 0.12, opacity: 0.2, time: i * 46,
				}, {
					x: who.x + dx * 0.5, y: who.y + 74, z: who.z, scale: 0.3, opacity: 0.95, time: i * 46 + 480,
				}, 'decel', 'fade');
			}
			this.cwEmit('rise', '#B9F5C8', move.type, { x: who.x, y: who.y, z: who.z },
				null, { count: 30, width: 76, life: 780 }, 40);
			return;
		}
		if (br === 'field') {
			// 화면 전체가 물든다
			this.cwWash(color, 0.3, 900, 0);
			this.cwRing(attacker, color, { delay: 60, size: 320, time: 760, thickness: 4, peak: 0.55 });
			this.cwRing(defender, color, { delay: 180, size: 320, time: 760, thickness: 4, peak: 0.55 });
			for (let i = 0; i < 12; i++) {
				const side = i % 2 ? 1 : -1;
				this.cwShape('spark', color, {
					x: side * 150, y: 120 + Math.random() * 60, z: 0, scale: 0.16, opacity: 0.15, time: i * 40,
				}, {
					x: side * 40, y: -20, z: 0, scale: 0.34, opacity: 0.8, time: i * 40 + 620,
				}, 'decel', 'fade');
			}
			this.cwEmit('rise', color, move.type, { x: 0, y: -30, z: 0 },
				null, { count: 40, width: 300, life: 900 }, 80);
			return;
		}
		if (br === 'screen') {
			// 아군 앞에 판이 선다
			this.cwShape('orb', color, {
				x: attacker.x, y: attacker.y + 10, z: attacker.behind(-30),
				scale: 0.4, xscale: 1.6, yscale: 0.1, opacity: 0.2, time: 0,
			}, {
				scale: 1.5, xscale: 1.9, yscale: 1.5, opacity: 0.5, time: 420,
			}, 'decel', 'fade');
			this.cwRing(attacker, color, { delay: 80, size: 180, time: 520, thickness: 5 });
			this.cwEmit('rise', color, move.type, { x: attacker.x, y: attacker.y, z: attacker.z },
				null, { count: 22, width: 110 }, 60);
			return;
		}
		if (br === 'hazard') {
			// 상대 발밑으로 떨어져 쌓인다
			for (let i = 0; i < 10; i++) {
				const dx = (Math.random() - 0.5) * 110;
				this.cwShape('shard', color, {
					x: defender.x + dx, y: defender.y + 90, z: defender.z,
					scale: 0.22, opacity: 0.9, time: i * 40,
				}, {
					x: defender.x + dx, y: defender.y - 26, z: defender.z,
					scale: 0.3, opacity: 1, time: i * 40 + 340,
				}, 'accel', 'fade');
			}
			this.cwRing(defender, color, { delay: 380, size: 170, time: 420, thickness: 4, peak: 0.7 });
			this.cwEmit('burst', color, move.type,
				{ x: defender.x, y: defender.y - 24, z: defender.z }, null,
				{ count: 20, speed: 0.7, spread: Math.PI * 0.8, dir: 0 }, 380);
			return;
		}
		if (br === 'status') {
			// 상대를 감아 돈다
			for (let i = 0; i < 3; i++) {
				this.cwRing(defender, color, {
					delay: i * 110, size: 120 + i * 20, time: 520, thickness: 3, peak: 0.7,
				});
			}
			this.cwStream(attacker, defender, color, {
				delay: 40, count: 10, span: 320, size: 0.26, spread: 40,
			});
			this.cwEmit('burst', color, move.type, { x: defender.x, y: defender.y, z: defender.z },
				null, { count: 22, speed: 0.55 }, 380);
			return;
		}
		if (br === 'debuff') {
			// 위에서 내리누른다
			this.cwShape('orb', color, {
				x: defender.x, y: defender.y + 110, z: defender.z,
				scale: 0.9, xscale: 1.4, yscale: 0.35, opacity: 0.25, time: 0,
			}, {
				y: defender.y + 6, scale: 1.1, xscale: 1.6, yscale: 0.3, opacity: 0.7, time: 380,
			}, 'accel', 'fade');
			this.cwRing(defender, color, { delay: 360, size: 160, time: 420, thickness: 5, peak: 0.8 });
			this.cwEmit('burst', color, move.type, { x: defender.x, y: defender.y, z: defender.z },
				null, { count: 20, speed: 0.8, spread: Math.PI, dir: 0 }, 370);
			return;
		}

		// 접촉기는 몸이 먼저 나간다 (접촉하지 않는 기술은 제자리)
		const lunge = !!flags.contact;
		if (lunge) {
			attacker.anim({ x: attacker.leftof(-16), y: attacker.y, z: attacker.behind(-14), time: 200 }, 'accel');
			attacker.anim({ time: 320 }, 'decel');
		}

		// cherish-fx-shapes — 플래그에 없던 결 넷
		if (br === 'burstish') {
			// 날아가는 게 아니라 그 자리에서 터진다
			this.cwMarkHit(240);
			this.cwShape('burst', color, {
				x: defender.x, y: defender.y, z: defender.behind(-18),
				scale: 0.2, opacity: 0.35, time: 180,
			}, {
				scale: mods && mods.heavy ? 2.2 : 1.7, opacity: 0, time: 700,
			}, 'decel');
			this.cwShape('impactstar', color, {
				x: defender.x, y: defender.y, z: defender.behind(-16),
				scale: 0.3, opacity: 0.9, time: 200,
			}, {
				scale: 1.6, opacity: 0, time: 560,
			}, 'decel');
			this.cwRing(defender, color, { delay: 210, size: 210, time: 460, thickness: 7 });
			this.cwRing(defender, color, { delay: 300, size: 300, time: 520, thickness: 3, peak: 0.6 });
			this.cwBurst(defender, color, { delay: 220, count: 16, dist: 108, shape: 'ember' });
			this.cwEmit('burst', color, move.type, { x: defender.x, y: defender.y, z: defender.z },
				null, { count: 44, speed: 1.5 }, 210);
			return;
		}
		if (br === 'vortexish') {
			// 아래에서 밀려와 덮친다
			this.cwMarkHit(380);
			for (let i = 0; i < 3; i++) {
				this.cwShape('wave', color, {
					x: attacker.x * 0.4, y: attacker.y - 30, z: attacker.z,
					scale: 0.9 + i * 0.2, xscale: 2.2, yscale: 0.5, opacity: 0.25, time: i * 110,
				}, {
					x: defender.x, y: defender.y + 24, z: defender.behind(-14),
					scale: 1.5 + i * 0.2, xscale: 2.6, yscale: 1.3, opacity: 0.8, time: i * 110 + 420,
				}, 'decel', 'fade');
			}
			this.cwRing(defender, color, { delay: 400, size: 250, time: 520, thickness: 5 });
			this.cwBurst(defender, color, { delay: 400, count: 12, dist: 92, shape: 'ember' });
			this.cwEmit('trail', color, move.type,
				{ x: attacker.x, y: attacker.y - 20, z: attacker.z },
				{ x: defender.x, y: defender.y, z: defender.z },
				{ count: 40, spread: 60 }, 120);
			return;
		}
		if (br === 'drainish') {
			// 빼앗아 되돌아온다 — 방향이 거꾸로다
			this.cwMarkHit(260);
			this.cwShape('drain', color, {
				x: defender.x, y: defender.y, z: defender.behind(-16),
				scale: 0.3, opacity: 0.3, time: 120,
			}, {
				scale: 1.3, opacity: 0.9, time: 520,
			}, 'decel', 'fade');
			this.cwRing(defender, color, { delay: 240, size: 140, time: 400, thickness: 5 });
			// 상대 → 나
			this.cwStream(defender, attacker, color, {
				delay: 300, count: 16, span: 420, size: 0.3, spread: 24,
			});
			this.cwEmit('trail', color, move.type,
				{ x: defender.x, y: defender.y, z: defender.z },
				{ x: attacker.x, y: attacker.y, z: attacker.z },
				{ count: 34, spread: 22 }, 320);
			this.cwRing(attacker, '#B9F5C8', { delay: 700, size: 120, time: 460, thickness: 4, peak: 0.7 });
			return;
		}
		if (br === 'danceish') {
			// 둘레를 돌고 나서 친다
			this.cwMarkHit(420);
			for (let i = 0; i < 3; i++) {
				this.cwShape('swirl', color, {
					x: attacker.x, y: attacker.y + 4, z: attacker.z,
					scale: 0.35 + i * 0.15, opacity: 0.25, time: i * 90,
				}, {
					scale: 1.15 + i * 0.15, opacity: 0.85, time: i * 90 + 360,
				}, 'decel', 'fade');
			}
			this.cwStream(attacker, defender, color, {
				delay: 300, count: 14, span: 300, size: 0.32, spread: 30,
			});
			this.cwShape('swirl', color, {
				x: defender.x, y: defender.y, z: defender.behind(-16),
				scale: 0.3, opacity: 0.5, time: 400,
			}, {
				scale: 1.5, opacity: 0, time: 820,
			}, 'decel');
			this.cwRing(defender, color, { delay: 430, size: 160, time: 420, thickness: 5 });
			this.cwBurst(defender, color, { delay: 440, count: 10, dist: 80, shape: 'ember' });
			this.cwEmit('burst', color, move.type, { x: defender.x, y: defender.y, z: defender.z },
				null, { count: 26 }, 440);
			return;
		}
		if (br === 'reflect') {
			// 막이 서고 → 번쩍 → 되돌아간다
			this.cwMarkHit(420);
			this.cwShape('orb', color, {
				x: attacker.x, y: attacker.y + 10, z: attacker.behind(-34),
				scale: 0.5, xscale: 1.7, yscale: 0.14, opacity: 0.15, time: 0,
			}, {
				scale: 1.5, xscale: 2.0, yscale: 1.6, opacity: 0.55, time: 260,
			}, 'decel');
			this.cwRing(attacker, color, { delay: 240, size: 190, time: 380, thickness: 5 });
			this.cwStream(attacker, defender, color, {
				delay: 300, count: 12, span: 260, size: 0.34, spread: 26,
			});
			this.cwRing(defender, color, { delay: 440, size: 150, time: 400, thickness: 5 });
			this.cwBurst(defender, color, { delay: 450, count: 9, dist: 74 });
			this.cwEmit('trail', color, move.type,
				{ x: attacker.x, y: attacker.y, z: attacker.z },
				{ x: defender.x, y: defender.y, z: defender.z },
				{ count: 26, spread: 24 }, 310);
			this.cwEmit('burst', color, move.type,
				{ x: defender.x, y: defender.y, z: defender.z }, null, { count: 22 }, 450);
			return;
		}
		if (br === 'punch') {
			this.cwMarkHit(240);
			for (let i = 0; i < 2; i++) {
				const t = 120 + i * 130;
				this.cwShape('fist', color, {
					x: attacker.x + (i ? 18 : -18), y: attacker.y + 12, z: attacker.z,
					scale: 0.5, opacity: 0.9, time: t,
				}, {
					x: defender.x + (i ? 14 : -14), y: defender.y + 6, z: defender.behind(-16),
					scale: 0.9, opacity: 1, time: t + 130,
				}, 'accel', 'fade');
				this.cwRing(defender, color, { delay: t + 130, size: 110, time: 300, thickness: 6 });
				this.cwBurst(defender, color, { delay: t + 140, count: 7, dist: 62 });
			}
			return;
		}

		if (br === 'bite') {
			this.cwMarkHit(260);
			this.cwShape('biteTop', color, {
				x: defender.x, y: defender.y + 46, z: defender.behind(-18), scale: 1.1, opacity: 0.9, time: 120,
			}, {
				y: defender.y + 14, scale: 0.95, opacity: 1, time: 300,
			}, 'accel', 'fade');
			this.cwShape('biteBottom', color, {
				x: defender.x, y: defender.y - 40, z: defender.behind(-18), scale: 1.1, opacity: 0.9, time: 120,
			}, {
				y: defender.y - 8, scale: 0.95, opacity: 1, time: 300,
			}, 'accel', 'fade');
			this.cwBurst(defender, color, { delay: 300, count: 8, dist: 66 });
			return;
		}

		if (br === 'slicing') {
			this.cwMarkHit(230);
			const shapes = ['slashL', 'slashR', 'slashL'];
			for (let i = 0; i < (bp >= 80 ? 3 : 2); i++) {
				const t = 110 + i * 110;
				this.cwShape(shapes[i], color, {
					x: defender.x + (i % 2 ? 26 : -26), y: defender.y + 26 - i * 12,
					z: defender.behind(-20), scale: 0.4, opacity: 0.3, time: t,
				}, {
					x: defender.x + (i % 2 ? -20 : 20), y: defender.y - 14 - i * 8,
					scale: 1.5, opacity: 1, time: t + 150,
				}, 'accel', 'fade');
			}
			this.cwBurst(defender, color, { delay: 260, count: 6, dist: 76, size: 0.2 });
			return;
		}

		if (br === 'sound') {
			this.cwMarkHit(320);
			for (let i = 0; i < 3; i++) {
				this.cwRing(attacker, color, {
					delay: i * 90, size: 200, time: 520, thickness: 4, peak: 0.7,
				});
			}
			this.cwStream(attacker, defender, color, {
				delay: 60, count: 8, span: 300, size: 0.3, spread: 46,
			});
			this.cwRing(defender, color, { delay: 340, size: 150, time: 420 });
			return;
		}

		if (br === 'wind') {
			this.cwMarkHit(300);
			for (let i = 0; i < 4; i++) {
				const off = (i - 1.5) * 26;
				this.cwShape('spark', color, {
					x: attacker.x + off, y: attacker.y + 20 + off * 0.3, z: attacker.z,
					scale: 0.2, xscale: 0.9, yscale: 0.14, opacity: 0.85, time: i * 70,
				}, {
					x: defender.x + off * 0.6, y: defender.y + 12 + off * 0.2, z: defender.behind(-10),
					scale: 0.9, xscale: 2.2, yscale: 0.2, opacity: 0.6, time: i * 70 + 340,
				}, 'linear', 'fade');
			}
			this.cwStream(attacker, defender, color, { count: 8, span: 340, size: 0.22, spread: 52 });
			return;
		}

		if (br === 'bullet') {
			this.cwMarkHit(300);
			this.cwShape('orb', color, {
				x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.35, opacity: 0.9, time: 60,
			}, {
				x: defender.x, y: defender.y, z: defender.behind(-12), scale: 0.75, opacity: 1, time: 300,
			}, 'accel', 'explode');
			this.cwRing(defender, color, { delay: 300, size: 160, time: 380 });
			this.cwBurst(defender, color, { delay: 300, count: 10, dist: 80 });
			return;
		}

		if (br === 'beam') {
			this.cwMarkHit(300);
			// 모으기 → 발사 → 착탄
			for (let i = 0; i < 5; i++) {
				const ang = (i / 5) * Math.PI * 2;
				this.cwShape('spark', color, {
					x: attacker.x + Math.cos(ang) * 52, y: attacker.y + Math.sin(ang) * 40, z: attacker.z,
					scale: 0.24, opacity: 0.2, time: i * 20,
				}, {
					x: attacker.x, y: attacker.y + 8, z: attacker.z, scale: 0.1, opacity: 1, time: 150,
				}, 'accel', 'fade');
			}
			// cherish-fx-beam: 속성마다 빛이 나가는 방식이 다르다
			const beamStyle = CW_BEAM_STYLE[move.type] || 'ray';
			if (beamStyle === 'bolt') {
				for (let i = 0; i < (bp >= 100 ? 3 : 2); i++) {
					this.cwBolt(attacker, defender, color, {
						delay: 140 + i * 90, thickness: bp >= 100 ? 15 : 11,
					});
				}
			} else if (beamStyle === 'shards') {
				// 얼음·바위·땅에 매끈한 빛줄기는 어색하다. 덩어리가 줄지어 날아간다
				const shots = bp >= 100 ? 7 : 5;
				for (let i = 0; i < shots; i++) {
					const t = 140 + i * 55;
					const off = (Math.random() - 0.5) * 26;
					this.cwShape('shard', color, {
						x: attacker.x + off * 0.4, y: attacker.y + 10 + off * 0.3, z: attacker.z,
						scale: 0.3, opacity: 0.95, time: t,
					}, {
						x: defender.x + off, y: defender.y + off * 0.5, z: defender.behind(-14),
						scale: 0.62, opacity: 1, time: t + 190,
					}, 'accel', 'explode');
				}
			} else {
				// cherish-fx-shapes: 착탄점에 빔 코어를 겹쳐 「닿았다」를 분명히
				this.cwShape('beamcore', color, {
					x: defender.x, y: defender.y, z: defender.behind(-16),
					scale: 0.25, opacity: 0.5, time: 300,
				}, {
					scale: 1.2, opacity: 0, time: 720,
				}, 'decel');
				this.cwBeam(attacker, defender, color, {
					delay: 150, grow: beamStyle === 'thick' ? 190 : 150,
					hold: bp >= 100 ? 420 : 260,
					thickness: (beamStyle === 'thick' ? 34 : 23) * (bp >= 100 ? 1.25 : 1),
				});
			}
			this.cwStream(attacker, defender, color, {
				delay: 170, count: 10, span: 320, size: 0.3, spread: 22,
			});
			this.cwRing(defender, color, { delay: 310, size: 180, time: 420, thickness: 6 });
			this.cwBurst(defender, color, { delay: 320, count: 10, dist: 86 });
			// cherish-fx3: 빔이 지나간 자리에 잔광이 남고, 착탄점이 터진다
			this.cwEmit('trail', color, move.type,
				{ x: attacker.x, y: attacker.y, z: attacker.z },
				{ x: defender.x, y: defender.y, z: defender.z },
				{ count: bp >= 100 ? 46 : 32, spread: 20 }, 170);
			this.cwEmit('burst', color, move.type, { x: defender.x, y: defender.y, z: defender.z },
				null, { count: 30, speed: 1.2 }, 310);
			return;
		}

		if (br === 'stream') {
			this.cwMarkHit(330);
			this.cwStream(attacker, defender, color, { count: 16, span: 420, size: 0.4, spread: 34 });
			this.cwRing(defender, color, { delay: 360, size: 140, time: 400 });
			this.cwBurst(defender, color, { delay: 370, count: 8, dist: 70 });
			// cherish-fx3
			this.cwEmit('trail', color, move.type,
				{ x: attacker.x, y: attacker.y, z: attacker.z },
				{ x: defender.x, y: defender.y, z: defender.z },
				{ count: 30, spread: 30 }, 120);
			this.cwEmit('burst', color, move.type, { x: defender.x, y: defender.y, z: defender.z },
				null, { count: 24 }, 350);
			return;
		}

		// 물리 — 부딪히고 파편이 튄다 (br === 'lunge' | 'shot')
		// cherish-fx-mods: 연타 기술은 그 횟수만큼 반복하고, 뒤 타격일수록 조금 작아진다
		const hits = mods.multi > 1 ? mods.multi : 1;
		const base0 = lunge ? 210 : 290;
		const gap = hits > 3 ? 120 : 155;
		this.cwMarkHit(base0 + 10);
		for (let h = 0; h < hits; h++) {
			const t0 = base0 + h * gap;
			const fade = 1 - h * 0.12;
			const jx = h ? (Math.random() - 0.5) * 30 : 0;
			const jy = h ? (Math.random() - 0.5) * 24 : 0;
			if (!lunge) {
				this.cwShape('shard', color, {
					x: attacker.x, y: attacker.y, z: attacker.z,
					scale: 0.35 * fade, opacity: 0.9, time: t0 - 230,
				}, {
					x: defender.x + jx, y: defender.y + jy, z: defender.behind(-12),
					scale: 0.6 * fade, opacity: 1, time: t0 + 10,
				}, 'accel', 'fade');
			}
			this.cwShape('impact', color, {
				x: defender.x + jx, y: defender.y + jy, z: defender.behind(-20),
				scale: 0.3 * fade, opacity: 0.5, time: t0,
			}, {
				scale: (mods.heavy ? 1.35 : 1.1) * fade, opacity: 0, time: t0 + 260,
			}, 'decel');
			this.cwRing(defender, color, {
				delay: t0 + 10, size: (mods.heavy ? 165 : 130) * fade, time: 340, thickness: 6,
			});
			this.cwBurst(defender, color, {
				delay: t0 + 15, count: hits > 2 ? 6 : 9, dist: 76 * fade,
			});
		}
	}
	// cherish-fx2:end
	// cherish-fx3:begin (patch-fx-particles.py)
	/** 입자 레이어를 켤지 (1·2차와 독립) */
	cwPartsOn() { return this.cwGate('cherishnofx3'); }
	/** 지연을 두고 입자를 터뜨린다. 좌표는 PS 좌표(스프라이트 기준). */
	cwEmit(
		kind: 'burst' | 'trail' | 'rise', color: string, type: string,
		at: { x: number, y: number, z: number },
		to: { x: number, y: number, z: number } | null,
		opts: any, delay: number
	) {
		if (!this.cwPartsOn()) return;
		const scene = this;
		setTimeout(() => {
			const P = scene.cwParts;
			if (!P || !scene.animating) return;
			const a = scene.cwPoint(at.x, at.y, at.z);
			if (kind === 'burst') {
				P.burst(a.left, a.top, color, type, opts);
			} else if (kind === 'rise') {
				P.rise(a.left, a.top + 26 * a.scale, color, type, opts);
			} else if (to) {
				const b = scene.cwPoint(to.x, to.y, to.z);
				P.trail(a.left, a.top, b.left, b.top, color, type, opts);
			}
		}, delay);
	}
	// cherish-fx3:end
	backgroundEffect(bg: string, duration: number, opacity = 1, delay = 0) {
		let $effect = $('<div class="background"></div>');
		$effect.css({
			background: bg,
			display: 'block',
			opacity: 0,
		});
		this.$bgEffect.append($effect);
		$effect.delay(delay).animate({
			opacity,
		}, 250).delay(duration - 250);
		$effect.animate({
			opacity: 0,
		}, 250);
	}

	/**
	 * Converts a PS location (x, y, z, scale, xscale, yscale, opacity)
	 * to a jQuery position (top, left, width, height, opacity) suitable
	 * for passing into `jQuery#css` or `jQuery#animate`.
	 * The display property is passed through if it exists.
	 */
	pos(loc: ScenePos, obj: SpriteData) {
		loc = {
			x: 0,
			y: 0,
			z: 0,
			scale: 1,
			opacity: 1,
			...loc,
		};
		if (!loc.xscale && loc.xscale !== 0) loc.xscale = loc.scale;
		if (!loc.yscale && loc.yscale !== 0) loc.yscale = loc.scale;

		let left = 210;
		let top = 245;
		let scale = (obj.gen === 5 ?
			2.0 - ((loc.z!) / 200) :
			1.5 - 0.5 * ((loc.z!) / 200));
		if (scale < 0.1) scale = 0.1;

		left += (410 - 190) * ((loc.z!) / 200);
		top += (135 - 245) * ((loc.z!) / 200);
		left += Math.floor(loc.x! * scale);
		top -= Math.floor(loc.y! * scale /* - loc.x * scale / 4 */);
		let width = Math.floor(obj.w * scale * loc.xscale!);
		let height = Math.floor(obj.h * scale * loc.yscale!);
		let hoffset = Math.floor((obj.h - (obj.y || 0) * 2) * scale * loc.yscale!);
		left -= Math.floor(width / 2);
		top -= Math.floor(hoffset / 2);

		let pos: JQuery.PlainObject = {
			left,
			top,
			width,
			height,
			opacity: loc.opacity,
		};
		if (loc.display) pos.display = loc.display;
		return pos;
	}
	/**
	 * Converts a PS location to a jQuery transition map (see `pos`)
	 * suitable for passing into `jQuery#animate`.
	 * oldLoc is required for ballistic (jumping) animations.
	 */
	posT(loc: ScenePos, obj: SpriteData, transition?: string, oldLoc?: ScenePos): JQuery.PlainObject {
		const pos = this.pos(loc, obj);
		const oldPos = (oldLoc ? this.pos(oldLoc, obj) : null);
		let transitionMap = {
			left: 'linear',
			top: 'linear',
			width: 'linear',
			height: 'linear',
			opacity: 'linear',
		};
		if (transition === 'ballistic') {
			transitionMap.top = (pos.top < oldPos!.top ? 'ballisticUp' : 'ballisticDown');
		}
		if (transition === 'ballisticUnder') {
			transitionMap.top = (pos.top < oldPos!.top ? 'ballisticDown' : 'ballisticUp');
		}
		if (transition === 'ballistic2') {
			transitionMap.top = (pos.top < oldPos!.top ? 'quadUp' : 'quadDown');
		}
		if (transition === 'ballistic2Back') {
			// This _should_ be the same as ballistic2.
			// Unfortunately, oldLoc is the original loc, rather than the
			// previous loc, so when you're "going back", loc === oldLoc, and
			// the direction has to instead be inferred from the destination.
			transitionMap.top = (loc.z! > 0 ? 'quadUp' : 'quadDown');
		}
		if (transition === 'ballistic2Under') {
			transitionMap.top = (pos.top < oldPos!.top ? 'quadDown' : 'quadUp');
		}
		if (transition === 'swing') {
			transitionMap.left = 'swing';
			transitionMap.top = 'swing';
			transitionMap.width = 'swing';
			transitionMap.height = 'swing';
		}
		if (transition === 'accel') {
			transitionMap.left = 'quadDown';
			transitionMap.top = 'quadDown';
			transitionMap.width = 'quadDown';
			transitionMap.height = 'quadDown';
		}
		if (transition === 'decel') {
			transitionMap.left = 'quadUp';
			transitionMap.top = 'quadUp';
			transitionMap.width = 'quadUp';
			transitionMap.height = 'quadUp';
		}
		return {
			left: [pos.left, transitionMap.left],
			top: [pos.top, transitionMap.top],
			width: [pos.width, transitionMap.width],
			height: [pos.height, transitionMap.height],
			opacity: [pos.opacity, transitionMap.opacity],
		};
	}

	waitFor(elem: JQuery) {
		this.activeAnimations = this.activeAnimations.add(elem);
	}

	startAnimations() {
		this.$fx.empty();
		this.activeAnimations = $();
		this.timeOffset = 0;
		this.minDelay = 0;
		this.cwHitTime = 0; // cherish-hit-fx
		this.cwTrailBudget = 24; // cherish-hit-fx
	}

	finishAnimations() {
		if (this.minDelay || this.timeOffset) {
			this.$delay.delay(Math.max(this.minDelay, this.timeOffset));
			this.activeAnimations = this.activeAnimations.add(this.$delay);
		}
		if (!this.activeAnimations.length) return undefined;
		return this.activeAnimations.promise();
	}

	// Messagebar and log
	/////////////////////////////////////////////////////////////////////

	preemptCatchup() {
		this.log.preemptCatchup();
	}
	message(message: string) {
		if (!this.messagebarOpen) {
			this.log.addSpacer();
			if (this.animating) {
				this.$messagebar.empty();
				this.$messagebar.css({
					display: 'block',
					opacity: 0,
					height: 'auto',
				});
				this.$messagebar.animate({
					opacity: 1,
				}, this.battle.messageFadeTime / this.acceleration);
			}
		}
		if (this.battle.hardcoreMode && message.startsWith('<small>(')) {
			message = '';
		}
		if (message && this.animating) {
			this.$hiddenMessage.append('<p></p>');
			let $message = this.$hiddenMessage.children().last();
			$message.html(message);
			$message.css({
				display: 'block',
				opacity: 0,
			});
			$message.animate({
				height: 'hide',
			}, 1, () => {
				$message.appendTo(this.$messagebar);
				$message.animate({
					height: 'show',
					'padding-bottom': 4,
					opacity: 1,
				}, this.battle.messageFadeTime / this.acceleration);
			});
			this.waitFor($message);
		}
		this.messagebarOpen = true;
	}
	maybeCloseMessagebar(args: Args, kwArgs: KWArgs) {
		if (this.log.battleParser!.sectionBreak(args, kwArgs)) {
			if (!this.messagebarOpen) return false;
			this.closeMessagebar();
			return true;
		}
		return false;
	}
	closeMessagebar() {
		if (this.messagebarOpen) {
			this.messagebarOpen = false;
			if (this.animating) {
				this.$messagebar.delay(this.battle.messageShownTime / this.acceleration).animate({
					opacity: 0,
				}, this.battle.messageFadeTime / this.acceleration);
				this.waitFor(this.$messagebar);
			}
			return true;
		}
		return false;
	}

	// General updating
	/////////////////////////////////////////////////////////////////////

	runMoveAnim(moveid: ID, participants: Pokemon[]) {
		if (!this.animating) return;
		let animEntry = BattleMoveAnims[moveid];
		if (this.acceleration >= 3) {
			const targetsSelf = !participants[1] || participants[0] === participants[1];
			const isSpecial = !targetsSelf && this.battle.dex.moves.get(moveid).category === 'Special';
			animEntry = BattleOtherAnims[targetsSelf ? 'fastanimself' : isSpecial ? 'fastanimspecial' : 'fastanimattack'];
		} else if (!animEntry) {
			animEntry = BattleMoveAnims['tackle'];
		}
		// cherish-hit-fx: 연출이 도는 동안 방어자 자리에 닿는 이펙트의 시각을 모은다
		this.cwHitProbe = { sprite: participants[1] ? participants[1].sprite : null, times: [], marks: [], reactions: [], elapsed: 0 };
		// cherish-fx2: 구슬만 쓰던 기술은 속성 조립 연출로 갈아 끼운다
		if (this.cwAutoOn() && CW_AUTO_MOVES[moveid]) {
			let cwMove: any = null;
			try { cwMove = this.battle.dex.moves.get(moveid); } catch {}
			if (cwMove) {
				this.cwAutoMove(participants.map(p => p.sprite), cwMove);
			} else {
				animEntry.anim(this, participants.map(p => p.sprite));
			}
		} else {
			animEntry.anim(this, participants.map(p => p.sprite));
		}
		const cwProbe = this.cwHitProbe;
		this.cwHitProbe = null;
		const candidates = cwProbe ? (cwProbe.marks.length ? cwProbe.marks :
			(cwProbe.times.length ? cwProbe.times : cwProbe.reactions)) : [];
		const measured = candidates.length > 0;
		this.cwHitTime = measured ? Math.min.apply(Math, candidates) : 300;
		this.cwPlayMoveSound(moveid, measured); // cherish-fx-sound

	}

	runOtherAnim(moveid: ID, participants: Pokemon[]) {
		if (!this.animating) return;
		BattleOtherAnims[moveid].anim(this, participants.map(p => p.sprite));
	}

	runStatusAnim(moveid: ID, participants: Pokemon[]) {
		if (!this.animating) return;
		BattleStatusAnims[moveid].anim(this, participants.map(p => p.sprite));
	}

	runResidualAnim(moveid: ID, pokemon: Pokemon) {
		if (!this.animating) return;
		BattleMoveAnims[moveid].residualAnim!(this, [pokemon.sprite]);
	}

	runPrepareAnim(moveid: ID, attacker: Pokemon, defender: Pokemon) {
		if (!this.animating || this.acceleration >= 3) return;
		const moveAnim = BattleMoveAnims[moveid];
		if (!moveAnim.prepareAnim) return;
		moveAnim.prepareAnim(this, [attacker.sprite, defender.sprite]);
	}

	updateGen() {
		let gen = this.battle.gen;
		if (Dex.prefs('nopastgens')) gen = 6;
		if (Dex.prefs('bwgfx') && gen > 5) gen = 5;
		this.gen = gen;
		this.activeCount = this.battle.nearSide?.active.length || 1;

		const rated = this.battle.rated;
		let bg: string;
		if (typeof rated === 'string' && rated.startsWith("Smogon Premier League")) {
			if (gen <= 1) bg = 'fx/bg-gen1-spl.png';
			else if (gen <= 2) bg = 'fx/bg-gen2-spl.png';
			else if (gen <= 3) bg = 'fx/bg-gen3-spl.png';
			else if (gen <= 4) bg = 'fx/bg-gen4-spl.png';
			else bg = 'fx/bg-spl.png';
		} else if (typeof rated === 'string' && rated.startsWith('National Pokemon Association')) {
			bg = 'fx/bg-npa.png';
		} else if (typeof rated === 'string' && rated.startsWith('World Cup of Pokemon')) {
			bg = 'fx/bg-wcop.png';
		} else if (typeof rated === 'string' && rated.startsWith('Smogon Champions League')) {
			bg = 'fx/bg-scl.png';
		} else {
			if (gen <= 1) bg = 'fx/bg-gen1.png?';
			else if (gen <= 2) bg = 'fx/bg-gen2.png?';
			else if (gen <= 3) bg = `fx/${BattleBackdropsThree[this.numericId % BattleBackdropsThree.length]}?`;
			else if (gen <= 4) bg = `fx/${BattleBackdropsFour[this.numericId % BattleBackdropsFour.length]}`;
			else if (gen <= 5) bg = `fx/${BattleBackdropsFive[this.numericId % BattleBackdropsFive.length]}`;
			else bg = `sprites/gen6bgs/${BattleBackdrops[this.numericId % BattleBackdrops.length]}`;
		}

		this.backdropImage = bg;
		if (this.$bg) {
			this.$bg.css('background-image', `url(${Dex.resourcePrefix}${this.backdropImage})`);
		}
	}

	getDetailsText(pokemon: Pokemon) {
		let name = pokemon.side?.isFar &&
			(this.battle.ignoreOpponent || this.battle.ignoreNicks) ? pokemon.speciesForme : pokemon.name;
		if (name !== pokemon.speciesForme) {
			name += ' (' + pokemon.speciesForme + ')';
		}
		if (pokemon === pokemon.side.active[0]) {
			name += ' (active)';
		} else if (pokemon.fainted) {
			name += ' (fainted)';
		} else {
			let statustext = '';
			if (pokemon.hp !== pokemon.maxhp) {
				statustext += pokemon.getHPText();
			}
			if (pokemon.status) {
				if (statustext) statustext += '|';
				statustext += pokemon.status;
			}
			if (statustext) {
				name += ' (' + statustext + ')';
			}
		}
		return BattleLog.escapeHTML(name);
	}
	getSidebarHTML(side: Side, posStr: string): string {
		let noShow = this.battle.hardcoreMode && this.battle.gen < 7;

		let speciesOverage = this.battle.speciesClause ? Infinity : Math.max(side.pokemon.length - side.totalPokemon, 0);
		const sidebarIcons: (
			['pokemon' | 'pokemon-illusion', number] | ['unrevealed' | 'empty' | 'pseudo-zoroark', null]
		)[] = [];
		const speciesTable: string[] = [];
		let zoroarkRevealed = false;
		let hasIllusion = false;
		if (speciesOverage) {
			for (let i = 0; i < side.pokemon.length; i++) {
				const species = side.pokemon[i].getBaseSpecies().baseSpecies;
				if (speciesOverage && speciesTable.includes(species)) {
					for (const sidebarIcon of sidebarIcons) {
						if (side.pokemon[sidebarIcon[1]!].getBaseSpecies().baseSpecies === species) {
							sidebarIcon[0] = 'pokemon-illusion';
						}
					}
					hasIllusion = true;
					speciesOverage--;
				} else {
					sidebarIcons.push(['pokemon', i]);
					speciesTable.push(species);
					if (['Zoroark', 'Zorua'].includes(species)) {
						zoroarkRevealed = true;
					}
				}
			}
		} else {
			for (let i = 0; i < side.pokemon.length; i++) {
				sidebarIcons.push(['pokemon', i]);
			}
		}
		if (!zoroarkRevealed && hasIllusion && sidebarIcons.length < side.totalPokemon) {
			sidebarIcons.push(['pseudo-zoroark', null]);
		}
		while (sidebarIcons.length < side.totalPokemon) {
			sidebarIcons.push(['unrevealed', null]);
		}
		while (sidebarIcons.length < 6) {
			sidebarIcons.push(['empty', null]);
		}

		let pokemonhtml = '';
		for (let i = 0; i < sidebarIcons.length; i++) {
			const [iconType, pokeIndex] = sidebarIcons[i];
			const poke = pokeIndex !== null ? side.pokemon[pokeIndex] : null;
			const tooltipCode = ` class="picon has-tooltip" data-tooltip="pokemon|${side.n}|${pokeIndex!}${iconType === 'pokemon-illusion' ? '|illusion' : ''}"`;
			if (iconType === 'empty') {
				pokemonhtml += `<span class="picon" style="${Dex.getPokemonIcon('pokeball-none')}"></span>`;
			} else if (noShow) {
				if (poke?.fainted) {
					pokemonhtml += `<span${tooltipCode} style="${Dex.getPokemonIcon('pokeball-fainted')}" aria-label="Fainted"></span>`;
				} else if (poke?.status) {
					pokemonhtml += `<span${tooltipCode} style="${Dex.getPokemonIcon('pokeball-statused')}" aria-label="Statused"></span>`;
				} else {
					pokemonhtml += `<span${tooltipCode} style="${Dex.getPokemonIcon('pokeball')}" aria-label="Non-statused"></span>`;
				}
			} else if (iconType === 'pseudo-zoroark') {
				pokemonhtml += `<span class="picon" style="${Dex.getPokemonIcon('zoroark')}" title="Unrevealed Illusion user" aria-label="Unrevealed Illusion user"></span>`;
			} else if (!poke) {
				pokemonhtml += `<span class="picon" style="${Dex.getPokemonIcon('pokeball')}" title="아직 공개되지 않음" aria-label="아직 공개되지 않음"></span>`;
			} else if (!poke.ident && this.battle.teamPreviewCount && this.battle.teamPreviewCount < side.pokemon.length) {
				// in VGC (bring 6 pick 4) and other pick-less-than-you-bring formats, this is
				// a pokemon that's been brought but not necessarily picked
				const details = this.getDetailsText(poke);
				pokemonhtml += `<span${tooltipCode} style="${Dex.getPokemonIcon(poke, !side.isFar)};opacity:0.6" aria-label="${details}"></span>`;
			} else {
				const details = this.getDetailsText(poke);
				pokemonhtml += `<span${tooltipCode} style="${Dex.getPokemonIcon(poke, !side.isFar)}" aria-label="${details}"></span>`;
			}
			if (i % 3 === 2) pokemonhtml += `</div><div class="teamicons">`;
		}
		pokemonhtml = '<div class="teamicons">' + pokemonhtml + '</div>';
		const ratinghtml = side.rating ? ` title="Rating: ${BattleLog.escapeHTML(side.rating)}"` : ``;
		const faded = side.name ? `` : ` style="opacity: 0.4"`;
		let badgehtml = '';
		if (side.badges.length) {
			badgehtml = '<span class="badges">';
			// hard limiting it to only ever 3 allowed at a time
			// that's what the server limit is anyway but there should be a client limit too
			// just in case
			for (const badgeData of side.badges.slice(0, 3)) {
				// ${badge.type}|${badge.format}|${BADGE_THRESHOLDS[badge.type]}-${badge.season}
				const [type, format, details] = badgeData.split('|');
				// todo, maybe make this more easily configured if we ever add badges for other stuff?
				// but idk that we're planning that for now so
				const [threshold] = details.split('-');
				const hover = `User is Top ${threshold} on the ${format} Ladder`;
				// ou and randbats get diff badges from everyone else, find it
				// (regex futureproofs for double digit gens)
				let formatType = format.split(/gen\d+/)[1] || 'none';
				if (!['ou', 'randombattle'].includes(formatType)) {
					formatType = 'rotating';
				}
				badgehtml += `<img src="${Dex.resourcePrefix}/sprites/misc/${formatType}_${type}.png" style="padding: 0px 1px 0px 1px" width="16px" height="16px" title="${hover}" />`;
			}
			badgehtml += '</span>';
		}
		let avatar = Dex.resolveAvatar(side.avatar);
		// cherish fork: 체리쉬홈 아바타는 상반신 크롭 대신 전신(-full) 고화질 표시
		let spriteStyle = `background-image:url(${avatar})`;
		// cherish fork: 체리쉬홈 아바타는 상반신 크롭 대신 전신(-full) 표시 (patch-battle-view.py)
		let cherishFullClass = '';
		if (String(side.avatar).startsWith('#cherish-')) {
			const fullUrl = `/sprites/trainers-custom/cherish${String(side.avatar).slice(9)}-full.png`;
			// 한 바(450px)에 트레이너가 2명 들어가는 판(2대2 멀티 · 난투전)에서는 전신 144px 가
			// 구조적으로 안 들어간다. 96px 로 낮추고 위아래 위치도 CSS 에서 다시 잡는다.
			// 판정은 updateLeftSidebar() 의 2인 배치 조건과 **같은 식**이어야 한다 —
			// gameType 이름으로 가르면 난투전이 샌다 (2026-09-04 실사고).
			const twoPerBar = !!side.ally || this.battle.sides.length > 2;
			const h = twoPerBar ? 90 : 144;
			spriteStyle = `background-image:url(${fullUrl});background-size:contain;background-repeat:no-repeat;background-position:center bottom;width:96px;height:${h}px;max-width:96px;max-height:${h}px;margin:0 auto`;
			// 블록이 높아져 기본 bottom 값(.trainer-far 232px 등)으로는 .battle{overflow:hidden}
			// 에 잘리거나 위아래가 겹친다 — 두 경우 다 CSS 에서 다시 잡는다 (cherish-skin.css §9)
			cherishFullClass = twoPerBar ? ' cw-halfavatar' : ' cw-fullavatar';
		}
		// cherish-blossom-badge:begin (patch-class-blossom.py)
		// 반 표식 — 전신 아바타 옆 반 꽃 (patch-class-blossom.py, 2026-09-14).
		// 표는 |cherishclass| 로 들어온다. 익명 좌석은 아바타가 '#cherish-0' 이라 여기 안 걸린다.
		let cherishClassBadge = '';
		if (String(side.avatar).startsWith('#cherish-')) {
			const cherishClsMap = (this.battle as any).cherishClassBadges;
			const cherishClsOne = cherishClsMap ? cherishClsMap[String(side.avatar).slice(9)] : null;
			if (cherishClsOne) cherishClassBadge = `<span class="cw-cls-badge">${BattleLog.escapeHTML(String(cherishClsOne))}</span>`;
		}
		// cherish-blossom-badge:end
		let noflip = '';
		if (posStr.startsWith('near')) {
			if (avatar.includes('unknown.png')) {
				avatar = avatar.replace('unknown.png', 'unknown-flipped.png');
				noflip = ' noflip';
			} else if (avatar.includes('unknownf.png')) {
				avatar = avatar.replace('unknownf.png', 'unknownf-flipped.png');
				noflip = ' noflip';
			}
		}
		return (
			`<div class="trainer trainer-${posStr}${cherishFullClass}"${faded}><strong>${BattleLog.escapeHTML(side.name)}${cherishClassBadge}</strong>` +
			`<div class="trainersprite${noflip}"${ratinghtml} style="${spriteStyle}">` +
			`</div>${badgehtml}${pokemonhtml}</div>`
		);
	}
	updateSidebar(side: Side) {
		if (this.battle.gameType === 'freeforall') {
			this.updateLeftSidebar();
			this.updateRightSidebar();
		} else if (side === this.battle.nearSide || side === this.battle.nearSide.ally) {
			this.updateLeftSidebar();
		} else {
			this.updateRightSidebar();
		}
	}
	updateLeftSidebar() {
		const side = this.battle.nearSide;

		if (side.ally) {
			const side2 = side.ally;
			this.$leftbar.html(this.getSidebarHTML(side, 'near2') + this.getSidebarHTML(side2, 'near'));
		} else if (this.battle.sides.length > 2) { // FFA
			const side2 = this.battle.sides[side.n === 0 ? 3 : 2];
			this.$leftbar.html(this.getSidebarHTML(side2, 'near2') + this.getSidebarHTML(side, 'near'));
		} else {
			this.$leftbar.html(this.getSidebarHTML(side, 'near'));
		}
	}
	updateRightSidebar() {
		const side = this.battle.farSide;

		if (side.ally) {
			const side2 = side.ally;
			this.$rightbar.html(this.getSidebarHTML(side, 'far2') + this.getSidebarHTML(side2, 'far'));
		} else if (this.battle.sides.length > 2) { // FFA
			const side2 = this.battle.sides[side.n === 0 ? 3 : 2];
			this.$rightbar.html(this.getSidebarHTML(side2, 'far2') + this.getSidebarHTML(side, 'far'));
		} else {
			this.$rightbar.html(this.getSidebarHTML(side, 'far'));
		}
	}
	updateSidebars() {
		this.updateLeftSidebar();
		this.updateRightSidebar();
	}
	updateStatbars() {
		for (const side of this.battle.sides) {
			for (const active of side.active) {
				if (active) active.sprite.updateStatbar(active);
			}
		}
	}

	resetSides(skipEmpty?: boolean) {
		if (!skipEmpty) {
			for (const $spritesContainer of this.$sprites) {
				$spritesContainer.empty();
			}
		}
		for (const side of this.battle.sides) {
			side.z = (side.isFar ? 200 : 0);
			side.missedPokemon?.sprite?.destroy();

			side.missedPokemon = {
				sprite: new PokemonSprite(null, {
					x: side.leftof(this.battle.gameType === 'freeforall' ? -50 : -100),
					y: side.y,
					z: side.z,
					opacity: 0,
				}, this, side.isFar),
			} as any;

			side.missedPokemon.sprite.isMissedPokemon = true;
		}
		if (this.battle.sides.length > 2 && this.sideConditions.length === 2) {
			this.sideConditions.push({}, {});
		}
		this.rebuildTooltips();
	}
	rebuildTooltips() {
		let tooltipBuf = '';
		const tooltips = this.battle.gameType === 'freeforall' ? {
			// FFA battles are visually rendered as triple battle with the center slots empty
			// so we swap the 2nd and 3rd tooltips on each side
			p2b: { top: 70, left: 250, width: 80, height: 100, tooltip: 'activepokemon|1|1' },
			p2a: { top: 90, left: 390, width: 100, height: 100, tooltip: 'activepokemon|1|0' },
			p1a: { top: 200, left: 130, width: 120, height: 160, tooltip: 'activepokemon|0|0' },
			p1b: { top: 200, left: 350, width: 150, height: 160, tooltip: 'activepokemon|0|1' },
		} : {
			p2c: { top: 70, left: 250, width: 80, height: 100, tooltip: 'activepokemon|1|2' },
			p2b: { top: 85, left: 320, width: 90, height: 100, tooltip: 'activepokemon|1|1' },
			p2a: { top: 90, left: 390, width: 100, height: 100, tooltip: 'activepokemon|1|0' },
			p1a: { top: 200, left: 130, width: 120, height: 160, tooltip: 'activepokemon|0|0' },
			p1b: { top: 200, left: 250, width: 150, height: 160, tooltip: 'activepokemon|0|1' },
			p1c: { top: 200, left: 350, width: 150, height: 160, tooltip: 'activepokemon|0|2' },
		};
		for (const id in tooltips) {
			let layout = tooltips[id as 'p1a'];
			tooltipBuf += `<div class="has-tooltip" style="position:absolute;`;
			tooltipBuf += `top:${layout.top}px;left:${layout.left}px;width:${layout.width}px;height:${layout.height}px;`;
			tooltipBuf += `" data-id="${id}" data-tooltip="${layout.tooltip}" data-ownheight="1"></div>`;
		}
		this.$tooltips.html(tooltipBuf);
	}

	teamPreview() {
		for (let siden = 0; siden < 2 || (this.battle.gameType === 'multi' && siden < 4); siden++) {
			let side = this.battle.sides[siden];
			const spriteIndex = +this.battle.viewpointSwitched ^ (siden % 2);
			let textBuf = '';
			let buf = '';
			let buf2 = '';
			this.$sprites[spriteIndex].empty();

			for (let i = 0; i < side.pokemon.length; i++) {
				let pokemon = side.pokemon[i];
				if (pokemon.speciesForme === 'Xerneas-*') {
					pokemon.speciesForme = 'Xerneas-Neutral';
				}

				let spriteData = Dex.getSpriteData(pokemon, !!spriteIndex, {
					gen: this.gen,
					noScale: true,
					mod: this.mod,
				});
				let y = 0;
				let x = 0;
				if (spriteIndex) {
					y = 48 + 50 + 3 * (i + 6 - side.pokemon.length);
					x = 48 + 180 + 50 * (i + 6 - side.pokemon.length);
				} else {
					y = 48 + 200 + 3 * i;
					x = 48 + 100 + 50 * i;
				}
				if (textBuf) textBuf += ' / ';
				textBuf += CherishKo.species(pokemon.speciesForme);
				let url = spriteData.url;
				// if (this.paused) url.replace('/xyani', '/xy').replace('.gif', '.png');
				buf += `<img src="${url}" width="${spriteData.w}" height="${spriteData.h}" style="position:absolute;top:${Math.floor(y - spriteData.h / 2)}px;left:${Math.floor(x - spriteData.w / 2)}px" />`;
				buf2 += `<div style="position:absolute;top:${y + 45}px;left:${x - 40}px;width:80px;font-size:10px;text-align:center;color:#FFF;">`;
				const gender = pokemon.gender;
				if (gender === 'M' || gender === 'F') {
					buf2 += `<img src="${Dex.fxPrefix}gender-${gender.toLowerCase()}.png" alt="${gender}" width="7" height="10" class="pixelated" style="margin-bottom:-1px" /> `;
				}
				// cherish fork: 레벨은 그리지 않는다 — 유저 전원 50 이고, 시험관·탑 봇의 레벨 핸디캡(30~45)은 상대가 봇·NPC 라는 표가 된다 (2026-09-07)
				if (pokemon.level < 0) {
					buf2 += `<span style="text-shadow:#000 1px 1px 0,#000 1px -1px 0,#000 -1px 1px 0,#000 -1px -1px 0"><small>L</small>${pokemon.level}</span>`;
				}
				if (pokemon.item === '(mail)') {
					buf2 += ` <img src="${Dex.resourcePrefix}fx/mail.png" width="8" height="10" alt="F" style="margin-bottom:-1px" />`;
				} else if (pokemon.item) {
					buf2 += ` <img src="${Dex.resourcePrefix}fx/item.png" width="8" height="10" alt="F" style="margin-bottom:-1px" />`;
				}
				buf2 += '</div>';
			}
			side.totalPokemon = side.pokemon.length;
			if (textBuf) {
				this.log.addDiv('chat battle-history',
					`<strong>${BattleLog.escapeHTML(side.name)}의 팀:</strong> <em style="color:#445566;display:block;">${BattleLog.escapeHTML(textBuf)}</em>`
				);
			}
			this.$sprites[spriteIndex].html(buf + buf2);

		}
		this.wait(1000);
		this.updateSidebars();
	}

	showJoinButtons() {
		if (!this.battle.joinButtons) return;
		if (this.battle.ended || this.battle.rated) return;
		if (!this.battle.p1.name) {
			this.$battle.append('<div class="playbutton1"><button name="joinBattle">배틀 참가</button></div>');
		}
		if (!this.battle.p2.name) {
			this.$battle.append('<div class="playbutton2"><button name="joinBattle">배틀 참가</button></div>');
		}
	}
	hideJoinButtons() {
		if (!this.battle.joinButtons) return;
		this.$battle.find('.playbutton1, .playbutton2').remove();
	}

	// cherish-fieldbar: 필드 상태 바 이름 — ko 로케일 우선, 없으면 상류 영어명 폴백.
	// Dex.moves.get().name 은 내부 영어명이라 그대로 쓰면 안 된다.
	fieldEffectName(id: string) {
		const move = Dex.moves.get(id);
		return Dex.text.get(move).name || move.name;
	}
	pseudoWeatherLeft(pWeather: WeatherState) {
		let buf = `<br />${this.fieldEffectName(pWeather[0])}`;
		if (!pWeather[1] && pWeather[2]) {
			pWeather[1] = pWeather[2];
			pWeather[2] = 0;
		}
		if (this.battle.gen < 7 && this.battle.hardcoreMode) return buf;
		if (pWeather[2]) {
			return `${buf} <small>(${pWeather[1]}~${pWeather[2]}턴)</small>`;
		}
		if (pWeather[1]) {
			return `${buf} <small>(${pWeather[1]}턴)</small>`;
		}
		return buf; // weather not found
	}
	sideConditionLeft(cond: Side['sideConditions'][string], isFoe: boolean, all?: boolean) {
		if (!cond[2] && !cond[3] && !all) return '';
		let buf = `<br />${isFoe && !all ? '상대의 ' : ''}${this.fieldEffectName(cond[0])}`;
		if (this.battle.gen < 7 && this.battle.hardcoreMode) return buf;

		if (!cond[2] && !cond[3]) return buf;
		if (!cond[2] && cond[3]) {
			cond[2] = cond[3];
			cond[3] = 0;
		}
		if (!cond[3]) {
			return `${buf} <small>(${cond[2]}턴)</small>`;
		}
		return `${buf} <small>(${cond[2]}~${cond[3]}턴)</small>`;
	}
	weatherLeft() {
		if (this.battle.gen < 7 && this.battle.hardcoreMode) return '';

		let weatherhtml = ``;

		if (this.battle.weather) {
			const weatherNameTable: { [id: string]: string } = {
				sunnyday: '쾌청',
				desolateland: '강한햇살',
				raindance: '비',
				primordialsea: '강한비',
				sandstorm: '모래바람',
				hail: '싸라기눈',
				snowscape: '설경',
				deltastream: '난기류',
			};
			weatherhtml = `${weatherNameTable[this.battle.weather] || this.battle.weather}`;
			if (this.battle.weatherMinTimeLeft !== 0) {
				weatherhtml += ` <small>(${this.battle.weatherMinTimeLeft}~${this.battle.weatherTimeLeft}턴)</small>`;
			} else if (this.battle.weatherTimeLeft !== 0) {
				weatherhtml += ` <small>(${this.battle.weatherTimeLeft}턴)</small>`;
			}
			const nullifyWeather = this.battle.abilityActive(['Air Lock', 'Cloud Nine']);
			weatherhtml = `${nullifyWeather ? '<s>' : ''}${weatherhtml}${nullifyWeather ? '</s>' : ''}`;
		}

		for (const pseudoWeather of this.battle.pseudoWeather) {
			weatherhtml += this.pseudoWeatherLeft(pseudoWeather);
		}

		return weatherhtml;
	}
	sideConditionsLeft(side: Side, all?: boolean) {
		let buf = ``;
		for (const id in side.sideConditions) {
			buf += this.sideConditionLeft(side.sideConditions[id], side.isFar, all);
		}
		return buf;
	}
	upkeepWeather() {
		const isIntense = ['desolateland', 'primordialsea', 'deltastream'].includes(this.curWeather);
		this.$weather.animate({
			opacity: 1.0,
		}, 300).animate({
			opacity: isIntense ? 0.9 : 0.5,
		}, 300);
	}
	updateWeather(instant?: boolean) {
		if (!this.animating) return;
		let isIntense = false;
		let weather = this.battle.weather;
		if (this.battle.abilityActive(['Air Lock', 'Cloud Nine'])) {
			weather = '' as ID;
		}
		let terrain = '' as ID;
		for (const pseudoWeatherData of this.battle.pseudoWeather) {
			terrain = toID(pseudoWeatherData[0]);
		}
		if (weather === 'desolateland' || weather === 'primordialsea' || weather === 'deltastream') {
			isIntense = true;
		}

		let weatherhtml = this.weatherLeft();
		for (const side of this.battle.sides) {
			weatherhtml += this.sideConditionsLeft(side);
		}
		if (weatherhtml) weatherhtml = `<br />` + weatherhtml;

		if (instant) {
			this.$weather.html('<em>' + weatherhtml + '</em>');
			if (this.curWeather === weather && this.curTerrain === terrain) return;
			this.$terrain.attr('class', terrain ? 'weather ' + terrain + 'weather' : 'weather');
			this.curTerrain = terrain;
			this.$weather.attr('class', weather ? 'weather ' + weather + 'weather' : 'weather');
			this.$weather.css('opacity', isIntense || !weather ? 0.9 : 0.5);
			this.curWeather = weather;
			return;
		}

		if (weather !== this.curWeather) {
			this.$weather.animate({
				opacity: 0,
			}, this.curWeather ? 300 : 100, () => {
				this.$weather.html('<em>' + weatherhtml + '</em>');
				this.$weather.attr('class', weather ? 'weather ' + weather + 'weather' : 'weather');
				this.$weather.animate({ opacity: isIntense || !weather ? 0.9 : 0.5 }, 300);
			});
			this.curWeather = weather;
		} else {
			this.$weather.html('<em>' + weatherhtml + '</em>');
		}

		if (terrain !== this.curTerrain) {
			this.$terrain.animate({
				top: 360,
				opacity: 0,
			}, this.curTerrain ? 400 : 1, () => {
				this.$terrain.attr('class', terrain ? 'weather ' + terrain + 'weather' : 'weather');
				this.$terrain.animate({ top: 0, opacity: 1 }, 400);
			});
			this.curTerrain = terrain;
		}
	}
	resetTurn() {
		if (this.battle.turn <= 0) {
			this.$turn.html('');
			return;
		}
		this.$turn.html(`<div class="turn has-tooltip" data-tooltip="field" data-ownheight="1">${this.battle.turn}턴</div>`);
	}
	incrementTurn() {
		if (!this.animating) return;

		const turn = this.battle.turn;
		if (turn <= 0) return;
		const $prevTurn = this.$turn.children();
		const $newTurn = $(`<div class="turn has-tooltip" data-tooltip="field" data-ownheight="1">${turn}턴</div>`);
		$newTurn.css({
			opacity: 0,
			left: 160,
		});
		this.$turn.append($newTurn);
		$newTurn.animate({
			opacity: 1,
			left: 110,
		}, 500).animate({
			opacity: 0.4,
		}, 1500);
		$prevTurn.animate({
			opacity: 0,
			left: 60,
		}, 500, () => {
			$prevTurn.remove();
		});
		this.updateAcceleration();
		this.wait(500 / this.acceleration);
	}
	updateAcceleration() {
		if (this.battle.turnsSinceMoved > 2) {
			this.acceleration = (this.battle.messageFadeTime < 150 ? 2 : 1) * Math.min(this.battle.turnsSinceMoved - 1, 3);
		} else {
			this.acceleration = (this.battle.messageFadeTime < 150 ? 2 : 1);
			if (this.battle.messageFadeTime < 50) this.acceleration = 3;
		}
	}

	addPokemonSprite(pokemon: Pokemon) {
		const sprite = new PokemonSprite(Dex.getSpriteData(pokemon, pokemon.side.isFar, {
			gen: this.gen,
			mod: this.mod,
		}), {
			x: pokemon.side.x,
			y: pokemon.side.y,
			z: pokemon.side.z,
			opacity: 0,
		}, this, pokemon.side.isFar);
		if (sprite.$el) this.$sprites[+pokemon.side.isFar].append(sprite.$el);
		return sprite;
	}

	addSideCondition(siden: number, id: ID, instant?: boolean) {
		if (!this.animating) return;
		const side = this.battle.sides[siden];
		const spriteIndex = +side.isFar;
		let x = side.x;
		let y = side.y;
		if (this.battle.gameType === 'freeforall') {
			x += side.isFar ? 20 : -20;
			if (side.n > 1) {
				x += (side.isFar ? -140 : 140);
				y += side.isFar ? 14 : -20;
			}
		}

		switch (id) {
		case 'auroraveil':
			const auroraveil = new Sprite(BattleEffects.auroraveil, {
				display: 'block',
				x,
				y,
				z: side.behind(-14),
				xscale: 1,
				yscale: 0,
				opacity: 0.1,
			}, this);
			this.$spritesFront[spriteIndex].append(auroraveil.$el);
			this.sideConditions[siden][id] = [auroraveil];
			auroraveil.anim({
				opacity: 0.7,
				time: instant ? 0 : 400,
			}).anim({
				opacity: 0.3,
				time: instant ? 0 : 300,
			});
			break;
		case 'reflect':
			const reflect = new Sprite(BattleEffects.reflect, {
				display: 'block',
				x,
				y,
				z: side.behind(-17),
				xscale: 1,
				yscale: 0,
				opacity: 0.1,
			}, this);
			this.$spritesFront[spriteIndex].append(reflect.$el);
			this.sideConditions[siden][id] = [reflect];
			reflect.anim({
				opacity: 0.7,
				time: instant ? 0 : 400,
			}).anim({
				opacity: 0.3,
				time: instant ? 0 : 300,
			});
			break;
		case 'safeguard':
			const safeguard = new Sprite(BattleEffects.safeguard, {
				display: 'block',
				x,
				y,
				z: side.behind(-20),
				xscale: 1,
				yscale: 0,
				opacity: 0.1,
			}, this);
			this.$spritesFront[spriteIndex].append(safeguard.$el);
			this.sideConditions[siden][id] = [safeguard];
			safeguard.anim({
				opacity: 0.7,
				time: instant ? 0 : 400,
			}).anim({
				opacity: 0.3,
				time: instant ? 0 : 300,
			});
			break;
		case 'lightscreen':
			const lightscreen = new Sprite(BattleEffects.lightscreen, {
				display: 'block',
				x,
				y,
				z: side.behind(-23),
				xscale: 1,
				yscale: 0,
				opacity: 0.1,
			}, this);
			this.$spritesFront[spriteIndex].append(lightscreen.$el);
			this.sideConditions[siden][id] = [lightscreen];
			lightscreen.anim({
				opacity: 0.7,
				time: instant ? 0 : 400,
			}).anim({
				opacity: 0.3,
				time: instant ? 0 : 300,
			});
			break;
		case 'mist':
			const mist = new Sprite(BattleEffects.mist, {
				display: 'block',
				x,
				y,
				z: side.behind(-27),
				xscale: 1,
				yscale: 0,
				opacity: 0.1,
			}, this);
			this.$spritesFront[spriteIndex].append(mist.$el);
			this.sideConditions[siden][id] = [mist];
			mist.anim({
				opacity: 0.7,
				time: instant ? 0 : 400,
			}).anim({
				opacity: 0.3,
				time: instant ? 0 : 300,
			});
			break;
		case 'stealthrock':
			const rock1 = new Sprite(BattleEffects.rock1, {
				display: 'block',
				x: x + side.leftof(-40),
				y: y - 10,
				z: side.z,
				opacity: 0.5,
				scale: 0.2,
			}, this);

			const rock2 = new Sprite(BattleEffects.rock2, {
				display: 'block',
				x: x + side.leftof(-20),
				y: y - 40,
				z: side.z,
				opacity: 0.5,
				scale: 0.2,
			}, this);

			const rock3 = new Sprite(BattleEffects.rock1, {
				display: 'block',
				x: x + side.leftof(30),
				y: y - 20,
				z: side.z,
				opacity: 0.5,
				scale: 0.2,
			}, this);

			const rock4 = new Sprite(BattleEffects.rock2, {
				display: 'block',
				x: x + side.leftof(10),
				y: y - 30,
				z: side.z,
				opacity: 0.5,
				scale: 0.2,
			}, this);

			this.$spritesFront[spriteIndex].append(rock1.$el);
			this.$spritesFront[spriteIndex].append(rock2.$el);
			this.$spritesFront[spriteIndex].append(rock3.$el);
			this.$spritesFront[spriteIndex].append(rock4.$el);
			this.sideConditions[siden][id] = [rock1, rock2, rock3, rock4];
			break;
		case 'gmaxsteelsurge':
			const surge1 = new Sprite(BattleEffects.greenmetal1, {
				display: 'block',
				x: x + side.leftof(-30),
				y: y - 20,
				z: side.z,
				opacity: 0.5,
				scale: 0.8,
			}, this);
			const surge2 = new Sprite(BattleEffects.greenmetal2, {
				display: 'block',
				x: x + side.leftof(35),
				y: y - 15,
				z: side.z,
				opacity: 0.5,
				scale: 0.8,
			}, this);
			const surge3 = new Sprite(BattleEffects.greenmetal1, {
				display: 'block',
				x: x + side.leftof(50),
				y: y - 10,
				z: side.z,
				opacity: 0.5,
				scale: 0.8,
			}, this);

			this.$spritesFront[spriteIndex].append(surge1.$el);
			this.$spritesFront[spriteIndex].append(surge2.$el);
			this.$spritesFront[spriteIndex].append(surge3.$el);
			this.sideConditions[siden][id] = [surge1, surge2, surge3];
			break;
		case 'spikes':
			let spikeArray = this.sideConditions[siden]['spikes'];
			if (!spikeArray) {
				spikeArray = [];
				this.sideConditions[siden]['spikes'] = spikeArray;
			}
			let levels = this.battle.sides[siden].sideConditions['spikes'][1];
			if (spikeArray.length < 1 && levels >= 1) {
				const spike1 = new Sprite(BattleEffects.caltrop, {
					display: 'block',
					x: x - 25,
					y: y - 40,
					z: side.z,
					scale: 0.3,
				}, this);
				this.$spritesFront[spriteIndex].append(spike1.$el);
				spikeArray.push(spike1);
			}
			if (spikeArray.length < 2 && levels >= 2) {
				const spike2 = new Sprite(BattleEffects.caltrop, {
					display: 'block',
					x: x + 30,
					y: y - 45,
					z: side.z,
					scale: 0.3,
				}, this);
				this.$spritesFront[spriteIndex].append(spike2.$el);
				spikeArray.push(spike2);
			}
			if (spikeArray.length < 3 && levels >= 3) {
				const spike3 = new Sprite(BattleEffects.caltrop, {
					display: 'block',
					x: x + 50,
					y: y - 40,
					z: side.z,
					scale: 0.3,
				}, this);
				this.$spritesFront[spriteIndex].append(spike3.$el);
				spikeArray.push(spike3);
			}
			break;
		case 'toxicspikes':
			let tspikeArray = this.sideConditions[siden]['toxicspikes'];
			if (!tspikeArray) {
				tspikeArray = [];
				this.sideConditions[siden]['toxicspikes'] = tspikeArray;
			}
			let tspikeLevels = this.battle.sides[siden].sideConditions['toxicspikes'][1];
			if (tspikeArray.length < 1 && tspikeLevels >= 1) {
				const tspike1 = new Sprite(BattleEffects.poisoncaltrop, {
					display: 'block',
					x: x + 5,
					y: y - 40,
					z: side.z,
					scale: 0.3,
				}, this);
				this.$spritesFront[spriteIndex].append(tspike1.$el);
				tspikeArray.push(tspike1);
			}
			if (tspikeArray.length < 2 && tspikeLevels >= 2) {
				const tspike2 = new Sprite(BattleEffects.poisoncaltrop, {
					display: 'block',
					x: x - 15,
					y: y - 35,
					z: side.z,
					scale: 0.3,
				}, this);
				this.$spritesFront[spriteIndex].append(tspike2.$el);
				tspikeArray.push(tspike2);
			}
			break;
		case 'stickyweb':
			const web = new Sprite(BattleEffects.web, {
				display: 'block',
				x: x + 15,
				y: y - 35,
				z: side.z,
				opacity: 0.4,
				scale: 0.7,
			}, this);
			this.$spritesFront[spriteIndex].append(web.$el);
			this.sideConditions[siden][id] = [web];
			break;
		}
	}
	removeSideCondition(siden: number, id: ID) {
		if (!this.animating) return;
		if (this.sideConditions[siden][id]) {
			for (const sprite of this.sideConditions[siden][id]) sprite.destroy();
			delete this.sideConditions[siden][id];
		}
	}
	resetSideConditions() {
		for (let siden = 0; siden < this.sideConditions.length; siden++) {
			for (const id in this.sideConditions[siden]) {
				this.removeSideCondition(siden, id as ID);
			}
			for (const id in this.battle.sides[siden].sideConditions) {
				this.addSideCondition(siden, id as ID, true);
			}
		}
	}

	typeAnim(pokemon: Pokemon, types: string) {
		const result = BattleLog.escapeHTML(types).split('/').map(type =>
			`<img src="${Dex.resourcePrefix}sprites/types/${encodeURIComponent(type)}.png" alt="${type}" class="pixelated" />`
		).join(' ');
		this.resultAnim(pokemon, result, 'neutral');
	}
	resultAnim(pokemon: Pokemon, result: string, type: 'bad' | 'good' | 'neutral' | Dex.StatusName) {
		if (!this.animating) return;
		let $effect = $('<div class="result ' + type + 'result"><strong>' + result + '</strong></div>');
		// cherish-hit-fx: 피해 숫자에만 팝을 준다 (급소·효과 굉장이면 더 크게)
		if (this.cwResultPop) {
			$effect.addClass('cw-pop' + (this.cwResultPop.crit ? ' cw-pop-crit' :
				this.cwResultPop.superEff ? ' cw-pop-super' : ''));
			this.cwResultPop = null;
		}
		this.$fx.append($effect);
		$effect.delay(this.timeOffset).css({
			display: 'block',
			opacity: 0,
			top: pokemon.sprite.top - 5,
			left: pokemon.sprite.left - 75,
		}).animate({
			opacity: 1,
		}, 1);
		$effect.animate({
			opacity: 0,
			top: pokemon.sprite.top - 65,
		}, 1000, 'swing');
		this.wait(this.acceleration < 2 ? 350 : 250);
		pokemon.sprite.updateStatbar(pokemon);
		if (this.acceleration < 3) this.waitFor($effect);
	}
	abilityActivateAnim(pokemon: Pokemon, result: string) {
		if (!this.animating) return;
		this.$fx.append(`<div class="result abilityresult"><strong>${CherishKo.ability(result)}</strong></div>`);
		let $effect = this.$fx.children().last();
		$effect.delay(this.timeOffset).css({
			display: 'block',
			opacity: 0,
			top: pokemon.sprite.top + 15,
			left: pokemon.sprite.left - 75,
		}).animate({
			opacity: 1,
		}, 1);
		$effect.delay(800).animate({
			opacity: 0,
		}, 400, 'swing');
		this.wait(100);
		pokemon.sprite.updateStatbar(pokemon);
		if (this.acceleration < 3) this.waitFor($effect);
	}
	damageAnim(pokemon: Pokemon, damage: number | string) {
		if (!this.animating) return;
		this.cwImpact(pokemon); // cherish-hit-fx
		if (!pokemon.sprite.$statbar) return;
		pokemon.sprite.updateHPText(pokemon);

		let $hp = pokemon.sprite.$statbar.find('div.hp');
		let w = pokemon.hpWidth(150);
		let hpcolor = BattleScene.getHPColor(pokemon);
		let callback;
		if (hpcolor === 'y') {
			callback = () => { $hp.addClass('hp-yellow'); };
		}
		if (hpcolor === 'r') {
			callback = () => { $hp.addClass('hp-yellow hp-red'); };
		}

		if (damage === '100%' && pokemon.hp > 0) damage = '99%';
		this.resultAnim(pokemon, this.battle.hardcoreMode ? '피해' : `&minus;${damage}`, 'bad');

		$hp.animate({
			width: w,
			'border-right-width': w ? 1 : 0,
		}, 350, callback);
	}
	healAnim(pokemon: Pokemon, damage: number | string) {
		if (!this.animating) return;
		if (!pokemon.sprite.$statbar) return;
		pokemon.sprite.updateHPText(pokemon);

		let $hp = pokemon.sprite.$statbar.find('div.hp');
		let w = pokemon.hpWidth(150);
		let hpcolor = BattleScene.getHPColor(pokemon);
		let callback;
		if (hpcolor === 'g') {
			callback = () => { $hp.removeClass('hp-yellow hp-red'); };
		}
		if (hpcolor === 'y') {
			callback = () => { $hp.removeClass('hp-red'); };
		}

		this.resultAnim(pokemon, this.battle.hardcoreMode ? '회복' : `+${damage}`, 'good');

		$hp.animate({
			width: w,
			'border-right-width': w ? 1 : 0,
		}, 350, callback);
	}

	// Sprite methods
	/////////////////////////////////////////////////////////////////////

	removeEffect(pokemon: Pokemon, id: ID, instant?: boolean) {
		return pokemon.sprite.removeEffect(id, instant);
	}
	addEffect(pokemon: Pokemon, id: ID, instant?: boolean) {
		return pokemon.sprite.addEffect(id, instant);
	}
	animSummon(pokemon: Pokemon, slot: number, instant?: boolean) {
		return pokemon.sprite.animSummon(pokemon, slot, instant);
	}
	animUnsummon(pokemon: Pokemon, instant?: boolean) {
		return pokemon.sprite.animUnsummon(pokemon, instant);
	}
	animDragIn(pokemon: Pokemon, slot: number) {
		return pokemon.sprite.animDragIn(pokemon, slot);
	}
	animDragOut(pokemon: Pokemon) {
		return pokemon.sprite.animDragOut(pokemon);
	}
	resetStatbar(pokemon: Pokemon, startHidden?: boolean) {
		return pokemon.sprite.resetStatbar(pokemon, startHidden);
	}
	updateStatbar(pokemon: Pokemon, updatePrevhp?: boolean, updateHp?: boolean) {
		return pokemon.sprite.updateStatbar(pokemon, updatePrevhp, updateHp);
	}
	updateStatbarIfExists(pokemon: Pokemon, updatePrevhp?: boolean, updateHp?: boolean) {
		return pokemon.sprite.updateStatbarIfExists(pokemon, updatePrevhp, updateHp);
	}
	animTransform(pokemon: Pokemon, useSpeciesAnim?: boolean, isPermanent?: boolean) {
		return pokemon.sprite.animTransform(pokemon, useSpeciesAnim, isPermanent);
	}
	clearEffects(pokemon: Pokemon) {
		return pokemon.sprite.clearEffects();
	}
	removeTransform(pokemon: Pokemon) {
		return pokemon.sprite.removeTransform();
	}
	animFaint(pokemon: Pokemon) {
		return pokemon.sprite.animFaint(pokemon);
	}
	animReset(pokemon: Pokemon) {
		return pokemon.sprite.animReset();
	}
	anim(pokemon: Pokemon, end: ScenePos, transition?: string) {
		return pokemon.sprite.anim(end, transition);
	}
	beforeMove(pokemon: Pokemon) {
		return pokemon.sprite.beforeMove();
	}
	afterMove(pokemon: Pokemon) {
		return pokemon.sprite.afterMove();
	}

	// Misc
	/////////////////////////////////////////////////////////////////////

	setFrameHTML(html: any) {
		this.customControls = true;
		this.$frame.html(html);
	}
	setControlsHTML(html: any) {
		this.customControls = true;
		let $controls = this.$frame.parent().children('.battle-controls');
		$controls.html(html);
	}

	preloadImage(url: string) {
		let token = url.replace(/\.(gif|png)$/, '').replace(/\//g, '-');
		if (this.preloadCache[token]) {
			return;
		}
		this.preloadNeeded++;
		this.preloadCache[token] = new Image();
		this.preloadCache[token].onload = () => {
			this.preloadDone++;
		};
		this.preloadCache[token].src = url;
	}
	preloadEffects() {
		for (let i in BattleEffects) {
			if (i === 'alpha' || i === 'omega') continue;
			const url = BattleEffects[i].url;
			if (url) this.preloadImage(url);
		}
		this.preloadImage(Dex.resourcePrefix + 'sprites/ani/substitute.gif');
		this.preloadImage(Dex.resourcePrefix + 'sprites/ani-back/substitute.gif');
	}
	rollBgm() {
		// cherish(2026-09-09 저녁): 곡은 **서버가 정해 방 로그로 내려준다** (`|cherishbgm|`,
		// patch-battle-dash.py). 여기서는 이미 도착해 있는 값을 읽어 **즉시** 튼다 — 왕복 0.
		//
		// ⚠️ 예전엔 이 자리에서 api 에 fetch 를 걸고 **답을 받아야** 음악을 시작했다. 그래서 배틀이
		//    시작돼도 왕복 시간만큼 음악이 통째로 밀렸다 (실측 0.57초, 회선이 나쁘면 몇 초 — 배틀 사이트는
		//    play.cherish-world.com, api 는 cherish-world.com 이라 DNS+TCP+TLS 부터 새로 맺는다).
		//    사용자 제보: 「포켓몬 다 나오고 액션버튼이 나올 때 되어서야 음악이 나온다」.
		//    **이 자리에 네트워크를 다시 들이지 말 것.**
		//
		// 서버가 안 줬으면(음악 CD 가 꺼져 있거나 배정이 없으면) 예전처럼 방 번호 해시로 기본곡을 고른다.
		const track = (this.battle as any)?.cherishBgmTrack;
		if (track && this.cherishApplyTrack(track.id, track.owner)) return;
		this.rollCherishFallbackBgm();
	}
	/**
	 * 서버가 정한 곡을 적용한다. 표에 없는 id 면 아무것도 안 하고 false (클라 표를 먼저 배포해야 한다).
	 * 배틀이 시작된 뒤에 `|cherishbgm|` 이 도착하는 판(훅 응답이 늦은 경우)도 이 자리에서 갈아탄다.
	 */
	cherishApplyTrack(id: string, owner: string | null) {
		let idx = -1;
		for (let i = 0; i < CHERISH_BGM.length; i++) {
			if (CHERISH_BGM[i].id === id) idx = i;
		}
		if (idx < 0) return false;
		// 소유자는 곡명 토스트가 읽는다 (patch-bgm-title.py). 익명 판이면 서버가 빈 값을 준다.
		(BattleSound as any).cherishTrackOwner = owner || null;
		this.setBgm(1 + idx);
		return true;
	}
	/** 방 번호 해시 — 기본곡끼리만. 같은 방이면 항상 같은 곡(새로고침·관전자 동일). */
	rollCherishFallbackBgm() {
		(BattleSound as any).cherishTrackOwner = null;
		let h = this.numericId >>> 0;
		h = Math.imul(h ^ h >>> 16, 2246822507) >>> 0;
		h = Math.imul(h ^ h >>> 13, 3266489909) >>> 0;
		h = (h ^ h >>> 16) >>> 0;
		const pool: number[] = [];
		for (let i = 0; i < CHERISH_BGM.length; i++) {
			if (CHERISH_BGM[i].dflt) pool.push(i);
		}
		if (!pool.length) {
			for (let i = 0; i < CHERISH_BGM.length; i++) pool.push(i);
		}
		this.setBgm(1 + pool[h % pool.length]);
	}
	setBgm(bgmNum: number) {
		if (this.bgmNum === bgmNum) return;
		this.bgmNum = bgmNum;

		const track = CHERISH_BGM[(bgmNum - 1) % CHERISH_BGM.length] || CHERISH_BGM[0];
		this.bgm = BattleSound.loadBgm(track.url, track.loopstart, track.loopend, this.bgm);
		// cherish-bgm-title: 음량 팝업이 읽어간다. 소리가 꺼져 있어도 채워 둔다.
		BattleSound.cherishTrackTitle = CHERISH_BGM_TITLES[track.url] || null;

		this.updateBgm();
	}
	updateBgm() {
		/**
		 * - not playing in non-battle RoomGames before `|start` (turn -1)
		 * - not playing at team preview in replays (paused)
		 * - playing at team preview in games (turn 0)
		 * - playing during the game (turn 1+)
		 * - not playing while paused
		 * - playing while waiting for players to choose moves (atQueueEnd && !ended)
		 * - not playing after the game has ended
		 */
		// cherish-bgm-keep: 배틀이 끝나도 방을 닫을 때까지 계속 튼다 (상류는 !ended 로 뚝 끊었다).
		// 끝난 방을 뒤늦게 열어도 튼다 — 「배틀 탭이 열려 있으면 음악」 규칙.
		const nowPlaying = (
			this.battle.turn >= 0 && !this.battle.paused
		);
		if (nowPlaying) {
			if (!this.bgm) this.rollBgm();
			this.bgm?.resume();
			this.cherishShowBgmTitle(); // cherish-bgm-title
		} else if (this.bgm) {
			this.bgm.pause();
		}
	}
	// cherish-bgm-title:begin-toast (patch-bgm-title.py)
	/**
	 * 이 방의 곡 제목을 화면 오른쪽 위에 잠깐 띄운다. 곡은 배틀 내내 안 바뀌므로 한 번이면 된다.
	 * 음량이 0 이어도 띄운다(사용자 확정) — 무슨 곡인지는 알려주고 소리만 안 나는 상태.
	 */
	cherishShowBgmTitle() {
		// 제목을 매번 다시 세운다. 배틀 방을 둘 열었다가 하나를 닫으면 destroy 가 제목을
		// null 로 지우는데, 남은 배틀이 다음 갱신에서 자기 제목을 되찾게 하는 자가 복구다.
		const track = CHERISH_BGM[(this.bgmNum - 1) % CHERISH_BGM.length];
		if (track) BattleSound.cherishTrackTitle = CHERISH_BGM_TITLES[track.url] || null;
		if (this.cherishTitleShown || !this.animating) return;
		// 끝났거나 멈춘 배틀에서는 띄우지 않는다. updateBgm 은 이미 걸러서 부르지만
		// animationOn(따라잡기 종료)에서도 부르기 때문에 여기서 한 번 더 막는다.
		if (this.battle.turn < 0 || this.battle.ended || this.battle.paused) return;
		const title = BattleSound.cherishTrackTitle;
		if (!title) return;
		this.cherishTitleShown = true;
		// 제목은 .text() 로 넣는다 — HTML 이 섞일 여지를 아예 없앤다.
		const $toast = $('<div class="cherish-bgmtitle"><span class="cherish-bgmnote">\u266a</span><span></span></div>');
		$toast.children().last().text(BattleSound.cherishTrackOwner ? title + ' (' + BattleSound.cherishTrackOwner + '의 CD)' : title);
		this.$battle.append($toast);
		setTimeout(() => $toast.addClass('cherish-bgmtitle-out'), 30000);
		setTimeout(() => $toast.remove(), 31200);
	}
	// cherish-bgm-title:end-toast
	resetBgm() {
		if (this.bgm) this.bgm.stop();
	}
	destroy() {
		this.log.destroy();
		if (this.$frame) {
			this.$frame.empty();
			// listeners set by BattleTooltips
			this.$frame.off();
		}
		if (this.bgm) {
			this.bgm.destroy();
			this.bgm = null;
			BattleSound.cherishTrackTitle = null; // cherish-bgm-title
		}
		this.battle = null!;
	}
	static getHPColor(pokemon: { hp: number, maxhp: number, hpcolor: HPColor | '' }) {
		if (pokemon.hpcolor) return pokemon.hpcolor;
		let ratio = pokemon.hp / pokemon.maxhp;
		if (ratio > 0.5) return 'g';
		if (ratio > 0.2) return 'y';
		return 'r';
	}
}

export interface ScenePos {
	/** - left, + right */
	x?: number;
	/** - down, + up */
	y?: number;
	/** - player, + opponent */
	z?: number;
	scale?: number;
	xscale?: number;
	yscale?: number;
	opacity?: number;
	time?: number;
	display?: string;
}
interface InitScenePos {
	x: number;
	y: number;
	z: number;
	scale?: number;
	xscale?: number;
	yscale?: number;
	opacity?: number;
	time?: number;
	display?: string;
}

export class Sprite {
	scene: BattleScene;
	$el: JQuery = null!;
	sp: SpriteData;
	x: number;
	y: number;
	z: number;
	constructor(spriteData: SpriteData | null, pos: InitScenePos, scene: BattleScene) {
		this.scene = scene;
		let sp = null;
		if (spriteData) {
			sp = spriteData;
			let rawHTML = sp.rawHTML ||
				`<img src="${sp.url!}" style="display:none;position:absolute"${sp.pixelated ? ' class="pixelated"' : ''} />`;
			this.$el = $(rawHTML);
		} else {
			sp = {
				w: 0,
				h: 0,
				url: '',
			};
		}
		this.sp = sp;

		this.x = pos.x;
		this.y = pos.y;
		this.z = pos.z;
		if (pos.opacity !== 0 && spriteData) this.$el.css(scene.pos(pos, sp));

		if (!spriteData) {
			this.delay = function () { return this; };
			this.anim = function () { return this; };
		}
	}

	destroy() {
		if (this.$el) this.$el.remove();
		this.$el = null!;
		this.scene = null!;
	}
	delay(time: number) {
		this.$el.delay(time);
		return this;
	}
	anim(end: ScenePos, transition?: string) {
		end = {
			x: this.x,
			y: this.y,
			z: this.z,
			scale: 1,
			opacity: 1,
			time: 500,
			...end,
		};
		if (end.time === 0) {
			this.$el.css(this.scene.pos(end, this.sp));
			return this;
		}
		this.$el.animate(this.scene.posT(end, this.sp, transition, this), end.time!);
		return this;
	}
}

export class PokemonSprite extends Sprite {
	// HTML strings are constructed from this table and stored back in it to cache them
	protected static statusTable: { [id: string]: [string, 'good' | 'bad' | 'neutral'] | null | string } = {
		formechange: null,
		typechange: null,
		typeadd: null,
		dynamax: ['다이맥스', 'good'],
		trapped: null, // linked volatiles are not implemented yet
		throatchop: ['소리봉인', 'bad'],
		confusion: ['혼란', 'bad'],
		healblock: ['회복봉인', 'bad'],
		yawn: ['졸음', 'bad'],
		flashfire: ['불꽃강화', 'good'],
		imprison: ['봉인 중', 'good'],
		autotomize: ['가벼워짐', 'neutral'],
		miracleeye: ['미라클아이', 'bad'],
		foresight: ['꿰뚫어보기', 'bad'],
		telekinesis: ['텔레키네시스', 'neutral'],
		transform: ['변신', 'neutral'],
		powertrick: ['파워트릭', 'neutral'],
		curse: ['저주', 'bad'],
		nightmare: ['악몽', 'bad'],
		attract: ['헤롱헤롱', 'bad'],
		torment: ['트집', 'bad'],
		taunt: ['도발', 'bad'],
		disable: ['사슬묶기', 'bad'],
		embargo: ['금제', 'bad'],
		ingrain: ['뿌리박기', 'good'],
		aquaring: ['아쿠아링', 'good'],
		stockpile1: ['비축', 'good'],
		stockpile2: ['비축&times;2', 'good'],
		stockpile3: ['비축&times;3', 'good'],
		perish0: ['멸망 0', 'bad'],
		perish1: ['멸망 1', 'bad'],
		perish2: ['멸망 2', 'bad'],
		perish3: ['멸망 3', 'bad'],
		airballoon: ['풍선', 'good'],
		leechseed: ['씨뿌리기', 'bad'],
		encore: ['앙코르', 'bad'],
		mustrecharge: ['재충전', 'bad'],
		bide: ['참기', 'good'],
		magnetrise: ['전자부유', 'good'],
		smackdown: ['떨어짐', 'bad'],
		focusenergy: ['급소율↑', 'good'],
		dragoncheer: ['급소율↑', 'good'],
		slowstart: ['슬로스타트', 'bad'],
		protosynthesisatk: ['고대활성:공격', 'good'],
		protosynthesisdef: ['고대활성:방어', 'good'],
		protosynthesisspa: ['고대활성:특공', 'good'],
		protosynthesisspd: ['고대활성:특방', 'good'],
		protosynthesisspe: ['고대활성:속도', 'good'],
		quarkdriveatk: ['쿼크차지:공격', 'good'],
		quarkdrivedef: ['쿼크차지:방어', 'good'],
		quarkdrivespa: ['쿼크차지:특공', 'good'],
		quarkdrivespd: ['쿼크차지:특방', 'good'],
		quarkdrivespe: ['쿼크차지:속도', 'good'],
		fallen1: ['총대장 1', 'good'],
		fallen2: ['총대장 2', 'good'],
		fallen3: ['총대장 3', 'good'],
		fallen4: ['총대장 4', 'good'],
		fallen5: ['총대장 5', 'good'],
		noretreat: ['배수의진', 'bad'],
		octolock: ['문어굳히기', 'bad'],
		tarshot: ['타르샷', 'bad'],
		saltcure: ['소금절이', 'bad'],
		syrupbomb: ['시럽범벅', 'bad'],
		doomdesire: null,
		futuresight: null,
		mimic: ['흉내내기', 'good'],
		watersport: ['물놀이', 'good'],
		mudsport: ['흙놀이', 'good'],
		substitute: null,
		// sub graphics are handled elsewhere, see Battle.Sprite.animSub()
		uproar: ['소란피기', 'neutral'],
		rage: ['분노', 'neutral'],
		roost: ['착지', 'neutral'],
		protect: ['방어', 'good'],
		quickguard: ['패스트가드', 'good'],
		wideguard: ['와이드가드', 'good'],
		craftyshield: ['트릭가드', 'good'],
		matblock: ['마룻바닥', 'good'],
		maxguard: ['다이월', 'good'],
		helpinghand: ['도우미', 'good'],
		magiccoat: ['매직코트', 'good'],
		destinybond: ['길동무', 'good'],
		snatch: ['가로채기', 'good'],
		grudge: ['원념', 'good'],
		charge: ['충전', 'good'],
		endure: ['버티기', 'good'],
		focuspunch: ['집중', 'neutral'],
		shelltrap: ['트랩 준비', 'neutral'],
		powder: ['분진', 'bad'],
		electrify: ['송전', 'bad'],
		glaiverush: ['대검돌격', 'bad'],
		ragepowder: ['분노가루', 'good'],
		followme: ['날따름', 'good'],
		instruct: ['지휘', 'neutral'],
		beakblast: ['부리캐논', 'neutral'],
		laserfocus: ['급소확정', 'good'],
		spotlight: ['스포트라이트', 'neutral'],
		itemremoved: null,
		// partial trapping
		bind: ['조이기', 'bad'],
		clamp: ['껍질끼우기', 'bad'],
		firespin: ['회오리불꽃', 'bad'],
		infestation: ['엉겨붙기', 'bad'],
		magmastorm: ['마그마스톰', 'bad'],
		sandtomb: ['모래지옥', 'bad'],
		snaptrap: ['집게덫', 'bad'],
		thundercage: ['썬더프리즌', 'bad'],
		whirlpool: ['바다회오리', 'bad'],
		wrap: ['김밥말이', 'bad'],
		// Gen 1-2
		mist: ['흰안개', 'good'],
		// Gen 1
		lightscreen: ['빛의장막', 'good'],
		reflect: ['리플렉터', 'good'],
	};
	forme = '';
	cryurl: string | undefined = undefined;

	subsp: SpriteData | null = null;
	$sub: JQuery | null = null;
	isSubActive = false;

	$statbar: JQuery | null = null;
	isFrontSprite: boolean;
	isMissedPokemon = false;
	/**
	 * If the pokemon is transformed, sprite.sp will be the transformed
	 * SpriteData and sprite.oldsp will hold the original form's SpriteData
	 */
	oldsp: SpriteData | null = null;

	statbarLeft = 0;
	statbarTop = 0;
	left = 0;
	top = 0;

	effects: { [id: string]: Sprite[] } = {};

	constructor(spriteData: SpriteData | null, pos: InitScenePos, scene: BattleScene, isFrontSprite: boolean) {
		super(spriteData, pos, scene);
		this.cryurl = this.sp.cryurl;
		this.isFrontSprite = isFrontSprite;
	}
	override destroy() {
		if (this.$el) this.$el.remove();
		this.$el = null!;
		if (this.$statbar) this.$statbar.remove();
		this.$statbar = null;
		if (this.$sub) this.$sub.remove();
		this.$sub = null;
		this.scene = null!;
	}

	override delay(time: number) {
		const probe = this.scene.cwHitProbe;
		if (probe && probe.sprite === this) probe.elapsed += time;
		this.$el.delay(time);
		if (this.$sub) this.$sub.delay(time);
		return this;
	}
	override anim(end: ScenePos, transition?: string) {
		end = {
			x: this.x,
			y: this.y,
			z: this.z,
			scale: 1,
			opacity: 1,
			time: 500,
			...end,
		};
		const probe = this.scene.cwHitProbe;
		if (probe && probe.sprite === this) {
			if (end.x !== this.x || end.y !== this.y || end.z !== this.z) {
				probe.reactions.push(probe.elapsed);
			}
			probe.elapsed += end.time!;
		}
		const [$el, sp] = (this.isSubActive ? [this.$sub!, this.subsp!] : [this.$el, this.sp]);
		$el.animate(this.scene.posT(end, sp, transition, this), end.time!);
		return this;
	}

	behindx(offset: number) {
		return this.x + (this.isFrontSprite ? 1 : -1) * offset;
	}
	behindy(offset: number) {
		return this.y + (this.isFrontSprite ? -1 : 1) * offset;
	}
	leftof(offset: number) {
		return this.x + (this.isFrontSprite ? 1 : -1) * offset;
	}
	behind(offset: number) {
		return this.z + (this.isFrontSprite ? 1 : -1) * offset;
	}

	removeTransform() {
		if (!this.scene.animating) return;
		if (!this.oldsp) return;
		let sp = this.oldsp;
		this.cryurl = sp.cryurl;
		this.sp = sp;
		this.oldsp = null;

		const $el = this.isSubActive ? this.$sub! : this.$el;
		$el.attr('src', sp.url!);
		$el.css(this.scene.pos({
			x: this.x,
			y: this.y,
			z: (this.isSubActive ? this.behind(30) : this.z),
			opacity: (this.$sub ? 0.3 : 1),
		}, sp));
	}
	animSub(instant?: boolean, noAnim?: boolean) {
		if (!this.scene.animating) return;
		if (this.$sub) return;
		const subsp = Dex.getSpriteData('substitute', this.isFrontSprite, {
			gen: this.scene.gen,
			mod: this.scene.mod,
		});
		this.subsp = subsp;
		this.$sub = $('<img src="' + subsp.url + '" style="display:block;opacity:0;position:absolute"' + (subsp.pixelated ? ' class="pixelated"' : '') + ' />');
		this.scene.$spritesFront[+this.isFrontSprite].append(this.$sub);
		this.isSubActive = true;
		if (instant) {
			if (!noAnim) this.animReset();
			return;
		}
		this.$el.animate(this.scene.pos({
			x: this.x,
			y: this.y,
			z: this.behind(30),
			opacity: 0.3,
		}, this.sp), 500);
		this.$sub.css(this.scene.pos({
			x: this.x,
			y: this.y + 50,
			z: this.z,
			opacity: 0,
		}, subsp));
		this.$sub.animate(this.scene.pos({
			x: this.x,
			y: this.y,
			z: this.z,
		}, subsp), 500);
		this.scene.waitFor(this.$sub);
	}
	animSubFade(instant?: boolean) {
		if (!this.$sub || !this.scene.animating) return;
		this.isSubActive = false;
		if (instant) {
			this.$sub.remove();
			this.$sub = null;
			this.animReset();
			return;
		}
		if (this.scene.timeOffset) {
			this.$el.delay(this.scene.timeOffset);
			this.$sub.delay(this.scene.timeOffset);
		}
		this.$sub.animate(this.scene.pos({
			x: this.x,
			y: this.y - 50,
			z: this.z,
			opacity: 0,
		}, this.subsp!), 500);

		this.$sub = null;
		this.anim({ time: 500 });
		if (this.scene.animating) this.scene.waitFor(this.$el);
	}
	beforeMove() {
		if (!this.scene.animating) return false;
		if (!this.isSubActive) return false;
		this.isSubActive = false;
		this.anim({ time: 300 });
		this.$sub!.animate(this.scene.pos({
			x: this.leftof(-50),
			y: this.y,
			z: this.z,
			opacity: 0.5,
		}, this.subsp!), 300);
		for (const side of this.scene.battle.sides) {
			for (const active of side.active) {
				if (active && active.sprite !== this) {
					active.sprite.delay(300);
				}
			}
		}
		this.scene.wait(300);
		this.scene.waitFor(this.$el);

		return true;
	}
	afterMove() {
		if (!this.scene.animating) return false;
		if (!this.$sub || this.isSubActive) return false;
		this.isSubActive = true;
		this.$sub.delay(300);
		this.$el.add(this.$sub).promise().done(() => {
			if (!this.$sub || !this.$el) return;
			this.$el.animate(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.behind(30),
				opacity: 0.3,
			}, this.sp), 300);
			this.anim({ time: 300 });
		});
		return false;
	}
	removeSub() {
		if (!this.$sub) return;
		this.isSubActive = false;
		if (!this.scene.animating) {
			this.$sub.remove();
		} else {
			const $sub = this.$sub;
			$sub.animate({
				opacity: 0,
			}, () => {
				$sub.remove();
			});
		}
		this.$sub = null;
	}
	reset(pokemon: Pokemon) {
		this.clearEffects();

		if (pokemon.volatiles.formechange || pokemon.volatiles.dynamax || pokemon.volatiles.terastallize) {
			if (!this.oldsp) this.oldsp = this.sp;
			this.sp = Dex.getSpriteData(pokemon, this.isFrontSprite, {
				gen: this.scene.gen,
				mod: this.scene.mod,
			});
		} else if (this.oldsp) {
			this.sp = this.oldsp;
			this.oldsp = null;
		}

		// I can rant for ages about how jQuery sucks, necessitating this function
		// The short version is: after calling elem.finish() on an animating
		// element, there appear to be a grand total of zero ways to hide it
		// afterwards. I've tried `elem.css('display', 'none')`, `elem.hide()`,
		// `elem.hide(1)`, `elem.hide(1000)`, `elem.css('opacity', 0)`,
		// `elem.animate({opacity: 0}, 1000)`.
		// They literally all do nothing, and the element retains
		// a style attribute containing `display: inline-block` and `opacity: 1`
		// Only forcibly removing the element from the DOM actually makes it
		// disappear, so that's what we do.
		if (this.$el) {
			this.$el.stop(true, false);
			this.$el.remove();
			const $newEl = $(`<img src="${this.sp.url!}" style="display:none;position:absolute"${this.sp.pixelated ? ' class="pixelated"' : ''} />`);
			this.$el = $newEl;
		}

		if (!pokemon.isActive()) {
			if (this.$statbar) {
				this.$statbar.remove();
				this.$statbar = null;
			}
			return;
		}

		if (this.$el) this.scene.$sprites[+this.isFrontSprite].append(this.$el);
		this.recalculatePos(pokemon.slot);
		this.resetStatbar(pokemon);
		this.$el.css(this.scene.pos({
			display: 'block',
			x: this.x,
			y: this.y,
			z: this.z,
		}, this.sp));

		for (const id in pokemon.volatiles) this.addEffect(id as ID, true);
		for (const id in pokemon.turnstatuses) this.addEffect(id as ID, true);
		for (const id in pokemon.movestatuses) this.addEffect(id as ID, true);
	}
	animReset() {
		if (!this.scene.animating) return;
		if (this.$sub) {
			this.isSubActive = true;
			this.$el.stop(true, false);
			this.$sub.stop(true, false);
			this.$el.css(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.behind(30),
				opacity: 0.3,
			}, this.sp));
			this.$sub.css(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.z,
			}, this.subsp!));
		} else {
			this.$el.stop(true, false);
			this.$el.css(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.z,
			}, this.sp));
		}
	}
	recalculatePos(slot: number) {
		let moreActive = this.scene.activeCount - 1;
		let statbarOffset = 0;
		const isFFA = this.scene.battle.gameType === 'freeforall';
		if (isFFA) {
			// create a gap between Pokemon on the same "side" as a distinction between FFA and Multi battles
			moreActive++;
			if (slot) slot++;
		}
		if (this.scene.gen <= 4 && moreActive) {
			this.x = (slot - 0.52) * (this.isFrontSprite ? 1 : -1) * -55;
			this.y = (this.isFrontSprite ? 1 : -1) + 1;
			if (this.isFrontSprite) statbarOffset = 30 * slot;
			if (!this.isFrontSprite) statbarOffset = -28 * slot;
		} else {
			switch (moreActive) {
			case 0:
				this.x = 0;
				break;
			case 1:
				if (this.sp.pixelated) {
					this.x = (slot * -100 + 18) * (this.isFrontSprite ? 1 : -1);
				} else {
					this.x = (slot * -75 + 18) * (this.isFrontSprite ? 1 : -1);
				}
				break;
			case 2:
				this.x = (slot * -70 + 20) * (this.isFrontSprite ? 1 : -1);
				break;
			}
			this.y = this.isFrontSprite ? slot * 7 : slot * -10;
			if (this.isFrontSprite) statbarOffset = 17 * slot;
			if (this.isFrontSprite && !moreActive && this.sp.pixelated) statbarOffset = 15;
			if (!this.isFrontSprite) statbarOffset = -7 * slot;
			if (this.isFrontSprite && moreActive === 2) statbarOffset = 14 * slot - 10;
		}
		if (this.scene.gen <= 2) {
			statbarOffset += this.isFrontSprite ? 20 : 1;
		} else if (this.scene.gen <= 3) {
			statbarOffset += this.isFrontSprite ? 30 : 5;
		} else if (this.scene.gen !== 5) {
			statbarOffset += this.isFrontSprite ? 30 : 20;
		}

		let pos = this.scene.pos({
			x: this.x,
			y: this.y,
			z: this.z,
		}, {
			w: 0,
			h: 96,
		});
		pos.top += 40;

		this.left = pos.left;
		this.top = pos.top;
		this.statbarLeft = pos.left - 80;
		this.statbarTop = pos.top - 73 - statbarOffset;
		if (this.statbarTop < -4) this.statbarTop = -4;

		if (moreActive) {
			// make sure element is in the right z-order
			if (!!slot === this.isFrontSprite) {
				this.$el.prependTo(this.$el.parent());
			} else {
				this.$el.appendTo(this.$el.parent());
			}
		}
	}
	animSummon(pokemon: Pokemon, slot: number, instant?: boolean) {
		if (!this.scene.animating) return;
		this.scene.$sprites[+this.isFrontSprite].append(this.$el);
		this.recalculatePos(slot);

		// 'z-index': (this.isFrontSprite ? 4-slot : 1+slot),
		if (instant) {
			this.$el.css('display', 'block');
			this.animReset();
			this.resetStatbar(pokemon);
			if (pokemon.hasVolatile('substitute' as ID)) this.animSub(true);
			return;
		}
		if (this.cryurl) {
			BattleSound.playEffect(this.cryurl, this.scene.battle.id); // cherish-extraction-audio
		}
		this.$el.css(this.scene.pos({
			display: 'block',
			x: this.x,
			y: this.y - 10,
			z: this.z,
			scale: 0,
			opacity: 0,
		}, this.sp));
		this.scene.showEffect('pokeball', {
			opacity: 0,
			x: this.x,
			y: this.y + 30,
			z: this.behind(50),
			scale: 0.7,
		}, {
			opacity: 1,
			x: this.x,
			y: this.y - 10,
			z: this.z,
			time: 300 / this.scene.acceleration,
		}, 'ballistic2', 'fade');
		if (this.scene.gen <= 4) {
			this.delay(this.scene.timeOffset + 300 / this.scene.acceleration).anim({
				x: this.x,
				y: this.y,
				z: this.z,
				time: 400 / this.scene.acceleration,
			});
		} else {
			this.delay(this.scene.timeOffset + 300 / this.scene.acceleration).anim({
				x: this.x,
				y: this.y + 30,
				z: this.z,
				time: 400 / this.scene.acceleration,
			}).anim({
				x: this.x,
				y: this.y,
				z: this.z,
				time: 300 / this.scene.acceleration,
			}, 'accel');
		}
		if (this.sp.shiny && this.scene.acceleration < 2) BattleOtherAnims.shiny.anim(this.scene, [this]);
		this.scene.waitFor(this.$el);

		if (pokemon.hasVolatile('substitute' as ID)) {
			this.animSub(true, true);
			this.$sub!.css(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.z,
			}, this.subsp!));
			this.$el.animate(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.behind(30),
				opacity: 0.3,
			}, this.sp), 300);
		}

		this.resetStatbar(pokemon, true);
		this.scene.updateSidebar(pokemon.side);
		this.$statbar!.css({
			display: 'block',
			left: this.statbarLeft,
			top: this.statbarTop + 20,
			opacity: 0,
		});
		this.$statbar!.delay(300 / this.scene.acceleration).animate({
			top: this.statbarTop,
			opacity: 1,
		}, 400 / this.scene.acceleration);

		this.dogarsCheck(pokemon);
	}
	animDragIn(pokemon: Pokemon, slot: number) {
		if (!this.scene.animating) return;
		this.scene.$sprites[+this.isFrontSprite].append(this.$el);
		this.recalculatePos(slot);

		// 'z-index': (this.isFrontSprite ? 4-slot : 1+slot),
		this.$el.css(this.scene.pos({
			display: 'block',
			x: this.leftof(-100),
			y: this.y,
			z: this.z,
			opacity: 0,
		}, this.sp));
		this.delay(300).anim({
			x: this.x,
			y: this.y,
			z: this.z,
			time: 400,
		}, 'decel');
		if (!!this.scene.animating && this.sp.shiny) BattleOtherAnims.shiny.anim(this.scene, [this]);
		this.scene.waitFor(this.$el);
		this.scene.timeOffset = 700;

		this.resetStatbar(pokemon, true);
		this.scene.updateSidebar(pokemon.side);
		this.$statbar!.css({
			display: 'block',
			left: this.statbarLeft + (this.isFrontSprite ? -100 : 100),
			top: this.statbarTop,
			opacity: 0,
		});
		this.$statbar!.delay(300).animate({
			left: this.statbarLeft,
			opacity: 1,
		}, 400);

		this.dogarsCheck(pokemon);
	}
	animDragOut(pokemon: Pokemon) {
		if (!this.scene.animating) return this.animUnsummon(pokemon, true);
		if (this.$sub) {
			this.isSubActive = false;
			const $sub = this.$sub;
			$sub.animate(this.scene.pos({
				x: this.leftof(100),
				y: this.y,
				z: this.z,
				opacity: 0,
				time: 400,
			}, this.subsp!), () => {
				$sub.remove();
			});
			this.$sub = null;
		}
		this.anim({
			x: this.leftof(100),
			y: this.y,
			z: this.z,
			opacity: 0,
			time: 400,
		}, 'accel');

		this.updateStatbar(pokemon, true);
		let $statbar = this.$statbar;
		if ($statbar) {
			this.$statbar = null;
			$statbar.animate({
				left: this.statbarLeft - (this.isFrontSprite ? -100 : 100),
				opacity: 0,
			}, 300 / this.scene.acceleration, () => {
				$statbar.remove();
			});
		}
	}
	animUnsummon(pokemon: Pokemon, instant?: boolean) {
		this.removeSub();
		if (!this.scene.animating || instant) {
			this.$el.hide();
			if (this.$statbar) {
				this.$statbar.remove();
				this.$statbar = null;
			}
			return;
		}
		if (this.scene.gen <= 4) {
			this.anim({
				x: this.x,
				y: this.y - 25,
				z: this.z,
				scale: 0,
				opacity: 0,
				time: 400 / this.scene.acceleration,
			});
		} else {
			this.anim({
				x: this.x,
				y: this.y - 40,
				z: this.z,
				scale: 0,
				opacity: 0,
				time: 400 / this.scene.acceleration,
			});
		}
		this.scene.showEffect('pokeball', {
			opacity: 1,
			x: this.x,
			y: this.y - 40,
			z: this.z,
			scale: 0.7,
			time: 300 / this.scene.acceleration,
		}, {
			opacity: 0,
			x: this.x,
			y: this.y,
			z: this.behind(50),
			time: 700 / this.scene.acceleration,
		}, 'ballistic2');
		if (this.scene.acceleration < 3) this.scene.wait(600 / this.scene.acceleration);

		this.updateStatbar(pokemon, true);
		let $statbar = this.$statbar;
		if ($statbar) {
			this.$statbar = null;
			$statbar.animate({
				left: this.statbarLeft + (this.isFrontSprite ? 50 : -50),
				opacity: 0,
			}, 300 / this.scene.acceleration, () => {
				$statbar.remove();
			});
		}
	}
	animFaint(pokemon: Pokemon) {
		this.removeSub();
		if (!this.scene.animating) {
			this.$el.remove();
			if (this.$statbar) {
				this.$statbar.remove();
				this.$statbar = null;
			}
			return;
		}
		this.updateStatbar(pokemon, false, true);
		this.scene.updateSidebar(pokemon.side);
		// cherish-hit-fx: 쓰러지는 아이는 색을 잃으며 내려간다 (흔들림은 앞의 -damage 가 이미 냈다)
		if (this.scene.cwFxOn() && this.$el) this.$el.addClass('cw-faint');
		if (this.cryurl) {
			BattleSound.playEffect(this.cryurl, this.scene.battle.id); // cherish-extraction-audio
		}
		this.anim({
			y: this.y - 80,
			opacity: 0,
		}, 'accel');
		this.scene.waitFor(this.$el);
		this.$el.promise().done(() => {
			this.$el.remove();
		});

		let $statbar = this.$statbar;
		if ($statbar) {
			this.$statbar = null;
			$statbar.animate({
				opacity: 0,
			}, 300, () => {
				$statbar.remove();
			});
		}
	}
	/**
	 * @param pokemon
	 * @param useSpeciesAnim false = Transform the move or Imposter the ability
	 * @param isPermanent false = reverts on switch-out
	 */
	animTransform(pokemon: Pokemon, useSpeciesAnim?: boolean, isPermanent?: boolean) {
		if (!this.scene.animating && !isPermanent) return;
		let sp = Dex.getSpriteData(pokemon, this.isFrontSprite, {
			gen: this.scene.gen,
			mod: this.scene.mod,
		});
		let oldsp = this.sp;
		if (isPermanent) {
			if (pokemon.volatiles.dynamax) {
				// if a permanent forme change happens while dynamaxed, we need an undynamaxed sprite to go back to
				this.oldsp = Dex.getSpriteData(pokemon, this.isFrontSprite, {
					gen: this.scene.gen,
					mod: this.scene.mod,
					dynamax: false,
				});
			} else {
				this.oldsp = null;
			}
		} else if (!this.oldsp) {
			this.oldsp = oldsp;
		}
		this.sp = sp;
		this.cryurl = sp.cryurl;

		if (!this.scene.animating) return;
		let speciesid = toID(pokemon.getSpeciesForme());
		let doCry = false;
		let skipAnim = !pokemon.isActive();
		const scene = this.scene;
		if (useSpeciesAnim && !skipAnim) {
			if (speciesid === 'kyogreprimal') {
				BattleOtherAnims.primalalpha.anim(scene, [this]);
				doCry = true;
			} else if (speciesid === 'groudonprimal') {
				BattleOtherAnims.primalomega.anim(scene, [this]);
				doCry = true;
			} else if (speciesid === 'necrozmaultra') {
				BattleOtherAnims.ultraburst.anim(scene, [this]);
				doCry = true;
			} else if (speciesid === 'zygardecomplete') {
				BattleOtherAnims.powerconstruct.anim(scene, [this]);
			} else if (speciesid === 'wishiwashischool' || speciesid === 'greninjaash') {
				BattleOtherAnims.schoolingin.anim(scene, [this]);
			} else if (speciesid === 'wishiwashi') {
				BattleOtherAnims.schoolingout.anim(scene, [this]);
			} else if (speciesid === 'mimikyubusted' || speciesid === 'mimikyubustedtotem' ||
				speciesid === 'aegislash' || speciesid === 'aegislashblade') {
				// standard animation
			} else if (speciesid === 'palafinhero') {
				skipAnim = true;
			} else {
				BattleOtherAnims.megaevo.anim(scene, [this]);
				doCry = true;
			}
		}
		// Constructing here gives us 300ms extra time to preload the new sprite
		let $newEl = $('<img src="' + sp.url + '" style="display:block;opacity:0;position:absolute"' + (sp.pixelated ? ' class="pixelated"' : '') + ' />');
		$newEl.css(this.scene.pos({
			x: this.x,
			y: this.y,
			z: this.z,
			yscale: 0,
			xscale: 0,
			opacity: 0,
		}, sp));
		if (skipAnim) {
			this.$el.replaceWith($newEl);
			this.$el = $newEl;
			this.animReset();
		} else {
			this.$el.animate(this.scene.pos({
				x: this.x,
				y: this.y,
				z: this.z,
				yscale: 0,
				xscale: 0,
				opacity: 0.3,
			}, oldsp), 300, () => {
				if (this.cryurl && doCry) {
					BattleSound.playEffect(this.cryurl, this.scene.battle.id); // cherish-extraction-audio
				}
				this.$el.replaceWith($newEl);
				this.$el = $newEl;
				this.$el.animate(scene.pos({
					x: this.x,
					y: this.y,
					z: this.z,
					opacity: 1,
				}, sp), 300);
			});
			this.scene.wait(500);
		}

		this.scene.updateSidebar(pokemon.side);
		if (isPermanent) {
			this.resetStatbar(pokemon);
		} else {
			this.updateStatbar(pokemon);
		}
	}

	pokeEffect(id: ID) {
		if (id === 'protect' || id === 'magiccoat') {
			this.effects[id][0].anim({
				scale: 1.2,
				opacity: 1,
				time: 100,
			}).anim({
				opacity: 0.4,
				time: 300,
			});
		}
	}
	addEffect(id: ID, instant?: boolean) {
		if (id in this.effects) {
			this.pokeEffect(id);
			return;
		}
		const spriten = +this.isFrontSprite;
		if (id === 'substitute' || id === 'shedtail') {
			this.animSub(instant);
		} else if (id === 'leechseed') {
			const pos1 = {
				display: 'block',
				x: this.x - 30,
				y: this.y - 40,
				z: this.z,
				scale: 0.2,
				opacity: 0.6,
			};
			const pos2 = {
				display: 'block',
				x: this.x + 40,
				y: this.y - 35,
				z: this.z,
				scale: 0.2,
				opacity: 0.6,
			};
			const pos3 = {
				display: 'block',
				x: this.x + 20,
				y: this.y - 25,
				z: this.z,
				scale: 0.2,
				opacity: 0.6,
			};

			const leechseed1 = new Sprite(BattleEffects.energyball, pos1, this.scene);
			const leechseed2 = new Sprite(BattleEffects.energyball, pos2, this.scene);
			const leechseed3 = new Sprite(BattleEffects.energyball, pos3, this.scene);
			this.scene.$spritesFront[spriten].append(leechseed1.$el);
			this.scene.$spritesFront[spriten].append(leechseed2.$el);
			this.scene.$spritesFront[spriten].append(leechseed3.$el);
			this.effects['leechseed'] = [leechseed1, leechseed2, leechseed3];
		} else if (id === 'protect' || id === 'magiccoat') {
			const protect = new Sprite(BattleEffects.protect, {
				display: 'block',
				x: this.x,
				y: this.y,
				z: this.behind(-15),
				xscale: 1,
				yscale: 0,
				opacity: 0.1,
			}, this.scene);
			this.scene.$spritesFront[spriten].append(protect.$el);
			this.effects[id] = [protect];
			protect.anim({
				opacity: 0.9,
				time: instant ? 0 : 400,
			}).anim({
				opacity: 0.4,
				time: instant ? 0 : 300,
			});
		}
	}

	removeEffect(id: ID, instant?: boolean) {
		if (id === 'formechange') this.removeTransform();
		if (id === 'substitute') this.animSubFade(instant);
		if (this.effects[id]) {
			for (const sprite of this.effects[id]) sprite.destroy();
			delete this.effects[id];
		}
	}
	clearEffects() {
		for (const id in this.effects) this.removeEffect(id as ID, true);
		this.animSubFade(true);
		this.removeTransform();
	}

	dogarsCheck(pokemon: Pokemon) {
		// cherish: BGM 은 방마다 고정. 또가스 특수곡 교체 없음.
		void pokemon;
	}

	// Statbar
	/////////////////////////////////////////////////////////////////////

	getClassForPosition(slot: number) {
		// DOUBLES: Slot0 -> left / Slot1 -> Right
		// TRIPLES: slot0 -> left / Slot1 -> Center / Slot2 -> Right
		const position = [
			' leftstatbar',
			this.scene.activeCount === 3 ? ' centerstatbar' : ' rightstatbar',
			' rightstatbar',
		];
		return position[slot];
	}

	getStatbarHTML(pokemon: Pokemon) {
		let buf = '<div class="statbar' + (this.isFrontSprite ? ' lstatbar' : ' rstatbar') + this.getClassForPosition(pokemon.slot) + (this.scene.activeCount > 1 ? ' cherish-dbl' : '') + '" style="display: none">';
		const ignoreNick = this.isFrontSprite && (this.scene.battle.ignoreOpponent || this.scene.battle.ignoreNicks);
		buf += `<strong>${BattleLog.escapeHTML(ignoreNick ? pokemon.speciesForme : pokemon.name)}`;
		const gender = pokemon.gender;
		if (gender === 'M' || gender === 'F') {
			buf += ` <img src="${Dex.fxPrefix}gender-${gender.toLowerCase()}.png" alt="${gender}" width="7" height="10" class="pixelated" />`;
		}
		buf += ``; // cherish fork: 레벨은 그리지 않는다 (유저 전원 50 · 시험관·탑 봇의 레벨 핸디캡은 숨긴다, 2026-09-07)
		buf += ' <span class="cherish-types"></span>'; // cherish-badge: 타입 칩 (updateStatbar 가 채움)

		let symbol = '';
		if (pokemon.speciesForme.includes('-Mega')) symbol = 'mega';
		else if (pokemon.speciesForme === 'Kyogre-Primal') symbol = 'alpha';
		else if (pokemon.speciesForme === 'Groudon-Primal') symbol = 'omega';
		if (symbol) {
			buf += ` <img src="${Dex.resourcePrefix}sprites/misc/${symbol}.png" alt="${symbol}" style="vertical-align:text-bottom;" />`;
		}
		if (pokemon.terastallized) {
			buf += ` <img src="${Dex.resourcePrefix}sprites/types/Tera${pokemon.terastallized}.png" alt="Tera-${pokemon.terastallized}" style="vertical-align:text-bottom;" height="16" width="16" />`;
		}

		buf += `</strong><div class="hpbar"><div class="hptext"></div><div class="hptextborder"></div><div class="prevhp"><div class="hp"></div></div><div class="status"></div>`;
		buf += `</div>`;
		return buf;
	}

	resetStatbar(pokemon: Pokemon, startHidden?: boolean) {
		if (this.$statbar) {
			this.$statbar.remove();
			this.$statbar = null as any; // workaround for TS thinking $statbar is still null after `updateStatbar`
		}
		this.updateStatbar(pokemon, true);
		if (!startHidden && this.$statbar) {
			this.$statbar.css({
				display: 'block',
				left: this.statbarLeft,
				top: this.statbarTop,
				opacity: 1,
			});
		}
	}

	updateStatbarIfExists(pokemon: Pokemon, updatePrevhp?: boolean, updateHp?: boolean) {
		if (this.$statbar) {
			this.updateStatbar(pokemon, updatePrevhp, updateHp);
		}
	}

	// cherish-speed-memo-chip:begin (patch-speed-memo-ui.py)
	/**
	 * 속도 기억 칩 — 상대 아이의 상태줄 맨 앞. 내 자리의 아이와 이 상대가 전에 맞붙은 기억이 있을 때만.
	 * 파랑 = 상대가 느림(나에게 유리) · 빨강 = 상대가 빠름. 숫자는 없다.
	 */
	cherishSpeedChip(pokemon: Pokemon): string {
		try {
			const battle = this.scene.battle as any;
			const memo = battle.cherishSpeedMemo;
			const me = battle.mySide;
			if (!Array.isArray(memo) || !memo.length || !me) return '';
			if (pokemon.side === me || pokemon.side === me.ally) return '';
			const mine: string[] = (me.active || []).filter((p: Pokemon | null) => p && !p.fainted).map((p: Pokemon) => p.name);
			const hit = memo.find((l: any) => l && l.foeSide === pokemon.side.sideid && l.foe === pokemon.name && mine.includes(l.mine));
			if (!hit) return '';
			PokemonSprite.cherishSpeedTapOnce();
			const label = hit.faster ? '나보다 느림' : '나보다 빠름';
			const bg = hit.faster ? '#3a72c4' : '#e0413a';
			return `<span class="cherish-spd" data-label="${label}" title="지난 맞대결 기준이에요" style="display:inline-block;padding:0 5px;margin:0 2px 0 0;border-radius:7px;font-size:9px;font-weight:700;line-height:13px;color:#fff;text-shadow:none;cursor:pointer;background:${bg}">${label}</span> `;
		} catch {
			return '';
		}
	}
	/** 칩을 누르면 2초간 「지난 맞대결 기준」 — 휴대폰엔 호버가 없어서. 위임 핸들러라 한 번만 건다. */
	static cherishSpeedTapBound = false;
	static cherishSpeedTapOnce() {
		if (PokemonSprite.cherishSpeedTapBound) return;
		PokemonSprite.cherishSpeedTapBound = true;
		$(document).on('click', '.cherish-spd', function (this: HTMLElement) {
			const el = $(this);
			el.text('지난 맞대결 기준');
			setTimeout(() => el.text(el.attr('data-label') || ''), 2000);
		});
	}
	// cherish-speed-memo-chip:end
	updateStatbar(pokemon: Pokemon, updatePrevhp?: boolean, updateHp?: boolean) {
		if (!this.scene.animating) return;
		if (!pokemon.isActive()) {
			if (this.$statbar) this.$statbar.hide();
			return;
		}
		if (!this.$statbar) {
			this.$statbar = $(this.getStatbarHTML(pokemon));
			this.scene.$stat.append(this.$statbar);
			updatePrevhp = true;
		}
		let hpcolor;
		if (updatePrevhp || updateHp) {
			hpcolor = BattleScene.getHPColor(pokemon);
			let w = pokemon.hpWidth(150);
			let $hp = this.$statbar.find('.hp');
			$hp.css({
				width: w,
				'border-right-width': (w ? 1 : 0),
			});
			if (hpcolor === 'g') $hp.removeClass('hp-yellow hp-red');
			else if (hpcolor === 'y') $hp.removeClass('hp-red').addClass('hp-yellow');
			else $hp.addClass('hp-yellow hp-red');
			this.updateHPText(pokemon);
		}
		if (updatePrevhp) {
			let $prevhp = this.$statbar.find('.prevhp');
			$prevhp.css('width', pokemon.hpWidth(150) + 1);
			if (hpcolor === 'g') $prevhp.removeClass('prevhp-yellow prevhp-red');
			else if (hpcolor === 'y') $prevhp.removeClass('prevhp-red').addClass('prevhp-yellow');
			else $prevhp.addClass('prevhp-yellow prevhp-red');
		}
		let status = '';
		if (pokemon.status === 'brn') {
			status += '<span class="brn">화상</span> ';
		} else if (pokemon.status === 'psn') {
			status += '<span class="psn">독</span> ';
		} else if (pokemon.status === 'tox') {
			status += '<span class="psn">맹독</span> ';
		} else if (pokemon.status === 'slp') {
			status += '<span class="slp">잠듦</span> ';
		} else if (pokemon.status === 'par') {
			status += '<span class="par">마비</span> ';
		} else if (pokemon.status === 'frz') {
			status += '<span class="frz">얼음</span> ';
		}
		// cherish-badge: 타입 칩은 이름 옆에 항상, 상태줄의 원본 타입 칩은 배지 OFF 일 때만 (중복 방지)
		const cherishNoBadges = this.scene.battle.cherishNoBadges;
		this.$statbar.find('.cherish-types').html(
			cherishNoBadges ? '' : pokemon.getTypeList().map(type => Dex.getTypeIcon(type)).join(' ')
		);
		if (cherishNoBadges && pokemon.terastallized) {
			status += Dex.getTypeIcon(pokemon.terastallized) + ' ';
		} else if (cherishNoBadges && pokemon.volatiles.typechange?.[1]) {
			const types = pokemon.volatiles.typechange[1].split('/');
			for (const type of types) {
				status += Dex.getTypeIcon(type) + ' ';
			}
		}
		if (cherishNoBadges && pokemon.volatiles.typeadd) {
			const type = pokemon.volatiles.typeadd[1];
			status += '+' + Dex.getTypeIcon(type) + ' ';
		}
		for (const stat in pokemon.boosts) {
			if (pokemon.boosts[stat]) {
				status += '<span class="' + pokemon.getBoostType(stat as Dex.BoostStatName) + '">' + pokemon.getBoost(stat as Dex.BoostStatName) + '</span> ';
			}
		}

		for (let i in pokemon.volatiles) {
			status += PokemonSprite.getEffectTag(i);
		}
		for (let i in pokemon.turnstatuses) {
			if (i === 'roost' && !pokemon.getTypeList().includes('Flying')) continue;
			status += PokemonSprite.getEffectTag(i);
		}
		for (let i in pokemon.movestatuses) {
			status += PokemonSprite.getEffectTag(i);
		}
		let statusbar = this.$statbar.find('.status');
		statusbar.html(this.cherishSpeedChip(pokemon) + status); // cherish-speed-memo (patch-speed-memo-ui.py)
		// 내 자리의 아이가 바뀌면 상대 칩도 바뀐다 — 상대 상태바를 다시 그린다 (상대 → 나 방향은 안 부른다)
		const cherishBattle = this.scene.battle as any;
		if (cherishBattle.cherishSpeedMemo && pokemon.side === cherishBattle.mySide) {
			for (const side of cherishBattle.sides) {
				if (side === cherishBattle.mySide || side === cherishBattle.mySide.ally) continue;
				for (const foe of side.active) if (foe) foe.sprite.updateStatbarIfExists(foe);
			}
		}
	}

	private static getEffectTag(id: string) {
		let effect = PokemonSprite.statusTable[id];
		if (typeof effect === 'string') return effect;
		if (effect === null) return (PokemonSprite.statusTable[id] = '');
		if (effect === undefined) {
			let label = `[[${id}]]`;
			if (Dex.species.get(id).exists) {
				label = Dex.species.get(id).name;
			} else if (Dex.items.get(id).exists) {
				label = Dex.items.get(id).name;
			} else if (Dex.moves.get(id).exists) {
				label = Dex.moves.get(id).name;
			} else if (Dex.abilities.get(id).exists) {
				label = Dex.abilities.get(id).name;
			} else if ((BattleNatures as any)[id.substr(0, 1).toUpperCase() + id.substr(1).toLowerCase()]) {
				label = id.substr(0, 1).toUpperCase() + id.substr(1).toLowerCase();
			}
			effect = [label, 'neutral'];
		}
		// cherish fork: 뱃지 라벨 한글화
		const koEff = CherishKo.effectTag(id, effect[0]);
		if (koEff) effect = [koEff, effect[1]];
		return (PokemonSprite.statusTable[id] = `<span class="${effect[1]}">${effect[0].replace(/ /g, '&nbsp;')}</span> `);
	}

	updateHPText(pokemon: Pokemon) {
		if (!this.$statbar) return;
		let $hptext = this.$statbar.find('.hptext');
		let $hptextborder = this.$statbar.find('.hptextborder');
		if (pokemon.maxhp === 48 || this.scene.battle.hardcoreMode && pokemon.maxhp === 100) {
			$hptext.hide();
			$hptextborder.hide();
		} else if (this.scene.battle.hardcoreMode || this.scene.battle.reportExactHP) {
			$hptext.html(`${pokemon.hp}/`);
			$hptext.show();
			$hptextborder.show();
		} else {
			$hptext.html(`${pokemon.hpWidth(100)}%`);
			$hptext.show();
			$hptextborder.show();
		}
	}
}

// par: -webkit-filter:  sepia(100%) hue-rotate(373deg) saturate(592%);
//      -webkit-filter:  sepia(100%) hue-rotate(22deg) saturate(820%) brightness(29%);
// psn: -webkit-filter:  sepia(100%) hue-rotate(618deg) saturate(285%);
// brn: -webkit-filter:  sepia(100%) hue-rotate(311deg) saturate(469%);
// slp: -webkit-filter:  grayscale(100%);
// frz: -webkit-filter:  sepia(100%) hue-rotate(154deg) saturate(759%) brightness(23%);

Object.assign($.easing, {
	ballisticUp(x: number, t: number, b: number, c: number, d: number) {
		return -3 * x * x + 4 * x;
	},
	ballisticDown(x: number, t: number, b: number, c: number, d: number) {
		x = 1 - x;
		return 1 - (-3 * x * x + 4 * x);
	},
	quadUp(x: number, t: number, b: number, c: number, d: number) {
		x = 1 - x;
		return 1 - (x * x);
	},
	quadDown(x: number, t: number, b: number, c: number, d: number) {
		return x * x;
	},
});

interface AnimData {
	anim(scene: BattleScene, args: PokemonSprite[]): void;
	prepareAnim?(scene: BattleScene, args: PokemonSprite[]): void;
	residualAnim?(scene: BattleScene, args: PokemonSprite[]): void;
}
export type AnimTable = { [k: string]: AnimData };

const BattleEffects: { [k: string]: SpriteData } = {
	wisp: {
		url: 'wisp.png?v=20260909a',
		w: 100, h: 100,
	},
	purplewisp: {
		url: 'purplewisp.png?v=20260909a',
		w: 100, h: 100,
	},
	waterwisp: {
		url: 'waterwisp.png?v=20260909a',
		w: 100, h: 100,
	},
	mudwisp: {
		url: 'mudwisp.png?v=20260909a',
		w: 100, h: 100,
	},
	blackwisp: {
		url: 'blackwisp.png?v=20260909a',
		w: 100, h: 100,
	},
	fireball: {
		url: 'fireball.png?v=20260909a',
		w: 64, h: 64,
	},
	bluefireball: {
		url: 'bluefireball.png?v=20260909a',
		w: 64, h: 64,
	},
	icicle: {
		url: 'icicle.png?v=20260909a', // http://opengameart.org/content/icicle-spell
		w: 80, h: 60,
	},
	pinkicicle: {
		url: 'icicle-pink.png?v=20260909a', // http://opengameart.org/content/icicle-spell, recolored by Kalalokki
		w: 80, h: 60,
	},
	lightning: {
		url: 'lightning.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 41, h: 229,
	},
	rocks: {
		url: 'rocks.png?v=20260909a', // Pokemon Online - Gilad
		w: 100, h: 100,
	},
	rock1: {
		url: 'rock1.png?v=20260909a', // Pokemon Online - Gilad
		w: 64, h: 80,
	},
	rock2: {
		url: 'rock2.png?v=20260909a', // Pokemon Online - Gilad
		w: 66, h: 72,
	},
	rock3: {
		url: 'rock3.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 66, h: 72,
	},
	leaf1: {
		url: 'leaf1.png?v=20260909a',
		w: 32, h: 26,
	},
	leaf2: {
		url: 'leaf2.png?v=20260909a',
		w: 40, h: 26,
	},
	bone: {
		url: 'bone.png?v=20260909a',
		w: 29, h: 29,
	},
	caltrop: {
		url: 'caltrop.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 80, h: 80,
	},
	greenmetal1: {
		url: 'greenmetal1.png?v=20260909a', // by Pokemon Showdown user Kalalokki
		w: 45, h: 45,
	},
	greenmetal2: {
		url: 'greenmetal2.png?v=20260909a', // by Pokemon Showdown user Kalalokki
		w: 45, h: 45,
	},
	poisoncaltrop: {
		url: 'poisoncaltrop.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 80, h: 80,
	},
	shadowball: {
		url: 'shadowball.png?v=20260909a',
		w: 100, h: 100,
	},
	energyball: {
		url: 'energyball.png?v=20260909a',
		w: 100, h: 100,
	},
	electroball: {
		url: 'electroball.png?v=20260909a',
		w: 100, h: 100,
	},
	mistball: {
		url: 'mistball.png?v=20260909a',
		w: 100, h: 100,
	},
	iceball: {
		url: 'iceball.png?v=20260909a',
		w: 100, h: 100,
	},
	flareball: {
		url: 'flareball.png?v=20260909a',
		w: 100, h: 100,
	},
	moon: {
		url: 'moon.png?v=20260909a', // by Kalalokki
		w: 100, h: 100,
	},
	pokeball: {
		url: 'pokeball.png?v=20260909a',
		w: 24, h: 24,
	},
	fist: {
		url: 'fist.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 55, h: 49,
	},
	fist1: {
		url: 'fist1.png?v=20260909a',
		w: 49, h: 55,
	},
	foot: {
		url: 'foot.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 50, h: 75,
	},
	topbite: {
		url: 'topbite.png?v=20260909a',
		w: 108, h: 64,
	},
	bottombite: {
		url: 'bottombite.png?v=20260909a',
		w: 108, h: 64,
	},
	web: {
		url: 'web.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 120, h: 122,
	},
	leftclaw: {
		url: 'leftclaw.png?v=20260909a',
		w: 44, h: 60,
	},
	rightclaw: {
		url: 'rightclaw.png?v=20260909a',
		w: 44, h: 60,
	},
	leftslash: {
		url: 'leftslash.png?v=20260909a', // by Pokemon Showdown user Modeling Clay
		w: 57, h: 56,
	},
	rightslash: {
		url: 'rightslash.png?v=20260909a', // by Pokemon Showdown user Modeling Clay
		w: 57, h: 56,
	},
	leftchop: {
		url: 'leftchop.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 100, h: 130,
	},
	rightchop: {
		url: 'rightchop.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 100, h: 130,
	},
	angry: {
		url: 'angry.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 30, h: 30,
	},
	heart: {
		url: 'heart.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 30, h: 30,
	},
	pointer: {
		url: 'pointer.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 100, h: 100,
	},
	sword: {
		url: 'sword.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 48, h: 100,
	},
	impact: {
		url: 'impact.png?v=20260909a', // by Pokemon Showdown user SailorCosmos
		w: 127, h: 119,
	},
	stare: {
		url: 'stare.png?v=20260909a',
		w: 100, h: 35,
	},
	shine: {
		url: 'shine.png?v=20260909a', // by Smogon user Jajoken
		w: 127, h: 119,
	},
	feather: {
		url: 'feather.png?v=20260909a', // Ripped from http://www.clker.com/clipart-black-and-white-feather.html
		w: 100, h: 38,
	},
	shell: {
		url: 'shell.png?v=20260909a', // by Smogon user Jajoken
		w: 100, h: 91.5,
	},
	petal: {
		url: 'petal.png?v=20260909a', // by Smogon user Jajoken
		w: 60, h: 60,
	},
	gear: {
		url: 'gear.png?v=20260909a', // by Smogon user Jajoken
		w: 100, h: 100,
	},
	alpha: {
		url: 'alpha.png?v=20260909a', // Ripped from Pokemon Global Link
		w: 80, h: 80,
	},
	omega: {
		url: 'omega.png?v=20260909a', // Ripped from Pokemon Global Link
		w: 80, h: 80,
	},
	rainbow: {
		url: 'rainbow.png?v=20260909a',
		w: 128, h: 128,
	},
	zsymbol: {
		url: 'z-symbol.png?v=20260909a', // From http://froggybutt.deviantart.com/art/Pokemon-Z-Move-symbol-633125033
		w: 150, h: 100,
	},
	ultra: {
		url: 'ultra.png?v=20260909a', // by Pokemon Showdown user Modeling Clay
		w: 113, h: 165,
	},
	hitmark: {
		url: 'hitmarker.png?v=20260909a', // by Pokemon Showdown user Ridaz
		w: 100, h: 100,
	},
	protect: {
		rawHTML: '<div class="turnstatus-protect" style="display:none;position:absolute" />',
		w: 100, h: 70,
	},
	auroraveil: {
		rawHTML: '<div class="sidecondition-auroraveil" style="display:none;position:absolute" />',
		w: 100, h: 50,
	},
	reflect: {
		rawHTML: '<div class="sidecondition-reflect" style="display:none;position:absolute" />',
		w: 100, h: 50,
	},
	safeguard: {
		rawHTML: '<div class="sidecondition-safeguard" style="display:none;position:absolute" />',
		w: 100, h: 50,
	},
	lightscreen: {
		rawHTML: '<div class="sidecondition-lightscreen" style="display:none;position:absolute" />',
		w: 100, h: 50,
	},
	mist: {
		rawHTML: '<div class="sidecondition-mist" style="display:none;position:absolute" />',
		w: 100, h: 50,
	},
};
(() => {
	if (!window.Dex || !Dex.resourcePrefix) return;
	for (const id in BattleEffects) {
		if (!BattleEffects[id].url) continue;
		BattleEffects[id].url = Dex.fxPrefix + BattleEffects[id].url;
	}
})();
const BattleBackdropsThree = [
	'bg-gen3.png',
	'bg-gen3-cave.png',
	'bg-gen3-ocean.png',
	'bg-gen3-sand.png',
	'bg-gen3-forest.png',
	'bg-gen3-arena.png',
];
const BattleBackdropsFour = [
	'bg-gen4.png',
	'bg-gen4-cave.png',
	'bg-gen4-snow.png',
	'bg-gen4-indoors.png',
	'bg-gen4-water.png',
];
const BattleBackdropsFive = [
	'bg-beach.png',
	'bg-beachshore.png',
	'bg-desert.png',
	'bg-meadow.png',
	'bg-thunderplains.png',
	'bg-city.png',
	'bg-earthycave.png',
	'bg-mountain.png',
	'bg-volcanocave.png',
	'bg-dampcave.png',
	'bg-forest.png',
	'bg-river.png',
	'bg-deepsea.png',
	'bg-icecave.png',
	'bg-route.png',
];
const BattleBackdrops = [
	// cherish-world fork: 이미지 캐시 스탬프 20260909a
	// cherish-world fork: 배경 12종 — 새로 그린 것만 남긴다 (patch-bg12.py)
	'bg-meadow.jpg?v=20260909a',
	'bg-forest.jpg?v=20260909a',
	'bg-icecave.jpg?v=20260909a',
	'bg-aquacordetown.jpg?v=20260909a',
	'bg-beach.jpg?v=20260909a',
	'bg-city.jpg?v=20260909a',
	'bg-dampcave.jpg?v=20260909a',
	'bg-darkbeach.jpg?v=20260909a',
	'bg-darkcity.jpg?v=20260909a',
	'bg-desert.jpg?v=20260909a',
	'bg-deepsea.jpg?v=20260909a',
	'bg-skypillar.jpg?v=20260909a',
];

export const BattleOtherAnims: AnimTable = {
	hitmark: {
		anim(scene, [attacker]) {
			scene.showEffect('hitmark', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
			}, {
				opacity: 0.5,
				time: 250,
			}, 'linear', 'fade');
		},
	},
	attack: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.1,
				opacity: 1,
			}, {
				x: defender.x,
				y: defender.y,
				z: defender.behind(40),
				scale: 1,
				opacity: 0.5,
			}, 'linear');
		},
	},
	contactattack: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				x: defender.x,
				y: defender.y + 80,
				z: defender.behind(-30),
				time: 400,
			}, 'ballistic');
			attacker.anim({
				x: defender.x,
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				time: 500,
			}, 'ballistic2Back');
			defender.delay(450);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
			scene.wait(500);
		},
	},
	xattack: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 400,
			}, {
				x: defender.leftof(-20),
				y: defender.y,
				z: defender.behind(20),
				scale: 3,
				opacity: 0,
				time: 700,
			}, 'linear');
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 700,
			}, {
				x: defender.leftof(-20),
				y: defender.y,
				z: defender.behind(20),
				scale: 3,
				opacity: 0,
				time: 1000,
			}, 'linear');
			defender.delay(480);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 200,
			}, 'swing');
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
			attacker.anim({
				x: defender.leftof(-30),
				y: defender.y + 80,
				z: defender.behind(-30),
				time: 400,
			}, 'ballistic');
			attacker.anim({
				x: defender.leftof(30),
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				x: defender.leftof(30),
				y: defender.y + 80,
				z: defender.behind(-30),
				time: 200,
			}, 'ballisticUp');
			attacker.anim({
				x: defender.leftof(-30),
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				time: 500,
			}, 'ballistic2Back');
		},
	},
	slashattack: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				x: defender.x,
				y: defender.y + 80,
				z: defender.behind(-30),
				time: 400,
			}, 'ballistic');
			attacker.anim({
				x: defender.x,
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				time: 500,
			}, 'ballistic2Back');
			defender.delay(450);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');

			scene.showEffect('rightslash', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 1,
				opacity: 1,
				time: 500,
			}, {
				scale: 3,
				opacity: 0,
				time: 800,
			}, 'linear', 'fade');
		},
	},
	clawattack: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				x: defender.leftof(-30),
				y: defender.y + 80,
				z: defender.behind(-30),
				time: 400,
			}, 'ballistic');
			attacker.anim({
				x: defender.leftof(30),
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				x: defender.leftof(30),
				y: defender.y + 80,
				z: defender.behind(-30),
				time: 200,
			}, 'ballisticUp');
			attacker.anim({
				x: defender.leftof(-30),
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				time: 500,
			}, 'ballistic2Back');
			defender.delay(450);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 200,
			}, 'swing');
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');

			scene.showEffect('leftclaw', {
				x: defender.x - 20,
				y: defender.y + 20,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 400,
			}, {
				x: defender.x - 20,
				y: defender.y + 20,
				z: defender.z,
				scale: 3,
				opacity: 0,
				time: 700,
			}, 'linear', 'fade');
			scene.showEffect('leftclaw', {
				x: defender.x - 20,
				y: defender.y - 20,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 400,
			}, {
				x: defender.x - 20,
				y: defender.y - 20,
				z: defender.z,
				scale: 3,
				opacity: 0,
				time: 700,
			}, 'linear', 'fade');
			scene.showEffect('rightclaw', {
				x: defender.x + 20,
				y: defender.y + 20,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 700,
			}, {
				x: defender.x + 20,
				y: defender.y + 20,
				z: defender.z,
				scale: 3,
				opacity: 0,
				time: 1000,
			}, 'linear', 'fade');
			scene.showEffect('rightclaw', {
				x: defender.x + 20,
				y: defender.y - 20,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 700,
			}, {
				x: defender.x + 20,
				y: defender.y - 20,
				z: defender.z,
				scale: 3,
				opacity: 0,
				time: 1000,
			}, 'linear', 'fade');
		},
	},
	punchattack: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 400,
			}, {
				x: defender.leftof(-20),
				y: defender.y,
				z: defender.behind(20),
				scale: 3,
				opacity: 0,
				time: 700,
			}, 'linear');
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 1,
				time: 500,
			}, {
				x: defender.leftof(-20),
				y: defender.y,
				z: defender.behind(20),
				scale: 3,
				opacity: 0,
				time: 800,
			}, 'linear');
			scene.showEffect('fist', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 1,
				opacity: 1,
				time: 400,
			}, {
				x: defender.leftof(-20),
				y: defender.y,
				z: defender.behind(20),
				scale: 2,
				opacity: 0,
				time: 800,
			}, 'linear');
			attacker.anim({
				x: defender.leftof(20),
				y: defender.y,
				z: defender.behind(-20),
				time: 400,
			}, 'ballistic2Under');
			attacker.anim({
				x: defender.x,
				y: defender.y,
				z: defender.z,
				time: 50,
			});
			attacker.anim({
				time: 500,
			}, 'ballistic2');
			defender.delay(425);
			defender.anim({
				x: defender.leftof(-15),
				y: defender.y,
				z: defender.behind(15),
				time: 50,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
		},
	},
	bite: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('topbite', {
				x: defender.x,
				y: defender.y + 50,
				z: defender.z,
				scale: 0.5,
				opacity: 0,
				time: 370,
			}, {
				x: defender.x,
				y: defender.y + 10,
				z: defender.z,
				scale: 0.5,
				opacity: 1,
				time: 500,
			}, 'linear', 'fade');
			scene.showEffect('bottombite', {
				x: defender.x,
				y: defender.y - 50,
				z: defender.z,
				scale: 0.5,
				opacity: 0,
				time: 370,
			}, {
				x: defender.x,
				y: defender.y - 10,
				z: defender.z,
				scale: 0.5,
				opacity: 1,
				time: 500,
			}, 'linear', 'fade');
		},
	},
	kick: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('foot', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 1,
				opacity: 1,
				time: 400,
			}, {
				x: defender.x,
				y: defender.y - 20,
				z: defender.behind(15),
				scale: 2,
				opacity: 0,
				time: 800,
			}, 'linear');
		},
	},
	fastattack: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 0.5,
				time: 260,
			}, {
				scale: 2,
				opacity: 0,
				time: 560,
			}, 'linear');
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 0.5,
				time: 310,
			}, {
				scale: 2,
				opacity: 0,
				time: 610,
			}, 'linear');
			scene.showEffect(attacker.sp, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0.3,
				time: 50,
			}, {
				x: defender.x,
				y: defender.y,
				z: defender.behind(70),
				time: 350,
			}, 'accel', 'fade');
			scene.showEffect(attacker.sp, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0.3,
				time: 100,
			}, {
				x: defender.x,
				y: defender.y,
				z: defender.behind(70),
				time: 400,
			}, 'accel', 'fade');
			attacker.anim({
				x: defender.x,
				y: defender.y,
				z: defender.behind(70),
				time: 300,
				opacity: 0.5,
			}, 'accel');
			attacker.anim({
				x: defender.x,
				y: defender.x,
				z: defender.behind(100),
				opacity: 0,
				time: 100,
			}, 'linear');
			attacker.anim({
				x: attacker.x,
				y: attacker.y,
				z: attacker.behind(70),
				opacity: 0,
				time: 1,
			}, 'linear');
			attacker.anim({
				opacity: 1,
				time: 500,
			}, 'decel');
			defender.delay(260);
			defender.anim({
				z: defender.behind(30),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
		},
	},
	fastanimattack: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				z: attacker.behind(-70),
				time: 50,
				opacity: 1,
			}, 'decel');
			attacker.anim({
				opacity: 1,
				time: 150,
			}, 'accel');
			defender.anim({
				z: defender.behind(30),
				time: 70,
			}, 'decel');
			defender.anim({
				time: 170,
			}, 'accel');
		},
	},
	fastanimspecial: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('shadowball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.8,
				time: 0,
			}, {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				time: 100,
			}, 'decel');
			scene.showEffect('shadowball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.8,
				time: 70,
			}, {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				time: 170,
			}, 'decel');
			defender.anim({
				z: defender.behind(30),
				time: 70,
			}, 'decel');
			defender.anim({
				time: 170,
			}, 'accel');
		},
	},
	fastanimself: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				scale: 1.5,
				time: 50,
			}, 'decel');
			attacker.anim({
				time: 170,
			}, 'accel');
		},
	},
	sneakattack: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				x: attacker.leftof(-20),
				y: attacker.y,
				z: attacker.behind(-20),
				opacity: 0,
				time: 200,
			}, 'linear');
			attacker.anim({
				x: defender.x,
				y: defender.y,
				z: defender.behind(-120),
				opacity: 0,
				time: 1,
			}, 'linear');
			attacker.anim({
				x: defender.x,
				y: defender.y,
				z: defender.behind(40),
				opacity: 1,
				time: 250,
			}, 'linear');
			attacker.anim({
				x: defender.x,
				y: defender.y,
				z: defender.behind(-5),
				opacity: 0,
				time: 300,
			}, 'linear');
			attacker.anim({
				opacity: 0,
				time: 1,
			}, 'linear');
			attacker.anim({
				time: 300,
				opacity: 1,
			}, 'linear');
			defender.delay(330);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
		},
	},
	spinattack: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				x: defender.x,
				y: defender.y + 60,
				z: defender.behind(-30),
				time: 400,
			}, 'ballistic2');
			attacker.anim({
				x: defender.x,
				y: defender.y + 5,
				z: defender.z,
				time: 100,
			});
			attacker.anim({
				time: 500,
			}, 'ballistic2');
			defender.delay(450);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
			scene.wait(500);
		},
	},
	bound: {
		anim(scene, [attacker]) {
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y + 15,
				z: attacker.z,
				scale: 0.7,
				xscale: 2,
				opacity: 0.3,
				time: 0,
			}, {
				scale: 0.4,
				xscale: 1,
				opacity: 0.1,
				time: 500,
			}, 'decel', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y - 5,
				z: attacker.z,
				scale: 0.7,
				xscale: 2,
				opacity: 0.3,
				time: 50,
			}, {
				scale: 0.4,
				xscale: 1,
				opacity: 0.1,
				time: 550,
			}, 'decel', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y - 20,
				z: attacker.z,
				scale: 0.7,
				xscale: 2,
				opacity: 0.3,
				time: 100,
			}, {
				scale: 0.4,
				xscale: 1,
				opacity: 0.1,
				time: 600,
			}, 'decel', 'fade');
			attacker.anim({
				y: attacker.y + 15,
				z: attacker.behind(10),
				yscale: 1.3,
				time: 200,
			}, 'swing');
			attacker.anim({
				time: 200,
			}, 'swing');
			attacker.delay(25);
			attacker.anim({
				x: attacker.leftof(-10),
				y: attacker.y + 15,
				z: attacker.behind(5),
				yscale: 1.3,
				time: 200,
			}, 'swing');
			attacker.anim({
				time: 200,
			}, 'swing');
		},
	},
	selfstatus: {
		anim(scene, [attacker]) {
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0.2,
				time: 0,
			}, {
				scale: 0,
				opacity: 1,
				time: 300,
			}, 'linear');
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0.2,
				time: 200,
			}, {
				scale: 0,
				opacity: 1,
				time: 500,
			}, 'linear');
		},
	},
	lightstatus: {
		anim(scene, [attacker]) {
			scene.showEffect('electroball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0.1,
				time: 0,
			}, {
				scale: 0,
				opacity: 0.5,
				time: 600,
			}, 'linear');
		},
	},
	chargestatus: {
		anim(scene, [attacker]) {
			scene.showEffect('electroball', {
				x: attacker.x - 60,
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 0,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x + 60,
				y: attacker.y - 5,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x - 30,
				y: attacker.y + 60,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 400,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x + 20,
				y: attacker.y - 50,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 400,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x - 70,
				y: attacker.y - 50,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 200,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 500,
			}, 'linear', 'fade');
		},
	},
	heal: {
		anim(scene, [attacker]) {
			scene.showEffect('iceball', {
				x: attacker.x + 30,
				y: attacker.y + 5,
				z: attacker.z,
				scale: 0.1,
				opacity: 0.7,
				time: 200,
			}, {
				x: attacker.x + 40,
				y: attacker.y + 10,
				opacity: 0,
				time: 600,
			}, 'accel');
			scene.showEffect('iceball', {
				x: attacker.x - 30,
				y: attacker.y - 10,
				z: attacker.z,
				scale: 0.1,
				opacity: 0.7,
				time: 300,
			}, {
				x: attacker.x - 40,
				y: attacker.y - 20,
				opacity: 0,
				time: 700,
			}, 'accel');
			scene.showEffect('iceball', {
				x: attacker.x + 15,
				y: attacker.y + 10,
				z: attacker.z,
				scale: 0.1,
				opacity: 0.7,
				time: 400,
			}, {
				x: attacker.x + 25,
				y: attacker.y + 20,
				opacity: 0,
				time: 800,
			}, 'accel');
			scene.showEffect('iceball', {
				x: attacker.x - 15,
				y: attacker.y - 30,
				z: attacker.z,
				scale: 0.1,
				opacity: 0.7,
				time: 500,
			}, {
				x: attacker.x - 25,
				y: attacker.y - 40,
				opacity: 0,
				time: 900,
			}, 'accel');
		},
	},
	shiny: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#000000', 800, 0.3, 100);
			scene.showEffect('shine', {
				x: attacker.x + 5,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.1,
				opacity: 0.7,
				time: 450,
			}, {
				y: attacker.y + 35,
				opacity: 0,
				time: 675,
			}, 'decel');
			scene.showEffect('shine', {
				x: attacker.x + 15,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.2,
				opacity: 0.7,
				time: 475,
			}, {
				x: attacker.x + 25,
				y: attacker.y + 30,
				opacity: 0,
				time: 700,
			}, 'decel');
			scene.showEffect('shine', {
				x: attacker.x - 15,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.2,
				opacity: 0.7,
				time: 500,
			}, {
				x: attacker.x - 25,
				y: attacker.y + 30,
				opacity: 0,
				time: 725,
			}, 'decel');
			scene.showEffect('shine', {
				x: attacker.x - 20,
				y: attacker.y + 5,
				z: attacker.z,
				scale: 0.2,
				opacity: 0.7,
				time: 550,
			}, {
				x: attacker.x - 30,
				y: attacker.y - 5,
				opacity: 0,
				time: 775,
			}, 'decel');
			scene.showEffect('shine', {
				x: attacker.x + 15,
				y: attacker.y + 10,
				z: attacker.z,
				scale: 0.2,
				opacity: 0.7,
				time: 650,
			}, {
				x: attacker.x + 35,
				y: attacker.y - 5,
				opacity: 0,
				time: 875,
			}, 'decel');
			scene.showEffect('shine', {
				x: attacker.x + 5,
				y: attacker.y - 5,
				z: attacker.z,
				scale: 0.2,
				opacity: 0.7,
				time: 675,
			}, {
				y: attacker.y - 20,
				opacity: 0,
				time: 900,
			}, 'decel');
		},
	},
	flight: {
		anim(scene, [attacker, defender]) {
			attacker.anim({
				x: attacker.leftof(-200),
				y: attacker.y + 80,
				z: attacker.z,
				opacity: 0,
				time: 350,
			}, 'accel');
			attacker.anim({
				x: defender.leftof(-200),
				y: defender.y + 80,
				z: defender.z,
				time: 1,
			}, 'linear');
			attacker.anim({
				x: defender.x,
				y: defender.y,
				z: defender.z,
				opacity: 1,
				time: 350,
			}, 'accel');
			scene.showEffect('wisp', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 0.5,
				time: 700,
			}, {
				scale: 2,
				opacity: 0,
				time: 900,
			}, 'linear');
			attacker.anim({
				x: defender.leftof(100),
				y: defender.y - 40,
				z: defender.z,
				opacity: 0,
				time: 175,
			});
			attacker.anim({
				x: attacker.x,
				y: attacker.y + 40,
				z: attacker.behind(40),
				time: 1,
			});
			attacker.anim({
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 250,
			}, 'decel');
			defender.delay(700);
			defender.anim({
				z: defender.behind(20),
				time: 100,
			}, 'swing');
			defender.anim({
				time: 300,
			}, 'swing');
		},
	},
	shake: {
		anim(scene, [attacker]) {
			attacker.anim({ x: attacker.x - 10, time: 200 });
			attacker.anim({ x: attacker.x + 10, time: 300 });
			attacker.anim({ x: attacker.x, time: 200 });
		},
	},
	dance: {
		anim(scene, [attacker]) {
			attacker.anim({ x: attacker.x - 10 });
			attacker.anim({ x: attacker.x + 10 });
			attacker.anim({ x: attacker.x });
		},
	},
	consume: {
		anim(scene, [defender]) {
			scene.showEffect('wisp', {
				x: defender.leftof(-25),
				y: defender.y + 40,
				z: defender.behind(-20),
				scale: 0.5,
				opacity: 1,
			}, {
				x: defender.leftof(-15),
				y: defender.y + 35,
				z: defender.z,
				scale: 0,
				opacity: 0.2,
				time: 500,
			}, 'swing', 'fade');

			defender.delay(400);
			defender.anim({
				y: defender.y + 5,
				yscale: 1.1,
				time: 200,
			}, 'swing');
			defender.anim({
				time: 200,
			}, 'swing');
			defender.anim({
				y: defender.y + 5,
				yscale: 1.1,
				time: 200,
			}, 'swing');
			defender.anim({
				time: 200,
			}, 'swing');
		},
	},
	leech: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('energyball', {
				x: defender.x - 30,
				y: defender.y - 40,
				z: defender.z,
				scale: 0.2,
				opacity: 0.7,
				time: 0,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 500,
				opacity: 0.1,
			}, 'ballistic2', 'fade');
			scene.showEffect('energyball', {
				x: defender.x + 40,
				y: defender.y - 35,
				z: defender.z,
				scale: 0.2,
				opacity: 0.7,
				time: 50,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 550,
				opacity: 0.1,
			}, 'linear', 'fade');
			scene.showEffect('energyball', {
				x: defender.x + 20,
				y: defender.y - 25,
				z: defender.z,
				scale: 0.2,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 600,
				opacity: 0.1,
			}, 'ballistic2Under', 'fade');
		},
	},
	drain: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('energyball', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0.6,
				opacity: 0.6,
				time: 0,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 500,
				opacity: 0,
			}, 'ballistic2');
			scene.showEffect('energyball', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0.6,
				opacity: 0.6,
				time: 50,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 550,
				opacity: 0,
			}, 'linear');
			scene.showEffect('energyball', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 0.6,
				opacity: 0.6,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				time: 600,
				opacity: 0,
			}, 'ballistic2Under');
		},
	},
	hydroshot: {
		anim(scene, [attacker, defender]) {
			scene.showEffect('waterwisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.4,
				opacity: 0.3,
			}, {
				x: defender.x + 10,
				y: defender.y + 5,
				z: defender.behind(30),
				scale: 1,
				opacity: 0.6,
			}, 'decel', 'explode');
			scene.showEffect('waterwisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.4,
				opacity: 0.3,
				time: 75,
			}, {
				x: defender.x - 10,
				y: defender.y - 5,
				z: defender.behind(30),
				scale: 1,
				opacity: 0.6,
			}, 'decel', 'explode');
			scene.showEffect('waterwisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.4,
				opacity: 0.3,
				time: 150,
			}, {
				x: defender.x,
				y: defender.y + 5,
				z: defender.behind(30),
				scale: 1,
				opacity: 0.6,
			}, 'decel', 'explode');
		},
	},
	sound: {
		anim(scene, [attacker]) {
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0,
				opacity: 0.7,
				time: 0,
			}, {
				z: attacker.behind(-50),
				scale: 5,
				opacity: 0,
				time: 400,
			}, 'linear');
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0,
				opacity: 0.7,
				time: 150,
			}, {
				z: attacker.behind(-50),
				scale: 5,
				opacity: 0,
				time: 600,
			}, 'linear');
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0,
				opacity: 0.7,
				time: 300,
			}, {
				z: attacker.behind(-50),
				scale: 5,
				opacity: 0,
				time: 800,
			}, 'linear');
		},
	},
	gravity: {
		anim(scene, [attacker]) {
			attacker.anim({
				y: attacker.y - 20,
				yscale: 0.5,
				time: 300,
			}, 'decel');
			attacker.delay(200);
			attacker.anim({
				time: 300,
			});
		},
	},
	futuresighthit: {
		anim(scene, [defender]) {
			scene.backgroundEffect('#AA44BB', 250, 0.6);
			scene.backgroundEffect('#AA44FF', 250, 0.6, 400);
			defender.anim({
				scale: 1.2,
				time: 100,
			});
			defender.anim({
				scale: 1,
				time: 100,
			});
			defender.anim({
				scale: 1.4,
				time: 150,
			});
			defender.anim({
				scale: 1,
				time: 150,
			});
			scene.wait(700);
		},
	},
	doomdesirehit: {
		anim(scene, [defender]) {
			scene.backgroundEffect('#ffffff', 600, 0.6);
			scene.showEffect('fireball', {
				x: defender.x + 40,
				y: defender.y,
				z: defender.z,
				scale: 0,
				opacity: 0.6,
			}, {
				scale: 6,
				opacity: 0,
			}, 'linear');
			scene.showEffect('fireball', {
				x: defender.x - 40,
				y: defender.y - 20,
				z: defender.z,
				scale: 0,
				opacity: 0.6,
				time: 150,
			}, {
				scale: 6,
				opacity: 0,
			}, 'linear');
			scene.showEffect('fireball', {
				x: defender.x + 10,
				y: defender.y + 20,
				z: defender.z,
				scale: 0,
				opacity: 0.6,
				time: 300,
			}, {
				scale: 6,
				opacity: 0,
			}, 'linear');

			defender.delay(100);
			defender.anim({
				x: defender.x - 30,
				time: 75,
			});
			defender.anim({
				x: defender.x + 30,
				time: 100,
			});
			defender.anim({
				x: defender.x - 30,
				time: 100,
			});
			defender.anim({
				x: defender.x + 30,
				time: 100,
			});
			defender.anim({
				x: defender.x,
				time: 100,
			});
		},
	},
	itemoff: {
		anim(scene, [defender]) {
			scene.showEffect('pokeball', {
				x: defender.x,
				y: defender.y,
				z: defender.z,
				scale: 1,
				opacity: 1,
			}, {
				x: defender.x,
				y: defender.y + 40,
				z: defender.behind(70),
				opacity: 0,
				time: 400,
			}, 'ballistic2');
		},
	},
	anger: {
		anim(scene, [attacker]) {
			scene.showEffect('angry', {
				x: attacker.x + 20,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 0,
			}, {
				scale: 1,
				opacity: 1,
				time: 300,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('angry', {
				x: attacker.x - 20,
				y: attacker.y + 10,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 100,
			}, {
				scale: 1,
				opacity: 1,
				time: 400,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('angry', {
				x: attacker.x,
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 200,
			}, {
				scale: 1,
				opacity: 1,
				time: 500,
			}, 'ballistic2Under', 'fade');
		},
	},
	bidecharge: {
		anim(scene, [attacker]) {
			scene.showEffect('wisp', {
				x: attacker.x + 30,
				y: attacker.y,
				z: attacker.z,
				scale: 1,
				opacity: 1,
				time: 0,
			}, {
				y: attacker.y + 60,
				opacity: 0.2,
				time: 400,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x - 30,
				y: attacker.y,
				z: attacker.z,
				scale: 1,
				opacity: 1,
				time: 100,
			}, {
				y: attacker.y + 60,
				opacity: 0.2,
				time: 500,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x + 15,
				y: attacker.y,
				z: attacker.z,
				scale: 1,
				opacity: 1,
				time: 200,
			}, {
				y: attacker.y + 60,
				opacity: 0.2,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x - 15,
				y: attacker.y,
				z: attacker.z,
				scale: 1,
				opacity: 1,
				time: 300,
			}, {
				y: attacker.y + 60,
				opacity: 0.2,
				time: 700,
			}, 'linear', 'fade');

			attacker.anim({
				x: attacker.x - 2.5,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x + 2.5,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x - 2.5,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x + 2.5,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x - 2.5,
				time: 75,
			}, 'swing');
			attacker.anim({
				time: 100,
			}, 'accel');
		},
	},
	bideunleash: {
		anim(scene, [attacker]) {
			scene.showEffect('fireball', {
				x: attacker.x + 40,
				y: attacker.y,
				z: attacker.z,
				scale: 0,
				opacity: 0.6,
			}, {
				scale: 6,
				opacity: 0,
			}, 'linear');
			scene.showEffect('fireball', {
				x: attacker.x - 40,
				y: attacker.y - 20,
				z: attacker.z,
				scale: 0,
				opacity: 0.6,
				time: 150,
			}, {
				scale: 6,
				opacity: 0,
			}, 'linear');
			scene.showEffect('fireball', {
				x: attacker.x + 10,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0,
				opacity: 0.6,
				time: 300,
			}, {
				scale: 6,
				opacity: 0,
			}, 'linear');

			attacker.anim({
				x: attacker.x - 30,
				time: 75,
			});
			attacker.anim({
				x: attacker.x + 30,
				time: 100,
			});
			attacker.anim({
				x: attacker.x - 30,
				time: 100,
			});
			attacker.anim({
				x: attacker.x + 30,
				time: 100,
			});
			attacker.anim({
				x: attacker.x - 30,
				time: 100,
			});
			attacker.anim({
				x: attacker.x + 30,
				time: 100,
			});
			attacker.anim({
				x: attacker.x,
				time: 100,
			});
		},
	},
	spectralthiefboost: {
		anim(scene, [attacker, defender]) {
			scene.backgroundEffect('linear-gradient(#000000 30%, #440044', 1400, 0.5);
			scene.showEffect('shadowball', {
				x: defender.x,
				y: defender.y - 30,
				z: defender.z,
				scale: 0.5,
				xscale: 0.5,
				yscale: 1,
				opacity: 0.5,
			}, {
				scale: 2,
				xscale: 4,
				opacity: 0.1,
				time: 400,
			}, 'decel', 'fade');
			scene.showEffect('purplewisp', {
				x: defender.x,
				y: defender.y - 25,
				z: defender.z,
				scale: 1,
			}, {
				x: defender.x + 50,
				scale: 3,
				xscale: 3.5,
				opacity: 0.3,
				time: 500,
			}, 'linear', 'fade');
			scene.showEffect('purplewisp', {
				x: defender.x,
				y: defender.y - 25,
				z: defender.z,
				scale: 1,
			}, {
				x: defender.x - 50,
				scale: 3,
				xscale: 3.5,
				opacity: 0.3,
				time: 500,
			}, 'linear', 'fade');
			scene.showEffect('shadowball', {
				x: defender.x + 35,
				y: defender.y,
				z: defender.z,
				opacity: 0.4,
				scale: 0.25,
				time: 50,
			}, {
				y: defender.y - 40,
				opacity: 0,
				time: 300,
			}, 'accel');
			scene.showEffect('shadowball', {
				x: defender.x - 35,
				y: defender.y,
				z: defender.z,
				opacity: 0.4,
				scale: 0.25,
				time: 100,
			}, {
				y: defender.y - 40,
				opacity: 0,
				time: 350,
			}, 'accel');
			scene.showEffect('shadowball', {
				x: defender.x + 15,
				y: defender.y,
				z: defender.z,
				opacity: 0.4,
				scale: 0.5,
				time: 150,
			}, {
				y: defender.y - 40,
				opacity: 0,
				time: 400,
			}, 'accel');
			scene.showEffect('shadowball', {
				x: defender.x + 15,
				y: defender.y,
				z: defender.z,
				opacity: 0.4,
				scale: 0.25,
				time: 200,
			}, {
				y: defender.y - 40,
				opacity: 0,
				time: 450,
			}, 'accel');

			scene.showEffect('purplewisp', {
				x: defender.x - 50,
				y: defender.y - 40,
				z: defender.z,
				scale: 2,
				opacity: 0.3,
				time: 300,
			}, {
				x: attacker.x - 50,
				y: attacker.y - 40,
				z: attacker.z,
				time: 900,
			}, 'decel', 'fade');
			scene.showEffect('purplewisp', {
				x: defender.x - 50,
				y: defender.y - 40,
				z: defender.z,
				scale: 2,
				opacity: 0.3,
				time: 400,
			}, {
				x: attacker.x - 50,
				y: attacker.y - 40,
				z: attacker.z,
				time: 900,
			}, 'decel', 'fade');
			scene.showEffect('purplewisp', {
				x: defender.x,
				y: defender.y - 40,
				z: defender.z,
				scale: 2,
				opacity: 0.3,
				time: 450,
			}, {
				x: attacker.x,
				y: attacker.y - 40,
				z: attacker.z,
				time: 950,
			}, 'decel', 'fade');

			scene.showEffect('shadowball', {
				x: attacker.x,
				y: attacker.y - 30,
				z: attacker.z,
				scale: 0,
				xscale: 0.5,
				yscale: 1,
				opacity: 0.5,
				time: 750,
			}, {
				scale: 2,
				xscale: 4,
				opacity: 0.1,
				time: 1200,
			}, 'decel', 'fade');

			scene.showEffect('shadowball', {
				x: attacker.x + 35,
				y: attacker.y - 40,
				z: attacker.z,
				opacity: 0.4,
				scale: 0.25,
				time: 750,
			}, {
				y: attacker.y,
				opacity: 0,
				time: 1000,
			}, 'decel');
			scene.showEffect('shadowball', {
				x: attacker.x - 35,
				y: attacker.y - 40,
				z: attacker.z,
				opacity: 1,
				scale: 0.25,
				time: 800,
			}, {
				y: attacker.y,
				opacity: 0,
				time: 1150,
			}, 'decel');
			scene.showEffect('shadowball', {
				x: attacker.x + 15,
				y: attacker.y - 40,
				z: attacker.z,
				opacity: 1,
				scale: 0.25,
				time: 950,
			}, {
				y: attacker.y,
				opacity: 0,
				time: 1200,
			}, 'decel');
			scene.showEffect('shadowball', {
				x: attacker.x + 15,
				y: attacker.y - 40,
				z: attacker.z,
				opacity: 1,
				scale: 0.25,
				time: 1000,
			}, {
				y: attacker.y,
				opacity: 0,
				time: 1350,
			}, 'decel');

			scene.showEffect('purplewisp', {
				x: attacker.x,
				y: attacker.y - 25,
				z: attacker.z,
				scale: 2,
				opacity: 1,
				time: 750,
			}, {
				x: attacker.x + 75,
				opacity: 0.3,
				time: 1200,
			}, 'linear', 'fade');
			scene.showEffect('purplewisp', {
				x: attacker.x,
				y: attacker.y - 25,
				z: attacker.z,
				scale: 2,
				opacity: 1,
				time: 750,
			}, {
				x: attacker.x - 75,
				opacity: 0.3,
				time: 1200,
			}, 'linear', 'fade');

			defender.anim({
				x: defender.x - 15,
				time: 75,
			});
			defender.anim({
				x: defender.x + 15,
				time: 100,
			});
			defender.anim({
				x: defender.x - 15,
				time: 100,
			});
			defender.anim({
				x: defender.x + 15,
				time: 100,
			});
			defender.anim({
				x: defender.x - 15,
				time: 100,
			});
			defender.anim({
				x: defender.x + 15,
				time: 100,
			});
			defender.anim({
				x: defender.x,
				time: 100,
			});
		},
	},
	schoolingin: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#0000DD', 600, 0.2);
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2.5,
				opacity: 1,
			}, {
				scale: 3,
				time: 600,
			}, 'linear', 'explode');
			scene.showEffect('waterwisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 3,
				opacity: 0.3,
			}, {
				scale: 3.25,
				time: 600,
			}, 'linear', 'explode');

			scene.showEffect('iceball', {
				x: attacker.leftof(200),
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0,
				time: 200,
			}, 'ballistic', 'fade');
			scene.showEffect('iceball', {
				x: attacker.leftof(-140),
				y: attacker.y - 60,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0,
				time: 300,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('iceball', {
				x: attacker.leftof(-140),
				y: attacker.y + 50,
				z: attacker.behind(170),
				scale: 0.5,
				opacity: 0.5,
				time: 200,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0,
				time: 400,
			}, 'ballistic2', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y + 30,
				z: attacker.behind(-250),
				scale: 0.5,
				opacity: 0.5,
				time: 200,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0,
				time: 500,
			}, 'ballistic', 'fade');
			scene.showEffect('iceball', {
				x: attacker.leftof(240),
				y: attacker.y - 80,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 300,
			}, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0,
				time: 600,
			}, 'ballistic2Under');
		},
	},
	schoolingout: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#0000DD', 600, 0.2);
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 3,
				opacity: 1,
			}, {
				scale: 2,
				time: 600,
			}, 'linear', 'explode');
			scene.showEffect('waterwisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 3.25,
				opacity: 0.3,
			}, {
				scale: 2.5,
				time: 600,
			}, 'linear', 'explode');

			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0,
			}, {
				x: attacker.leftof(200),
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 200,
			}, 'ballistic', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0,
				time: 100,
			}, {
				x: attacker.leftof(-140),
				y: attacker.y - 60,
				z: attacker.z,
				opacity: 0.5,
				time: 300,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0,
				time: 200,
			}, {
				x: attacker.leftof(-140),
				y: attacker.y + 50,
				z: attacker.behind(170),
				opacity: 0.5,
				time: 400,
			}, 'ballistic2', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0,
				time: 200,
			}, {
				x: attacker.x,
				y: attacker.y + 30,
				z: attacker.behind(-250),
				opacity: 0.5,
				time: 500,
			}, 'ballistic', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0,
				time: 300,
			}, {
				x: attacker.leftof(240),
				y: attacker.y - 80,
				z: attacker.z,
				opacity: 0.5,
				time: 600,
			}, 'ballistic2Under');
		},
	},
	primalalpha: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#0000DD', 500, 0.4);
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0.2,
				time: 0,
			}, {
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, {
				scale: 4,
				opacity: 0,
				time: 700,
			}, 'linear', 'fade');
			scene.showEffect('shadowball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 300,
			}, {
				scale: 5,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('alpha', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, {
				scale: 2.5,
				opacity: 0,
				time: 600,
			}, 'decel');
		},
	},
	primalomega: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('linear-gradient(#390000 30%, #B84038)', 500, 0.4);
			scene.showEffect('flareball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0.2,
				time: 0,
			}, {
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('flareball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, {
				scale: 4,
				opacity: 0,
				time: 700,
			}, 'linear', 'fade');
			scene.showEffect('shadowball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 300,
			}, {
				scale: 5,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('omega', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, {
				scale: 2.5,
				opacity: 0,
				time: 600,
			}, 'decel');
		},
	},
	megaevo: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#835BA5', 500, 0.6);
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0.2,
				time: 0,
			}, {
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, {
				scale: 4,
				opacity: 0,
				time: 700,
			}, 'linear', 'fade');
			scene.showEffect('mistball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 300,
			}, {
				scale: 5,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('rainbow', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 300,
			}, {
				scale: 5,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
		},
	},
	zpower: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('linear-gradient(#000000 20%, #0000DD)', 1800, 0.4);
			scene.showEffect('electroball', {
				x: attacker.x - 60,
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 0,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x + 60,
				y: attacker.y - 5,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x - 30,
				y: attacker.y + 60,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 400,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x + 20,
				y: attacker.y - 50,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 400,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x - 70,
				y: attacker.y - 50,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 200,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 500,
			}, 'linear', 'fade');
			scene.showEffect('zsymbol', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.7,
				opacity: 1,
				time: 500,
			}, {
				scale: 1,
				opacity: 0.5,
				time: 800,
			}, 'decel', 'explode');
			scene.showEffect(attacker.sp, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0.3,
				time: 800,
			}, {
				y: attacker.y + 20,
				scale: 2,
				opacity: 0,
				time: 1200,
			}, 'accel');
			scene.showEffect(attacker.sp, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0.3,
				time: 1000,
			}, {
				y: attacker.y + 20,
				scale: 2,
				opacity: 0,
				time: 1400,
			}, 'accel');
			scene.showEffect(attacker.sp, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0.3,
				time: 1200,
			}, {
				y: attacker.y + 20,
				scale: 2,
				opacity: 0,
				time: 1600,
			}, 'accel');
		},
	},
	powerconstruct: {
		anim(scene, [attacker]) {
			let xf = [1, -1, 1, -1];
			let yf = [1, -1, -1, 1];
			let xf2 = [1, 0, -1, 0];
			let yf2 = [0, 1, 0, -1];

			scene.backgroundEffect('#000000', 1000, 0.7);
			for (let i = 0; i < 4; i++) {
				scene.showEffect('energyball', {
					x: attacker.x + 150 * xf[i],
					y: attacker.y - 50,
					z: attacker.z + 70 * yf[i],
					scale: 0.1,
					xscale: 0.5,
					opacity: 0.4,
				}, {
					x: attacker.x,
					y: attacker.y - 50,
					z: attacker.z,
					scale: 0.3,
					xscale: 0.8,
					opacity: 0,
					time: 500,
				}, 'decel', 'fade');
				scene.showEffect('energyball', {
					x: attacker.x + 200 * xf2[i],
					y: attacker.y - 50,
					z: attacker.z + 90 * yf2[i],
					scale: 0.1,
					xscale: 0.5,
					opacity: 0.4,
				}, {
					x: attacker.x,
					y: attacker.y - 50,
					z: attacker.z,
					scale: 0.3,
					xscale: 0.8,
					opacity: 0,
					time: 500,
				}, 'decel', 'fade');

				scene.showEffect('energyball', {
					x: attacker.x + 50 * xf[i],
					y: attacker.y - 50,
					z: attacker.z + 100 * yf[i],
					scale: 0.1,
					xscale: 0.5,
					opacity: 0.4,
					time: 200,
				}, {
					x: attacker.x,
					y: attacker.y - 50,
					z: attacker.z,
					scale: 0.3,
					xscale: 0.8,
					opacity: 0,
					time: 500,
				}, 'decel', 'fade');
				scene.showEffect('energyball', {
					x: attacker.x + 100 * xf2[i],
					y: attacker.y - 50,
					z: attacker.z + 90 * yf2[i],
					scale: 0.1,
					xscale: 0.5,
					opacity: 0.4,
					time: 200,
				}, {
					x: attacker.x,
					y: attacker.y - 50,
					z: attacker.z,
					scale: 0.3,
					xscale: 0.8,
					opacity: 0,
					time: 500,
				}, 'decel', 'fade');
			}
			scene.showEffect('energyball', {
				x: attacker.x,
				y: attacker.y - 25,
				z: attacker.z,
				scale: 3,
				opacity: 0,
				time: 50,
			}, {
				scale: 1,
				opacity: 0.8,
				time: 300,
			}, 'linear', 'fade');
			scene.showEffect('energyball', {
				x: attacker.x,
				y: attacker.y - 25,
				z: attacker.z,
				scale: 3.5,
				opacity: 0,
				time: 150,
			}, {
				scale: 1.5,
				opacity: 1,
				time: 350,
			}, 'linear', 'fade');
			scene.showEffect('energyball', {
				x: attacker.x,
				y: attacker.y - 25,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 200,
			}, {
				scale: 3,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y - 25,
				z: attacker.z,
				scale: 1,
				opacity: 0.6,
				time: 100,
			}, {
				scale: 3.5,
				opacity: 0.8,
				time: 500,
			}, 'linear', 'explode');
		},
	},
	ultraburst: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#000000', 600, 0.5);
			scene.backgroundEffect('#ffffff', 500, 1, 550);
			scene.showEffect('wisp', {
				x: attacker.x - 60,
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 0,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 150,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x + 60,
				y: attacker.y - 5,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 150,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x - 30,
				y: attacker.y + 60,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 250,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x + 20,
				y: attacker.y - 50,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 250,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x - 70,
				y: attacker.y - 50,
				z: attacker.z,
				scale: 0.7,
				opacity: 0.7,
				time: 100,
			}, {
				x: attacker.x,
				y: attacker.y,
				scale: 0.2,
				opacity: 0.2,
				time: 300,
			}, 'linear', 'fade');

			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 1.5,
				opacity: 1,
			}, {
				scale: 4,
				time: 600,
			}, 'linear', 'explode');
			scene.showEffect('electroball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0,
			}, {
				scale: 2.25,
				opacity: 0.1,
				time: 600,
			}, 'linear', 'explode');
			scene.showEffect('energyball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 2,
				opacity: 0,
				time: 200,
			}, {
				scale: 2.25,
				opacity: 0.1,
				time: 600,
			}, 'linear', 'explode');

			scene.showEffect('electroball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 6,
				opacity: 0.2,
			}, {
				scale: 1,
				opacity: 0,
				time: 300,
			}, 'linear');
			scene.showEffect('electroball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 6,
				opacity: 0.2,
				time: 150,
			}, {
				scale: 1,
				opacity: 0,
				time: 450,
			}, 'linear');
			scene.showEffect('electroball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 6,
				opacity: 0.2,
				time: 300,
			}, {
				scale: 1,
				opacity: 0,
				time: 600,
			}, 'linear');
			scene.showEffect('ultra', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 1,
				time: 600,
			}, {
				scale: 1,
				opacity: 0,
				time: 900,
			}, 'decel');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y - 60,
				z: attacker.z,
				scale: 0.5,
				xscale: 0.25,
				yscale: 0,
				opacity: 0.5,
				time: 600,
			}, {
				scale: 2,
				xscale: 6,
				yscale: 1,
				opacity: 0,
				time: 800,
			}, 'linear');
			scene.showEffect('iceball', {
				x: attacker.x,
				y: attacker.y - 60,
				z: attacker.z,
				scale: 0.5,
				xscale: 0.25,
				yscale: 0.75,
				opacity: 0.5,
				time: 800,
			}, {
				scale: 2,
				xscale: 6,
				opacity: 0.1,
				time: 1000,
			}, 'linear');
		},
	},
};
export const BattleStatusAnims: AnimTable = {
	brn: {
		anim(scene, [attacker]) {
			scene.showEffect('fireball', {
				x: attacker.x - 20,
				y: attacker.y - 15,
				z: attacker.z,
				scale: 0.2,
				opacity: 0.3,
			}, {
				x: attacker.x + 40,
				y: attacker.y + 15,
				z: attacker.z,
				scale: 1,
				opacity: 1,
				time: 300,
			}, 'swing', 'fade');
		},
	},
	psn: {
		anim(scene, [attacker]) {
			scene.showEffect('purplewisp', {
				x: attacker.x + 30,
				y: attacker.y - 40,
				z: attacker.z,
				scale: 0.2,
				opacity: 1,
				time: 0,
			}, {
				y: attacker.y,
				scale: 1,
				opacity: 0.5,
				time: 300,
			}, 'decel', 'fade');
			scene.showEffect('purplewisp', {
				x: attacker.x - 30,
				y: attacker.y - 40,
				z: attacker.z,
				scale: 0.2,
				opacity: 1,
				time: 100,
			}, {
				y: attacker.y,
				scale: 1,
				opacity: 0.5,
				time: 400,
			}, 'decel', 'fade');
			scene.showEffect('purplewisp', {
				x: attacker.x,
				y: attacker.y - 40,
				z: attacker.z,
				scale: 0.2,
				opacity: 1,
				time: 200,
			}, {
				y: attacker.y,
				scale: 1,
				opacity: 0.5,
				time: 500,
			}, 'decel', 'fade');
		},
	},
	slp: {
		anim(scene, [attacker]) {
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.1,
			}, {
				x: attacker.x,
				y: attacker.y + 20,
				z: attacker.behind(-50),
				scale: 1.5,
				opacity: 1,
				time: 400,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.1,
				time: 200,
			}, {
				x: attacker.x,
				y: attacker.y + 20,
				z: attacker.behind(-50),
				scale: 1.5,
				opacity: 1,
				time: 600,
			}, 'ballistic2Under', 'fade');
		},
	},
	par: {
		anim(scene, [attacker]) {
			scene.showEffect('electroball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 1.5,
				opacity: 0.2,
			}, {
				scale: 2,
				opacity: 0.1,
				time: 300,
			}, 'linear', 'fade');

			attacker.delay(100);
			attacker.anim({
				x: attacker.x - 1,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x + 1,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x - 1,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x + 1,
				time: 75,
			}, 'swing');
			attacker.anim({
				x: attacker.x - 1,
				time: 75,
			}, 'swing');
			attacker.anim({
				time: 100,
			}, 'accel');
		},
	},
	frz: {
		anim(scene, [attacker]) {
			scene.showEffect('icicle', {
				x: attacker.x - 30,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 200,
			}, {
				scale: 0.9,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('icicle', {
				x: attacker.x,
				y: attacker.y - 30,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 300,
			}, {
				scale: 0.9,
				opacity: 0,
				time: 650,
			}, 'linear', 'fade');
			scene.showEffect('icicle', {
				x: attacker.x + 15,
				y: attacker.y,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 400,
			}, {
				scale: 0.9,
				opacity: 0,
				time: 700,
			}, 'linear', 'fade');
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 1,
				opacity: 0.5,
			}, {
				scale: 3,
				opacity: 0,
				time: 600,
			}, 'linear', 'fade');
		},
	},
	flinch: {
		anim(scene, [attacker]) {
			scene.showEffect('shadowball', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 1,
				opacity: 0.2,
			}, {
				scale: 3,
				opacity: 0.1,
				time: 300,
			}, 'linear', 'fade');
		},
	},
	attracted: {
		anim(scene, [attacker]) {
			scene.showEffect('heart', {
				x: attacker.x + 20,
				y: attacker.y + 20,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 0,
			}, {
				scale: 1,
				opacity: 1,
				time: 300,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('heart', {
				x: attacker.x - 20,
				y: attacker.y + 10,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 100,
			}, {
				scale: 1,
				opacity: 1,
				time: 400,
			}, 'ballistic2Under', 'fade');
			scene.showEffect('heart', {
				x: attacker.x,
				y: attacker.y + 40,
				z: attacker.z,
				scale: 0.5,
				opacity: 0.5,
				time: 200,
			}, {
				scale: 1,
				opacity: 1,
				time: 500,
			}, 'ballistic2Under', 'fade');
		},
	},
	cursed: {
		anim(scene, [attacker]) {
			scene.backgroundEffect('#000000', 700, 0.2);
			attacker.delay(300);
			attacker.anim({ x: attacker.x - 5, time: 50 });
			attacker.anim({ x: attacker.x + 5, time: 50 });
			attacker.anim({ x: attacker.x - 5, time: 50 });
			attacker.anim({ x: attacker.x + 5, time: 50 });
			attacker.anim({ x: attacker.x, time: 50 });

			scene.showEffect(attacker.sp, {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				opacity: 0.5,
				time: 0,
			}, {
				z: attacker.behind(20),
				opacity: 0,
				time: 600,
			}, 'decel');
		},
	},
	confused: {
		anim(scene, [attacker]) {
			scene.showEffect('electroball', {
				x: attacker.x + 50,
				y: attacker.y + 30,
				z: attacker.z,
				scale: 0.1,
				opacity: 1,
				time: 400,
			}, {
				x: attacker.x - 50,
				scale: 0.15,
				opacity: 0.4,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x - 50,
				y: attacker.y + 30,
				z: attacker.z,
				scale: 0.1,
				opacity: 1,
				time: 400,
			}, {
				x: attacker.x + 50,
				scale: 0.15,
				opacity: 0.4,
				time: 600,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x + 50,
				y: attacker.y + 30,
				z: attacker.z,
				scale: 0.1,
				opacity: 1,
				time: 600,
			}, {
				x: attacker.x - 50,
				scale: 0.4,
				opacity: 0.4,
				time: 800,
			}, 'linear', 'fade');
			scene.showEffect('electroball', {
				x: attacker.x - 50,
				y: attacker.y + 30,
				z: attacker.z,
				scale: 0.15,
				opacity: 1,
				time: 600,
			}, {
				x: attacker.x + 50,
				scale: 0.4,
				opacity: 0.4,
				time: 800,
			}, 'linear', 'fade');
		},
	},
	confusedselfhit: {
		anim(scene, [attacker]) {
			scene.showEffect('wisp', {
				x: attacker.x,
				y: attacker.y,
				z: attacker.z,
				scale: 0,
				opacity: 0.5,
			}, {
				scale: 2,
				opacity: 0,
				time: 200,
			}, 'linear');
			attacker.delay(50);
			attacker.anim({
				x: attacker.leftof(2),
				z: attacker.behind(5),
				time: 100,
			}, 'swing');
			attacker.anim({
				time: 300,
			}, 'swing');
		},
	},
};
BattleStatusAnims['focuspunch'] = { anim: BattleStatusAnims['flinch'].anim };
